import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInstantTheme } from '../../contexts/InstantThemeContext';
import { useLayout } from '../../contexts/LayoutContext';
import { RestaurantTable } from '../../services/api';
import { ReservationCloudService, CloudReservation } from '../../services/restaurantCloudService';
import { firebaseTableService } from '../../services/firebaseTableService';
import DraftSplitOrdersDialog from '../../components/DraftSplitOrdersDialog';
import SplitTableDialog from '../../components/SplitTableDialog';
import CloseTabDialog from '../../components/CloseTabDialog';
import ManagerPinDialog from '../../components/ManagerPinDialog';
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  Chip,
  Dialog,
  ToggleButton,
  ToggleButtonGroup,

  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Restaurant,
  EventNote,
  LocalDining,
  OutdoorGrill,
  Euro,
  ShoppingCart,
  TrendingUp,
  ExitToApp,
  Refresh,
  Drafts as DraftIcon,
  DeleteForever,
} from '@mui/icons-material';

// Using types from API service

// Countdown Timer Component
const CountdownTimer: React.FC<{ startTime: string | undefined | null }> = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) {
      setElapsed(0);
      return;
    }

    // Helper function to convert various date formats to timestamp
    const getTimestamp = (dateValue: any): number => {
      // If it's already a timestamp
      if (typeof dateValue === 'number') {
        return dateValue;
      }
      
      // If it's a Firestore Timestamp object
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        return dateValue.toDate().getTime();
      }
      
      // If it's a Firestore Timestamp with seconds property
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        return dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000;
      }
      
      // Try to parse as ISO string
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return parsed.getTime();
      }
      
      return 0;
    };

    const interval = setInterval(() => {
      const start = getTimestamp(startTime);
      if (start === 0) {
        setElapsed(0);
        return;
      }
      
      const now = new Date().getTime();
      const elapsedMinutes = Math.floor((now - start) / (1000 * 60));
      
      // CRITICAL: Validate time is not stale
      // If time is older than 24 hours (1440 minutes), it's likely stale data
      // Reset to 0 to prevent displaying incorrect times like "557:23"
      const MAX_VALID_MINUTES = 24 * 60; // 24 hours in minutes
      const validElapsed = elapsedMinutes > MAX_VALID_MINUTES ? 0 : elapsedMinutes;
      
      setElapsed(Math.max(0, validElapsed)); // Ensure non-negative
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Convert minutes to HH:MM format
  const hours = Math.floor(elapsed / 60);
  const minutes = elapsed % 60;
  const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  return <span>{formattedTime}</span>;
};

const ModernRestaurantPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  const { appTheme } = useInstantTheme();
  const { hasSharedSidebar } = useLayout();
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [seatingArea, setSeatingArea] = useState<'indoor' | 'outdoor'>('indoor');
  const [isAdmin, setIsAdmin] = useState(false);
  const [tableStatusCounts, setTableStatusCounts] = useState({
    available: 0,
    occupied: 0,
    reserved: 0
  });

  const [areaStatusCounts, setAreaStatusCounts] = useState({
    indoor: { available: 0, occupied: 0, reserved: 0 },
    outdoor: { available: 0, occupied: 0, reserved: 0 }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [qrNotification, setQrNotification] = useState<{open: boolean, message: string}>({open: false, message: ''});
  const qrSeenOrderIds = useRef<Set<string>>(new Set());
  const qrIsInitialLoad = useRef<boolean>(true);
  const [centeredAlert, setCenteredAlert] = useState<{ open: boolean; message: string; title?: string; icon?: string } | null>(null);
  const [closeTabDialog, setCloseTabDialog] = useState<{ open: boolean; table: RestaurantTable | null }>({ open: false, table: null });
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [selectedTableForReservation, setSelectedTableForReservation] = useState<RestaurantTable | null>(null);
  const [reservationData, setReservationData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    reservation_time: '',
    party_size: 2,
    special_requests: ''
  });
  const [reservations, setReservations] = useState<CloudReservation[]>([]);
  const reservationsRef = useRef<CloudReservation[]>([]);
  const [reservationDetailsDialog, setReservationDetailsDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [reservationsListDialog, setReservationsListDialog] = useState(false);
  const [assignTableDialog, setAssignTableDialog] = useState(false);
  const [reservationTableWarning, setReservationTableWarning] = useState<{ open: boolean; table: RestaurantTable | null; type: 'occupied' | 'reserved' }>({ open: false, table: null, type: 'occupied' });
  const [isLoading, setIsLoading] = useState(true);
  
  // Flag to prevent auto-initialization after manual deletion
  // Set to true to permanently disable auto-initialization
  const skipAutoInitializationRef = useRef(true); // Changed to true to disable auto-initialization by default
  
  // Reservation notification dialog state
  const [reservationNotificationDialog, setReservationNotificationDialog] = useState(false);
  const [upcomingReservations, setUpcomingReservations] = useState<CloudReservation[]>([]);
  const [notificationType, setNotificationType] = useState<'tomorrow' | 'today' | 'soon' | null>(null);
  
  // Table order details dialog state
  const [tableOrderDialog, setTableOrderDialog] = useState(false);
  const [selectedTableOrder, setSelectedTableOrder] = useState<any>(null);
  const [tableOrders, setTableOrders] = useState<any[]>([]);
  const [loadingTableOrders, setLoadingTableOrders] = useState(false);
  const [tableCustomerInfo, setTableCustomerInfo] = useState<Map<number, any>>(new Map());
  
  // Draft split orders dialog state
  const [draftSplitOrdersDialog, setDraftSplitOrdersDialog] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<any>(null);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [newSplitDialog, setNewSplitDialog] = useState<{
    open: boolean;
    tableId: number;
    tableNumber: string;
    orderId: string;
    orderTotal: number;
    orderItems: any[];
    staffMember: string;
  } | null>(null);
  
  // Add table dialog state
  const [addTableDialog, setAddTableDialog] = useState(false);
  
  // CRITICAL: Flag to prevent fetchTableCustomerInfo from running during table operations
  const [isTableOperationInProgress, setIsTableOperationInProgress] = useState(false);
  
  // Debounce timer for silent refreshes to prevent too many rapid updates
  const silentRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // CRITICAL: Use ref so closures always have access to current flag value
  const isTableOperationInProgressRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isTableOperationInProgressRef.current = isTableOperationInProgress;
  }, [isTableOperationInProgress]);

  // Resolve admin role after Firebase Auth has loaded
  useEffect(() => {
    const checkAdmin = () => {
      try {
        const email = getAuth().currentUser?.email || '';
        const HARDCODED_ADMINS = ['admin@saheehpos.com', 'admin@saheehpos.com'];
        if (HARDCODED_ADMINS.includes(email.toLowerCase())) { setIsAdmin(true); return; }
        const raw = localStorage.getItem('saheeh_pos_user');
        if (raw) {
          const profile = JSON.parse(raw);
          const role = profile.role || '';
          if (role === 'admin' || role === 'manager') { setIsAdmin(true); return; }
        }
        if (email.includes('admin') || email.includes('manager')) setIsAdmin(true);
      } catch { /* ignore */ }
    };
    checkAdmin();
    const unsubscribe = onAuthStateChanged(getAuth(), () => checkAdmin());
    return () => unsubscribe();
  }, []);
  const [newTableData, setNewTableData] = useState({
    number: '',
    capacity: 4,
    area: 'indoor' as 'indoor' | 'outdoor'
  });

  // Filter tables by selected area
  // Filter tables by area and sort by number
  const filteredTables = tables
    .filter(table => {
      // CRITICAL: Only show tables that match the current seating area
      // Normalize area to handle any edge cases
      const tableArea = (table.area === 'indoor' || table.area === 'outdoor') ? table.area : null;
      
      if (tableArea) {
        const matches = tableArea === seatingArea;
        if (!matches) {
          // Only log if debugging is needed (reduce console spam)
          // console.log(`🚫 [FILTER] Table ${table.number} (area: ${tableArea}) filtered out - current area: ${seatingArea}`);
        }
        return matches;
    }
      // If no valid area property, don't show it (tables should always have an area)
      console.warn(`⚠️ [FILTER] Table ${table.number} has invalid area (${table.area}) - filtering out`);
      return false;
    })
    .sort((a, b) => {
      // Sort by table number (ascending)
      return (a.number || 0) - (b.number || 0);
  });
  
  // Debug: Log filtered tables when they change significantly
  useEffect(() => {
    if (filteredTables.length > 0) {
      console.log(`📊 [DISPLAY] Showing ${filteredTables.length} tables in ${seatingArea} area:`, 
        filteredTables.map(t => `Table ${t.number}`).join(', '));
    }
  }, [filteredTables, seatingArea]);
  
  // CRITICAL: Clean up stale/test data whenever outdoor tab is displayed
  // Note: cleanupStaleTestData is defined later, so we'll call it in handleSeatingAreaChange instead
  
  // Debug logging for filtered tables (only when area changes or issues detected)
  if (filteredTables.length === 0 && tables.length > 0) {
    console.warn(`⚠️ [FILTER] No tables found for area ${seatingArea}. Total tables: ${tables.length}`, 
      tables.map(t => ({ number: t.number, area: t.area })));
  }
  
  // Reduced debug logging - only log when needed for troubleshooting
  // console.log('🔍 Debug Info:');
  // console.log('Total tables:', tables.length);
  // console.log('Selected area:', seatingArea);
  // console.log('Tables with areas:', tables.map(t => ({ id: t.id, number: t.number, area: t.area })));
  // console.log('✔ Filtered tables:', filteredTables.length);
  // console.log('Filtered table numbers:', filteredTables.map(t => t.number));
  // console.log('Filtering logic check:');
  // tables.forEach(table => {
  //   console.log(`Table ${table.number}: area=${table.area}, seatingArea=${seatingArea}, match=${table.area === seatingArea}`);
  // });

  // Helper function to update table counts
  const updateTableCounts = useCallback((tables: RestaurantTable[]) => {
    // Filter out tables without valid area property
    const validTables = tables.filter(t => {
      const hasValidArea = t.area === 'indoor' || t.area === 'outdoor';
      if (!hasValidArea) {
        console.warn(`⚠️ [COUNTS] Table ${t.number} has invalid area: ${t.area}, excluding from counts`);
      }
      return hasValidArea;
    });
    
    // Calculate overall counts (only from valid tables)
    const availableCount = validTables.filter(t => t.status === 'available').length;
    const occupiedCount = validTables.filter(t => t.status === 'occupied').length;
    const reservedCount = validTables.filter(t => t.status === 'reserved').length;
        
    setTableStatusCounts({
      available: availableCount,
      occupied: occupiedCount,
      reserved: reservedCount
    });
        
    // Calculate area-specific counts - use strict matching
    const indoorTables = validTables.filter(t => {
      const isIndoor = t.area === 'indoor';
      return isIndoor;
    });
    
    const outdoorTables = validTables.filter(t => {
      const isOutdoor = t.area === 'outdoor';
      return isOutdoor;
    });
    
    // Calculate status counts for each area
    const indoorAvailable = indoorTables.filter(t => t.status === 'available').length;
    const indoorOccupied = indoorTables.filter(t => t.status === 'occupied').length;
    const indoorReserved = indoorTables.filter(t => t.status === 'reserved').length;
    
    const outdoorAvailable = outdoorTables.filter(t => t.status === 'available').length;
    const outdoorOccupied = outdoorTables.filter(t => t.status === 'occupied').length;
    const outdoorReserved = outdoorTables.filter(t => t.status === 'reserved').length;
    
    setAreaStatusCounts({
      indoor: {
        available: indoorAvailable,
        occupied: indoorOccupied,
        reserved: indoorReserved
      },
      outdoor: {
        available: outdoorAvailable,
        occupied: outdoorOccupied,
        reserved: outdoorReserved
      }
    });
    
    // Debug logging to verify calculations (only log if there's a mismatch)
    const indoorTotal = indoorAvailable + indoorOccupied + indoorReserved;
    const outdoorTotal = outdoorAvailable + outdoorOccupied + outdoorReserved;
    const grandTotal = indoorTotal + outdoorTotal;
    
    if (indoorTotal !== indoorTables.length || outdoorTotal !== outdoorTables.length || grandTotal !== validTables.length) {
      console.warn('⚠️ [COUNTS] Mismatch detected in table counts:', {
        indoor_expected: indoorTables.length,
        indoor_calculated: indoorTotal,
        outdoor_expected: outdoorTables.length,
        outdoor_calculated: outdoorTotal,
        total_expected: validTables.length,
        total_calculated: grandTotal
      });
    }
  }, [setTableStatusCounts, setAreaStatusCounts]);

  const fetchTablesInternal = useCallback(async (showLoading: boolean) => {
    // Only show loading state if requested
    if (showLoading) {
      setIsLoading(true);
    }
    
    try {
      // Initialize default tables if none exist (but skip if we just deleted all tables)
      if (!skipAutoInitializationRef.current) {
        await firebaseTableService.initializeDefaultTables();
      } else {
        console.log('⏸️ [FETCH] Skipping auto-initialization - tables were manually deleted');
      }
      
      // Fetch tables from Firebase
      const firebaseTables = await firebaseTableService.getTables();
      
      // Convert Firebase tables to local format
      const localTables: RestaurantTable[] = firebaseTables.map((table) => {
        // CRITICAL: Only mark as occupied if status is explicitly 'occupied' AND has customer data
        // If status is 'available' in Firebase, respect it (don't override)
        let finalStatus = table.status as 'available' | 'occupied' | 'reserved' | 'cleaning';
        
        // If status is 'available' in Firebase, keep it available (don't let reservation logic override)
        if (table.status === 'available' && (!table.customer_name || table.customer_name === null)) {
          finalStatus = 'available';
        }
        
        // CRITICAL FIX: Ensure table.number is always a valid number
        let tableId: number;
        if (typeof table.number === 'number' && !isNaN(table.number)) {
          tableId = table.number;
        } else if (table.number != null) {
          const parsed = parseInt(String(table.number), 10);
          tableId = isNaN(parsed) ? 0 : parsed;
        } else {
          console.warn(`⚠️ [RESTAURANT] Table has invalid number:`, table);
          tableId = 0; // Fallback to 0 if number is completely missing
        }
        
        // CRITICAL: Ensure area is always set and valid
        let tableArea: 'indoor' | 'outdoor' = (table.area === 'indoor' || table.area === 'outdoor') 
          ? table.area 
          : 'indoor'; // Default to indoor if area is missing or invalid
        
        if (!table.area || (table.area !== 'indoor' && table.area !== 'outdoor')) {
          console.warn(`⚠️ [RESTAURANT] Table ${tableId} has invalid or missing area (${table.area}), defaulting to 'indoor'`);
        }
        
        return {
          id: tableId, // Use the validated number as id
          number: tableId, // Use the same validated number
          status: finalStatus,
          capacity: table.capacity,
          area: tableArea, // Always ensure valid area
          customer_name: table.customer_name || undefined,
          total_amount: table.total_amount || undefined,
          item_count: table.item_count || undefined,
          start_time: table.start_time || undefined,
          active_order_number: table.active_order_number || undefined,
          active_order_id: (table as any).active_order_id || undefined,
          occupied_by: (table as any).occupied_by || undefined,
          // Split payment state — must be preserved so the UI can show "✅ SPLIT PAID"
          split_payment_complete: (table as any).split_payment_complete || false,
          split_people: (table as any).split_people || undefined,
          split_total: (table as any).split_total || undefined,
          split_session_id: (table as any).split_session_id || undefined,
        };
      });
      
      // Merge reservations if available before setting state
      const today = new Date().toISOString().split('T')[0];
      const todayRes = reservationsRef.current.filter(r => {
        try {
          let resTime: Date;
          if (typeof r.reservation_time === 'string') resTime = new Date(r.reservation_time);
          else if ((r.reservation_time as any)?.toDate) resTime = (r.reservation_time as any).toDate();
          else return false;
          return resTime.toISOString().split('T')[0] === today && r.status === 'confirmed';
        } catch { return false; }
      });
      const mergedInitial = todayRes.length > 0
        ? localTables.map(table => {
            const res = todayRes.find(r => r.table_number && r.table_number === table.number);
            if (res && table.status !== 'occupied') {
              if (table.status === 'available') {
                firebaseTableService.updateTableByNumber(table.number, 'reserved',
                  { customer_name: res.customer_name, start_time: res.reservation_time as string }, table.area
                ).catch(() => {});
              }
              return { ...table, status: 'reserved' as const, customer_name: res.customer_name || table.customer_name };
            }
            return table;
          })
        : localTables;
      setTables(mergedInitial);
      updateTableCounts(mergedInitial);
      
      // Only update loading state if requested
      if (showLoading) {
        setIsLoading(false);
      }
      return;
      
    } catch (error) {
      console.error('❌ Error loading tables from Firebase:', error);
      
      // Only show error snackbar if loading was shown (to avoid annoying user)
      if (showLoading) {
        setSnackbar({
          open: true,
          message: 'Failed to load tables from Firebase. Please refresh the page.',
          severity: 'error'
        });
        setIsLoading(false);
      } else {
        // For silent refreshes, just log the error
        console.warn('⚠️ Silent refresh failed, will retry on next cycle');
      }
    }
  }, [setIsLoading, setTables, setSnackbar, updateTableCounts]);

  const fetchTables = useCallback(async (silent: boolean = false) => {
    // For silent refreshes, debounce to prevent too many rapid updates
    if (silent) {
      // Clear any existing timer
      if (silentRefreshTimerRef.current) {
        clearTimeout(silentRefreshTimerRef.current);
      }
      
      // Debounce silent refreshes by 500ms to prevent glitches
      return new Promise<void>((resolve) => {
        silentRefreshTimerRef.current = setTimeout(async () => {
          try {
            await fetchTablesInternal(false); // Call internal function without debounce
            resolve();
          } catch (error) {
            console.warn('⚠️ Silent refresh failed:', error);
            resolve(); // Resolve anyway to not block
          }
        }, 500); // 500ms debounce
      });
    }
    
    // For non-silent refreshes, call immediately
    return fetchTablesInternal(true);
  }, [fetchTablesInternal]);

  // TEMPORARY: Function to delete all tables - can be called from browser console
  // Usage: window.deleteAllTables()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).deleteAllTables = async () => {
        try {
          const confirmDelete = window.confirm(
            '⚠️ ' + t('restaurant.delete_all_warning')
          );
          
          if (!confirmDelete) {
            console.log('❌ Deletion cancelled');
            return;
          }
          
          console.log('🗑️ Starting deletion of all tables...');
          const deletedCount = await firebaseTableService.deleteAllTables();
          console.log(`✅ Successfully deleted ${deletedCount} tables!`);
          
          // Refresh tables
          await fetchTables(true);
          
          alert('✅ ' + t('restaurant.delete_all_success', { count: deletedCount }));
          window.location.reload();
        } catch (error: any) {
          console.error('❌ Error deleting tables:', error);
          alert('❌ ' + t('restaurant.delete_all_error') + ': ' + (error.message || 'Unknown error'));
        }
      };
      
      console.log('✅ deleteAllTables() function is now available. Call window.deleteAllTables() in console.');
    }
  }, [fetchTables]);

  const fetchReservations = async () => {
    // Load reservations from Firebase Firestore
    try {
      const reservations = await ReservationCloudService.getReservations();
      setReservations(reservations);
      reservationsRef.current = reservations;
      console.log('✅ Loaded reservations from cloud:', reservations.length);
    } catch (error) {
      console.log('⚠️ Error loading reservations from cloud:', error);
      
      // No localStorage fallback - data must be in Firebase
      setReservations([]);
      console.log('⚠️ No reservations found in Firebase');
    }
  };

  // Listen for table status updates from Dashboard
  useEffect(() => {
    const handleTableStatusUpdate = (event: CustomEvent) => {
      const { tableNumber, status, updateData } = event.detail;
      
      // Update local table state
      setTables(prevTables => {
        const updatedTables = prevTables.map(table => 
          table.number === tableNumber 
            ? { 
                ...table, 
                status: status as 'available' | 'occupied' | 'reserved' | 'cleaning',
                customer_name: (updateData.customer_name && updateData.customer_name !== 'Laufkunde' && updateData.customer_name !== 'Walk-in Customer')
                  ? updateData.customer_name
                  : (table.customer_name && table.customer_name !== 'Laufkunde' && table.customer_name !== 'Walk-in Customer' ? table.customer_name : ''),
                total_amount: updateData.total_amount || 0,
                item_count: updateData.item_count || 0,
                start_time: updateData.start_time
              }
            : table
        );
        
        // Update counts
        updateTableCounts(updatedTables);
        
        return updatedTables;
      });
    };
    
    window.addEventListener('tableStatusUpdated', handleTableStatusUpdate as EventListener);
    
    return () => {
      window.removeEventListener('tableStatusUpdated', handleTableStatusUpdate as EventListener);
    };
  }, [updateTableCounts]);

  // CRITICAL: Cleanup function to remove stale data
  // This function clears stale customer data from tables based on time only
  // Cleanup function to remove stale/test data - kept for manual cleanup if needed
  // Note: This function is intentionally unused but kept for potential future manual cleanup
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // @ts-ignore - Intentionally unused, kept for manual cleanup
  const _cleanupStaleTestData = useCallback(async (_targetArea?: 'indoor' | 'outdoor') => {
    try {
      console.log(`🧹 [CLEANUP] Starting cleanup of stale/test data${_targetArea ? ` for ${_targetArea} area` : ''}...`);
      const allTables = await firebaseTableService.getTables();
      let cleanedCount = 0;
      
      for (const table of allTables) {
        // If target area specified, only clean tables in that area
        if (_targetArea && table.area !== _targetArea) {
          continue;
        }
        
        if (table.status === 'occupied' && table.customer_name) {
          let hasStaleTime = false;
          if (table.start_time) {
            let startTime: Date | null = null;
            if (typeof table.start_time === 'string') {
              startTime = new Date(table.start_time);
            } else if (table.start_time && typeof table.start_time === 'object') {
              const st = table.start_time as any;
              if (st.toDate && typeof st.toDate === 'function') {
                startTime = st.toDate();
              } else if (st.seconds && typeof st.seconds === 'number') {
                startTime = new Date(st.seconds * 1000);
              }
            }
            if (startTime && !isNaN(startTime.getTime())) {
              const hoursDiff = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
              const staleThreshold = 8; // 8 hours for all tables
              hasStaleTime = hoursDiff > staleThreshold;
            }
          }
          
          // Clear if stale time (same logic for all tables, indoor and outdoor)
          if (hasStaleTime) {
            // CRITICAL: Before clearing, check if there are any active orders for this table
            // Don't clear if there are real orders, even if customer name looks like test data
            // CRITICAL: Also check if table was recently updated (within last 5 minutes) - don't clear recently updated tables
            const tableUpdatedAt = table.updated_at || table.created_at;
            let tableUpdatedDate: Date | null = null;
            if (tableUpdatedAt) {
              if (typeof tableUpdatedAt === 'string') {
                tableUpdatedDate = new Date(tableUpdatedAt);
              } else if (tableUpdatedAt && typeof tableUpdatedAt === 'object') {
                const ut = tableUpdatedAt as any;
                if (ut.toDate && typeof ut.toDate === 'function') {
                  tableUpdatedDate = ut.toDate();
                } else if (ut.seconds && typeof ut.seconds === 'number') {
                  tableUpdatedDate = new Date(ut.seconds * 1000);
                }
              }
            }
            
            const isRecentlyUpdated = tableUpdatedDate && !isNaN(tableUpdatedDate.getTime()) && 
                                     (Date.now() - tableUpdatedDate.getTime()) < (5 * 60 * 1000); // 5 minutes
            
            // Don't clear if table was recently updated - it might be a real order that just got created
            if (isRecentlyUpdated) {
              console.log(`⏸️ [CLEANUP] Table ${table.number} (${table.area}) was recently updated - NOT clearing (might be real order)`);
              continue; // Skip clearing this table
            }
            
            try {
              const { collection, query, where, getDocs } = await import('../../services/firestoreCompat');
              const { db } = await import('../../services/firebaseConfig');
              const ordersRef = collection(db, 'orders');
              
              const tableNumberStr = String(table.number);
              const tableNumberNum = typeof table.number === 'number' ? table.number : parseInt(tableNumberStr);
              
              // Check for orders with this table number and area
              let orderQuery = query(
                ordersRef,
                where('table_number', '==', tableNumberStr),
                where('order_type', '==', 'dine_in'),
                where('table_area', '==', table.area)
              );
              
              let orderSnapshot;
              try {
                orderSnapshot = await getDocs(orderQuery);
              } catch (queryError: any) {
                // Try with number instead of string
                if (queryError.code === 'failed-precondition') {
                  orderQuery = query(
                    ordersRef,
                    where('table_number', '==', tableNumberNum),
                    where('order_type', '==', 'dine_in'),
                    where('table_area', '==', table.area)
                  );
                  orderSnapshot = await getDocs(orderQuery);
                } else {
                  throw queryError;
                }
              }
              
              // Check if there are any active orders
              let hasActiveOrders = false;
              if (orderSnapshot && !orderSnapshot.empty) {
                orderSnapshot.forEach((doc: any) => {
                  const orderData = doc.data();
                  const orderStatus = orderData.status || 'pending';
                  const inactiveStatuses = ['completed', 'delivered', 'cancelled'];
                  if (!inactiveStatuses.includes(orderStatus.toLowerCase())) {
                    hasActiveOrders = true;
                  }
                });
              }
              
              if (hasActiveOrders) {
                console.log(`⏸️ [CLEANUP] Table ${table.number} (${table.area}) has active orders - NOT clearing`);
                continue; // Skip clearing this table
              }
              
              // Only clear if no active orders found AND table is truly stale (not recently updated)
              console.log(`🧹 [CLEANUP] Clearing stale/test data from Table ${table.number} (${table.area}) - no active orders found and table is stale`);
              await firebaseTableService.updateTableByNumber(
                table.number,
                'available',
                {
                  customer_name: undefined,
                  total_amount: undefined,
                  item_count: undefined,
                  start_time: undefined
                },
                table.area
              );
              cleanedCount++;
            } catch (clearError) {
              console.warn(`⚠️ [CLEANUP] Could not clear Table ${table.number}:`, clearError);
            }
          }
        }
      }
      
      if (cleanedCount > 0) {
        console.log(`✅ [CLEANUP] Cleaned ${cleanedCount} table(s) with stale/test data`);
        // Refresh tables after cleanup (silent background refresh)
        await fetchTables(true);
      } else {
        console.log(`✅ [CLEANUP] No stale/test data found`);
      }
    } catch (error) {
      console.error('❌ [CLEANUP] Error cleaning up stale data:', error);
    }
  }, [fetchTables]);
  
  // Fetch customer information for occupied tables
  // CRITICAL: This function should NOT update table status - only read order data
  // Table status should only be updated when orders are actually created/completed
  const fetchTableCustomerInfo = useCallback(async () => {
    console.log(`🔍 [DEBUG] fetchTableCustomerInfo called, isTableOperationInProgress: ${isTableOperationInProgress}`);
    
    // CRITICAL: Do NOT run if table operations are in progress
    // This prevents overwriting real customer data when creating/deleting tables
    if (isTableOperationInProgress) {
      console.log('⏸️ [FETCH] Skipping fetchTableCustomerInfo - table operation in progress');
      return;
    }
    
    try {
      const { collection, query, where, getDocs } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      
      const ordersRef = collection(db, 'orders');
      const tablesRef = collection(db, 'restaurant_tables');
      
      // CRITICAL: Fetch fresh table data from Firebase to get current status
      // Don't rely on the 'tables' state which might be stale
      const tablesSnapshot = await getDocs(tablesRef);
      const freshTables = tablesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Array<{
        id: string;
        number?: number;
        area?: string;
        status?: string;
        [key: string]: any;
      }>;
      
      if (freshTables.length === 0) {
        setTableCustomerInfo(new Map());
        return;
      }
      
      const customerInfoMap = new Map<number, any>();
      
      // All tables treated equally - same logic for indoor and outdoor
      
      for (const table of freshTables) {
        try {
          // Skip tables without valid number or area
          if (!table.number || !table.area || (table.area !== 'indoor' && table.area !== 'outdoor')) {
            continue;
          }
          
          // CRITICAL: IMMEDIATELY CLEAR "Test Customer" data from ANY table
          // This must happen BEFORE other logic to ensure test data is removed
          // This runs for ALL tables to remove test data once and for all
          if (table.status === 'occupied' && table.customer_name) {
            const customerNameLower = table.customer_name.toLowerCase().trim();
            const isTestCustomer = customerNameLower === 'test customer' || 
                                  customerNameLower.includes('test customer') ||
                                  customerNameLower.startsWith('test customer');
            
            if (isTestCustomer) {
              console.log(`🧹 [CLEANUP] Found "Test Customer" on Table ${table.number} (${table.area}) - clearing immediately`);
              console.log(`🔍 [CLEANUP] Customer name: "${table.customer_name}"`);
              try {
                await firebaseTableService.updateTableByNumber(
                  table.number,
                  'available',
                  {
                    customer_name: undefined,
                    total_amount: undefined,
                    item_count: undefined,
                    start_time: undefined
                  },
                  table.area
                );
                console.log(`✅ Cleared "Test Customer" data from Table ${table.number} (${table.area})`);
                continue; // Skip to next table - don't process further
              } catch (clearError) {
                console.warn(`⚠️ Could not clear "Test Customer" data from Table ${table.number}:`, clearError);
              }
            }
          }
          
          // All tables treated equally - same logic for indoor and outdoor
          
          // CRITICAL: Check if table was created recently (within last 5 minutes)
          // If table was just created, skip it to prevent updating with old orders
          const tableCreatedAt = table.created_at || table.updated_at;
          const tableCreatedDate = tableCreatedAt ? 
            (typeof tableCreatedAt === 'string' ? new Date(tableCreatedAt) : 
             (tableCreatedAt?.toDate ? tableCreatedAt.toDate() : 
              (tableCreatedAt?.seconds ? new Date(tableCreatedAt.seconds * 1000) : null))) : null;
          const isNewlyCreatedTable = tableCreatedDate && (Date.now() - tableCreatedDate.getTime()) < (5 * 60 * 1000); // 5 minutes
          
          // CRITICAL: Check for stale customer data and clear it immediately
          // Clear stale data based on time only (8 hours threshold for all tables)
          if (table.status === 'occupied' && table.customer_name) {
            // Check if start_time is very old (> 8 hours)
            let hasStaleTime = false;
            let hoursDiff = 0;
            if (table.start_time) {
              let startTime: Date | null = null;
              if (typeof table.start_time === 'string') {
                startTime = new Date(table.start_time);
              } else if (table.start_time && typeof table.start_time === 'object') {
                const st = table.start_time as any;
                if (st.toDate && typeof st.toDate === 'function') {
                  startTime = st.toDate();
                } else if (st.seconds && typeof st.seconds === 'number') {
                  startTime = new Date(st.seconds * 1000);
                }
              }
              if (startTime && !isNaN(startTime.getTime())) {
                hoursDiff = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
                hasStaleTime = hoursDiff > 8; // 8 hours for all tables
              }
            }
            
            // Clear if stale time (same logic for all tables, indoor and outdoor)
            if (hasStaleTime) {
              console.log(`🧹 Table ${table.number} (${table.area}) has stale time (${Math.round(hoursDiff)} hours) - clearing immediately`);
              try {
                await firebaseTableService.updateTableByNumber(
                  table.number,
                  'available',
                  {
                    customer_name: undefined,
                    total_amount: undefined,
                    item_count: undefined,
                    start_time: undefined
                  },
                  table.area
                );
                console.log(`✅ Cleared stale data from Table ${table.number} (${table.area})`);
                // Skip to next table - don't process orders for this table
                continue;
              } catch (clearError) {
                console.warn(`⚠️ Could not clear stale data from Table ${table.number}:`, clearError);
              }
            }
          }
          
          // CRITICAL: Only check orders for tables that are OCCUPIED in Firestore.
          // If the table doc says 'available', it was explicitly cleared — do NOT re-occupy it from old orders.
          if (table.status !== 'occupied') {
            continue;
          }
          
          const tableNumberStr = String(table.number);
          const tableNumberNum = typeof table.number === 'number' ? table.number : parseInt(tableNumberStr);
          
          // Query for orders with this table number AND area
          // CRITICAL: Filter by table_area to prevent matching orders from wrong area
          // IMPORTANT: Filter by order_type === 'dine_in' (with underscore, not hyphen)
          // This matches how orders are stored when created from the dashboard
          let q = query(
            ordersRef,
            where('table_number', '==', tableNumberStr),
            where('order_type', '==', 'dine_in'),
            where('table_area', '==', table.area) // CRITICAL: Filter by area to prevent cross-area matching
          );
          
          let querySnapshot;
          let usedFallbackQuery = false;
          try {
            querySnapshot = await getDocs(q);

            // If string query returns empty, try with number type
            if (querySnapshot.empty) {
              console.log(`⚠️ Table ${table.number}: String query empty, trying number type`);
              q = query(
                ordersRef,
                where('table_number', '==', tableNumberNum),
                where('order_type', '==', 'dine_in'),
                where('table_area', '==', table.area)
              );
              querySnapshot = await getDocs(q);
            }

            // If still empty, try without table_area (for old orders)
            if (querySnapshot.empty && !isNewlyCreatedTable) {
              console.log(`⚠️ Table ${table.number}: Trying fallback without table_area`);
              // Try string first
              q = query(
                ordersRef,
                where('table_number', '==', tableNumberStr),
                where('order_type', '==', 'dine_in')
              );
              querySnapshot = await getDocs(q);
              if (querySnapshot.empty) {
                q = query(
                  ordersRef,
                  where('table_number', '==', tableNumberNum),
                  where('order_type', '==', 'dine_in')
                );
                querySnapshot = await getDocs(q);
              }
              if (!querySnapshot.empty) usedFallbackQuery = true;
            }
          } catch (queryError: any) {
            // If query fails due to missing index, try simpler queries
            if (queryError.code === 'failed-precondition') {
              console.log(`⚠️ Table ${table.number}: Index missing, trying simpler query`);
              try {
                q = query(
                  ordersRef,
                  where('table_number', '==', tableNumberNum),
                  where('order_type', '==', 'dine_in')
                );
                querySnapshot = await getDocs(q);
                if (querySnapshot.empty) {
                  q = query(
                    ordersRef,
                    where('table_number', '==', tableNumberStr),
                    where('order_type', '==', 'dine_in')
                  );
                  querySnapshot = await getDocs(q);
                }
                usedFallbackQuery = true;
              } catch (fallbackError: any) {
                throw fallbackError;
              }
            } else {
              throw queryError;
            }
          }
          
          if (!querySnapshot.empty) {
            
            // Sort client-side by created_at (newest first)
            const orders = querySnapshot.docs
              .map(doc => {
                const data = doc.data();
                return { id: doc.id, ...data } as any;
              })
              .sort((a: any, b: any) => {
                const dateA = a.created_at?.toDate?.() || new Date(a.created_at || 0);
                const dateB = b.created_at?.toDate?.() || new Date(b.created_at || 0);
                return dateB.getTime() - dateA.getTime(); // Descending
              });
            
            // Get the most recent order for this table
            // CRITICAL: Filter out orders with "Test Customer" - NEVER process them
            const validOrders = orders.filter((order: any) => {
              const customerName = order.customer_name || '';
              const customerNameLower = customerName.toLowerCase().trim();
              const isTestCustomer = customerNameLower === 'test customer' || 
                                    customerNameLower.includes('test customer') ||
                                    customerNameLower.startsWith('test customer');
              if (isTestCustomer) {
                console.log(`🚫 [FILTER] Skipping order ${order.order_number || order.id} with "Test Customer" - will not update table`);
                return false;
              }
              return true;
            });
            
            const latestOrder = validOrders[0];
            if (latestOrder) {
              // CRITICAL: Only process orders that are truly active
              const orderStatus = latestOrder.status || 'pending';
              const inactiveStatuses = ['completed', 'delivered', 'cancelled'];
              
              // CRITICAL: Verify the order is actually for this table AND area
              const orderArea = latestOrder.table_area || latestOrder.area;
              
              // If we used fallback query (orders without table_area), we need extra validation
              if (usedFallbackQuery) {
                // For fallback orders (without table_area), only process if:
                // 1. Order was created very recently (within last hour) - ensures it's a real order that just missed the area
                // 2. Order is active
                // 3. Table is NOT newly created (to prevent linking old orders to new tables)
                const orderDate = latestOrder.created_at?.toDate?.() || 
                                 (latestOrder.created_at?.seconds ? new Date(latestOrder.created_at.seconds * 1000) : 
                                  (typeof latestOrder.created_at === 'string' ? new Date(latestOrder.created_at) : null));
                
                const timeLimit = 60 * 60 * 1000; // 1 hour for all tables
                const isVeryRecentOrder = orderDate && (Date.now() - orderDate.getTime()) < timeLimit;
                
                if (!isVeryRecentOrder || isNewlyCreatedTable) {
                  console.log(`⏸️ Table ${table.number} (${table.area}): Skipping fallback order - not recent enough or table is newly created`);
                  // Don't process this order - continue to next table
                  continue;
                }
                
                // Update the order to include table_area so future queries work correctly
                try {
                  const { doc, updateDoc } = await import('../../services/firestoreCompat');
                  const { db } = await import('../../services/firebaseConfig');
                  const orderRef = doc(db, 'orders', latestOrder.id);
                  await updateDoc(orderRef, {
                    table_area: table.area
                  });
                  console.log(`✅ Updated order ${latestOrder.id} to include table_area: ${table.area}`);
                } catch (updateError) {
                  console.warn(`⚠️ Could not update order ${latestOrder.id} with table_area:`, updateError);
                }
              }
              
              // CRITICAL: If order doesn't have table_area set and we didn't use fallback, skip it to prevent cross-area matching
              // Only process orders that explicitly have table_area matching the table's area (or were validated via fallback)
              const tableMatchesArea = usedFallbackQuery || (orderArea && (orderArea === table.area || table.area === orderArea));
              
              // CRITICAL: Check if order is recent (within last 24 hours) to avoid stale orders
              const orderDate = latestOrder.created_at?.toDate?.() || 
                               (latestOrder.created_at?.seconds ? new Date(latestOrder.created_at.seconds * 1000) : 
                                (typeof latestOrder.created_at === 'string' ? new Date(latestOrder.created_at) : null));
              const isRecentOrder = orderDate && (Date.now() - orderDate.getTime()) < (24 * 60 * 60 * 1000); // 24 hours
              
              // CRITICAL: Don't update newly created tables with old orders
              // If table was just created and order is older than table, skip it to prevent fake data
              if (isNewlyCreatedTable && orderDate && tableCreatedDate && orderDate < tableCreatedDate) {
                console.log(`⏸️ Table ${table.number} (${table.area}) was just created - skipping update with old order (order: ${orderDate.toISOString()}, table: ${tableCreatedDate.toISOString()})`);
                // Don't update this table - continue to next table
                continue;
              }
              
              // All tables treated equally - same logic for indoor and outdoor
              
              if (!inactiveStatuses.includes(orderStatus.toLowerCase()) && tableMatchesArea && isRecentOrder) {

                // SUM all active orders for this table (not just latest)
                // CRITICAL: Also filter by date (12 hours) to prevent old uncompleted orders
                // from inflating the table card totals. This matches the "View All Orders" dialog filter.
                const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000);
                const activeOrders = validOrders.filter((o: any) => {
                  const s = (o.status || 'pending').toLowerCase();
                  if (inactiveStatuses.includes(s)) return false;
                  // Filter out old orders that were never completed
                  const oDate = o.created_at?.toDate?.() ||
                    (o.created_at?.seconds ? new Date(o.created_at.seconds * 1000) :
                     (typeof o.created_at === 'string' ? new Date(o.created_at) : null));
                  if (oDate && oDate < twelveHoursAgo) return false;
                  return true;
                });
                let combinedTotal = 0;
                let combinedItemCount = 0;
                for (const o of activeOrders) {
                  let orderTotal = parseFloat(o.total || 0);
                  // Fallback: recompute from item prices when total field is 0 or missing
                  // (happens if the order was created with a serialization bug)
                  if (orderTotal === 0 && Array.isArray(o.items) && o.items.length > 0) {
                    orderTotal = o.items.reduce((s: number, item: any) => {
                      const lineTotal = parseFloat(item.total || 0);
                      if (lineTotal > 0) return s + lineTotal;
                      const price = parseFloat(item.unit_price || item.price || 0);
                      const qty = parseInt(item.quantity || 1, 10);
                      return s + price * qty;
                    }, 0);
                  }
                  combinedTotal += orderTotal;
                  combinedItemCount += (o.items?.length || 0);
                }

                // Prefer real customer name over defaults (Laufkunde, Walk-in, Dine-In Tisch X, Bestellung)
                const isDefaultName = (name: string | undefined | null): boolean => {
                  if (!name) return true;
                  const t = name.trim();
                  return t === '' || t === 'Laufkunde' || t === 'Walk-in Customer' || t === 'Bestellung' || t.startsWith('Dine-In');
                };

                // CRITICAL: Only consider orders that belong to the CURRENT tab session
                // This prevents old customer names from previous sessions leaking into new orders
                const tabOrderIds: string[] = table.tab_orders || [];
                const currentSessionOrders = tabOrderIds.length > 0
                  ? activeOrders.filter((o: any) => tabOrderIds.includes(o.id))
                  : activeOrders;

                // Use the most recent current-session order's customer name
                const currentLatest = currentSessionOrders.length > 0 ? currentSessionOrders[0] : latestOrder;
                let bestCustomerName = currentLatest.customer_name || undefined;
                if (isDefaultName(bestCustomerName)) {
                  // Only check current table name, do NOT pull names from other orders
                  if (!isDefaultName(table.customer_name)) {
                    bestCustomerName = table.customer_name;
                  }
                }

                // REMOVED: Auto-repair that was writing old customer names to new orders and tables
                // This was the root cause of stale customer names appearing on new orders

                // Earliest order's created_at for start time
                const earliestOrder = activeOrders[activeOrders.length - 1] || latestOrder;

                // Check if any active order came from QR ordering
                const hasQrOrder = activeOrders.some((o: any) => o.source === 'qr_order');

                customerInfoMap.set(table.number, {
                  customer_name: bestCustomerName,
                  customer_first_name: latestOrder.customer_first_name || '',
                  customer_last_name: latestOrder.customer_last_name || '',
                  customer_phone: latestOrder.customer_phone || '',
                  customer_address: latestOrder.customer_address || '',
                  customer_postcode: latestOrder.customer_postcode || '',
                  customer_city: latestOrder.customer_city || '',
                  total: combinedTotal,
                  items_count: combinedItemCount,
                  created_at: earliestOrder.created_at,
                  order_id: latestOrder.id,
                  order_number: latestOrder.order_number || latestOrder.id || '',
                  has_qr_order: hasQrOrder,
                });

                // NOTE: Do NOT write to Firestore table doc here.
                // Table docs are only updated by explicit user actions (create order, add items, close tab).
                // Writing here causes flickering because this function runs on every focus/visibility/listener event
                // and may compute slightly different totals than the dashboard wrote.
                console.log(`ℹ️ [INFO] Table ${table.number}: computed total €${combinedTotal.toFixed(2)} (${activeOrders.length} orders, ${combinedItemCount} items), customer=${bestCustomerName}`);
              } else {
                if (!isRecentOrder) {
                  console.log(`ℹ️ Table ${table.number} (${table.area}) has stale order (older than 24h) - skipping`);
                }

                // FIX: Free zombie tables — occupied with ALL orders completed/cancelled
                // CRITICAL: Also treat orders older than 12h as inactive (they were never properly closed)
                if (table.status === 'occupied') {
                  const twelveHoursAgoZombie = new Date(Date.now() - 12 * 60 * 60 * 1000);
                  const hasAnyActiveOrder = validOrders.some((o: any) => {
                    const s = (o.status || 'pending').toLowerCase();
                    if (inactiveStatuses.includes(s)) return false;
                    // Old orders (>12h) are effectively dead — don't count them as active
                    const oDate = o.created_at?.toDate?.() ||
                      (o.created_at?.seconds ? new Date(o.created_at.seconds * 1000) :
                       (typeof o.created_at === 'string' ? new Date(o.created_at) : null));
                    if (oDate && oDate < twelveHoursAgoZombie) return false;
                    return true;
                  });

                  // Check if table was recently updated (within 2 min) — protect freshly-occupied tables
                  const tUpdatedAt = table.updated_at;
                  let tUpdatedDate: Date | null = null;
                  if (typeof tUpdatedAt === 'string') tUpdatedDate = new Date(tUpdatedAt);
                  else if (tUpdatedAt?.toDate) tUpdatedDate = tUpdatedAt.toDate();
                  else if (tUpdatedAt?.seconds) tUpdatedDate = new Date(tUpdatedAt.seconds * 1000);
                  const wasRecentlyUpdated = tUpdatedDate && !isNaN(tUpdatedDate.getTime()) && (Date.now() - tUpdatedDate.getTime()) < 10 * 60 * 1000; // 10 minutes

                  if (!hasAnyActiveOrder && !wasRecentlyUpdated) {
                    console.log(`⚠️ Table ${table.number} (${table.area}): ALL ${validOrders.length} orders are completed/cancelled and table idle for 10+ min — freeing zombie table`);
                    try {
                      const { doc: fDoc2, updateDoc: fUpdate2, deleteField: fDel2 } = await import('../../services/firestoreCompat');
                      const { db: fDb2 } = await import('../../services/firebaseConfig');
                      await fUpdate2(fDoc2(fDb2, 'restaurant_tables', table.id), {
                        status: 'available',
                        customer_name: fDel2(),
                        total_amount: fDel2(),
                        item_count: fDel2(),
                        start_time: fDel2(),
                        occupied_by: fDel2(),
                        active_order_number: fDel2(),
                        tab_total: fDel2(),
                        tab_item_count: fDel2(),
                        tab_orders: fDel2(),
                        tab_rounds: fDel2(),
                        tab_open: false,
                      });
                      console.log(`✅ Freed zombie table ${table.number} (${table.area})`);
                    } catch (freeErr) {
                      console.warn(`⚠️ Could not free zombie table ${table.number}:`, freeErr);
                    }
                  } else if (wasRecentlyUpdated) {
                    console.log(`ℹ️ Table ${table.number}: recently updated, not freeing yet (order may be in progress)`);
                  }
                }
              }
            }
          } else {
            // No orders found for this table
            // Same logic for all tables - indoor and outdoor treated equally
            
            // CRITICAL: Only clear table if we're CERTAIN it's stale data
            // Don't clear if:
            // 1. Table was just created/updated (within last 5 minutes) - order might not be indexed yet
            // 2. Table has customer_name set - might be a real order that query didn't find
            // 3. Table was recently updated - give it time for orders to be created
            if (table.status === 'occupied') {
              // Check if table was recently updated
              const tableUpdatedAt = table.updated_at || table.created_at;
              let tableUpdatedDate: Date | null = null;
              if (tableUpdatedAt) {
                if (typeof tableUpdatedAt === 'string') {
                  tableUpdatedDate = new Date(tableUpdatedAt);
                } else if (tableUpdatedAt && typeof tableUpdatedAt === 'object') {
                  const ut = tableUpdatedAt as any;
                  if (ut.toDate && typeof ut.toDate === 'function') {
                    tableUpdatedDate = ut.toDate();
                  } else if (ut.seconds && typeof ut.seconds === 'number') {
                    tableUpdatedDate = new Date(ut.seconds * 1000);
                  }
                }
              }
              
              const isRecentlyUpdated = tableUpdatedDate && !isNaN(tableUpdatedDate.getTime()) && 
                                       (Date.now() - tableUpdatedDate.getTime()) < (5 * 60 * 1000); // 5 minutes
              
              // Check if table has been occupied for a long time (8+ hours) with no orders
              let hasStaleTime = false;
              if (table.start_time) {
                let startTime: Date | null = null;
                if (typeof table.start_time === 'string') {
                  startTime = new Date(table.start_time);
                } else if (table.start_time && typeof table.start_time === 'object') {
                  const st = table.start_time as any;
                  if (st.toDate && typeof st.toDate === 'function') {
                    startTime = st.toDate();
                  } else if (st.seconds && typeof st.seconds === 'number') {
                    startTime = new Date(st.seconds * 1000);
                  }
                }
                if (startTime && !isNaN(startTime.getTime())) {
                  const hoursDiff = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
                  hasStaleTime = hoursDiff > 8; // More than 8 hours
                }
              }
              
              // Only clear if:
              // 1. Table has been occupied for a long time (8+ hours) AND
              // 2. Table was NOT recently updated (not within last 5 minutes) AND
              // 3. Table has no customer_name OR has stale time
              const shouldClear = hasStaleTime && !isRecentlyUpdated && (!table.customer_name || hasStaleTime);
              
              if (shouldClear) {
                console.log(`⚠️ Table ${table.number} (${table.area}) is marked occupied but has no active orders and is stale - clearing data`);
                
                // Clear the table status and customer data
                try {
                  await firebaseTableService.updateTableByNumber(
                    table.number,
                    'available',
                    {
                      customer_name: undefined,
                      total_amount: undefined,
                      item_count: undefined,
                      start_time: undefined
                    },
                    table.area // CRITICAL: Pass area to ensure correct table is updated
                  );
                  console.log(`✅ Cleared stale data from Table ${table.number} (${table.area})`);
                } catch (clearError) {
                  console.warn(`⚠️ Could not clear stale data from Table ${table.number}:`, clearError);
                }
              } else {
                // Table is occupied with no orders found but not stale enough to clear
                // Don't auto-free — let the user close the tab manually
                console.log(`ℹ️ Table ${table.number} (${table.area}) is occupied but no orders found — leaving as-is (user can close tab manually)`);
              }
            }
          }
        } catch (error: any) {
          // Silent fail for individual tables - don't spam console
          if (error.code !== 'failed-precondition') {
            console.warn(`⚠️ Table ${table.number}: Could not fetch customer info - ${error.message}`);
          }
        }
      }
      
      setTableCustomerInfo(customerInfoMap);
      console.log('✅ Fetched customer info for', customerInfoMap.size, 'tables');
    } catch (error) {
      console.error('❌ Error fetching table customer info:', error);
    }
  }, [isTableOperationInProgress]); // Depend on isTableOperationInProgress to prevent running during operations

  // Auto-fill active_order_number for occupied tables that don't have it
  useEffect(() => {
    const fillMissingOrderNumbers = async () => {
      const occupiedWithoutOrderNum = tables.filter(
        t => t.status === 'occupied' && !t.active_order_number
      );
      if (occupiedWithoutOrderNum.length === 0) return;

      console.log(`🔧 [AUTO-FIX] ${occupiedWithoutOrderNum.length} occupied tables missing order number`);

      try {
        const { collection: col, query: q, where: w, getDocs: gd } = await import('../../services/firestoreCompat');
        const { db: fdb } = await import('../../services/firebaseConfig');
        const ordersRef = col(fdb, 'orders');

        for (const table of occupiedWithoutOrderNum) {
          try {
            // Try string first, then number (table_number may be stored as either type)
            let snap = await gd(q(ordersRef, w('table_number', '==', String(table.number))));
            if (snap.empty) {
              snap = await gd(q(ordersRef, w('table_number', '==', table.number)));
            }
            if (snap.empty) {
              console.log(`⚠️ [AUTO-FIX] No orders found for table ${table.number} (tried string and number)`);
              continue;
            }
            console.log(`📋 [AUTO-FIX] Found ${snap.size} orders for table ${table.number}`);

            // Find most recent active dine-in order
            let bestOrder: any = null;
            let bestDate = 0;
            snap.forEach(docSnap => {
              const d = docSnap.data();
              if (d.order_type !== 'dine_in') return;
              if (['cancelled', 'completed', 'delivered'].includes((d.status || '').toLowerCase())) return;
              const ts = d.created_at?.toDate?.()?.getTime() || (d.created_at?.seconds ? d.created_at.seconds * 1000 : 0);
              if (ts > bestDate) { bestDate = ts; bestOrder = { id: docSnap.id, ...d }; }
            });

            if (bestOrder?.order_number) {
              // Find the real Firestore doc ID, then do a minimal update (don't wipe other fields)
              const foundTable = await firebaseTableService.findTable(table.number, table.area as 'indoor' | 'outdoor' | undefined);
              if (foundTable?.id) {
                const { doc: fDoc, updateDoc: fUpdate } = await import('../../services/firestoreCompat');
                await fUpdate(fDoc(fdb, 'restaurant_tables', String(foundTable.id)), {
                  active_order_number: bestOrder.order_number
                });
                console.log(`✅ [AUTO-FIX] Set order number ${bestOrder.order_number} on table ${table.number} (doc: ${foundTable.id})`);
              }
            } else {
              console.log(`⚠️ [AUTO-FIX] No active dine-in order found for table ${table.number} (${snap.size} total orders)`);
            }
          } catch (e: any) {
            console.warn(`⚠️ [AUTO-FIX] Failed for table ${table.number}:`, e.message);
          }
        }
      } catch (e: any) {
        console.warn('⚠️ [AUTO-FIX] Failed:', e.message);
      }
    };
    fillMissingOrderNumbers();
  }, [tables]);

  // Load tables and reservations on component mount
  useEffect(() => {
    console.log('🔄 Component mounted, initializing data...');
    
    // Load data immediately with error handling
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchTables(),
          fetchReservations()
        ]);
        console.log('✅ Initial data loaded successfully');
        
        // Clean up stale/test data on page load
        setTimeout(async () => {
          try {
            const allTables = await firebaseTableService.getTables();
            
            // Clean up stale/test data for all tables
            for (const table of allTables) {
              
              if (table.status === 'occupied' && table.customer_name) {
                let hasStaleTime = false;
                if (table.start_time) {
                  let startTime: Date | null = null;
                  if (typeof table.start_time === 'string') {
                    startTime = new Date(table.start_time);
                  } else if (table.start_time && typeof table.start_time === 'object') {
                    const st = table.start_time as any;
                    if (st.toDate && typeof st.toDate === 'function') {
                      startTime = st.toDate();
                    } else if (st.seconds && typeof st.seconds === 'number') {
                      startTime = new Date(st.seconds * 1000);
                    }
                  }
                  if (startTime && !isNaN(startTime.getTime())) {
                    const hoursDiff = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
                    const staleThreshold = 8; // 8 hours for all tables
                    hasStaleTime = hoursDiff > staleThreshold;
                  }
                }
                
                // Clear if stale time (same logic for all tables, indoor and outdoor)
                if (hasStaleTime) {
                  console.log(`🧹 [INIT] Clearing stale data from Table ${table.number} (${table.area})`);
                  await firebaseTableService.updateTableByNumber(
                    table.number,
                    'available',
                    {
                      customer_name: undefined,
                      total_amount: undefined,
                      item_count: undefined,
                      start_time: undefined
                    },
                    table.area
                  );
                }
              }
            }
            console.log('✅ [INIT] Cleaned up stale customer data');
          } catch (cleanupError) {
            console.warn('⚠️ [INIT] Error cleaning up stale data:', cleanupError);
          }
        }, 1000); // Wait 1 second for tables to load
    } catch (error) {
        console.error('❌ Error loading initial data:', error);
        // Show user-friendly error message
      setSnackbar({
        open: true,
          message: 'Error loading restaurant data. Please refresh the page.',
        severity: 'error'
      });
    }
  };

    initializeData();
    
    // Set up real-time listeners for cloud data (with delay to avoid race conditions)
    const setupListeners = () => {
      const unsubscribeReservations = ReservationCloudService.onReservationsChange((reservations) => {
        setReservations(reservations);
        reservationsRef.current = reservations;
        console.log('🔄 Reservations updated from cloud:', reservations.length);
      });
      
      // Enable Firebase table listener
      const unsubscribeTables = firebaseTableService.onTablesChange((firebaseTables) => {
        console.log('🔄 Tables updated from Firebase:', firebaseTables.length);
        
        // Convert Firebase tables to local format - RESPECT available status from Firebase
        const localTables: RestaurantTable[] = firebaseTables.map((table) => {
          // CRITICAL: If status is 'available' in Firebase, respect it (don't override)
          let finalStatus = table.status as 'available' | 'occupied' | 'reserved' | 'cleaning';
          
          // If status is 'available' in Firebase, keep it available (don't let reservation logic override)
          if (table.status === 'available' && (!table.customer_name || table.customer_name === null)) {
            finalStatus = 'available';
          }
          
          // Use table.number as id for compatibility with RestaurantTable interface (expects number)
          // The Firebase document ID is stored separately if needed
          // CRITICAL FIX: Ensure table.number is always a valid number
          let tableId: number;
          if (typeof table.number === 'number' && !isNaN(table.number)) {
            tableId = table.number;
          } else if (table.number != null) {
            const parsed = parseInt(String(table.number), 10);
            tableId = isNaN(parsed) ? 0 : parsed;
          } else {
            console.warn(`⚠️ [RESTAURANT] Table has invalid number:`, table);
            tableId = 0; // Fallback to 0 if number is completely missing
          }
          
          return {
            id: tableId,
            number: tableId, // Use the same validated number
            status: finalStatus,
            capacity: table.capacity,
            area: table.area as 'indoor' | 'outdoor',
            customer_name: table.customer_name || undefined,
            total_amount: table.total_amount || (table as any).tab_total || undefined,
            item_count: table.item_count || (table as any).tab_item_count || undefined,
            start_time: table.start_time || undefined,
            active_order_number: table.active_order_number || undefined,
            active_order_id: (table as any).active_order_id || undefined,
            occupied_by: (table as any).occupied_by || undefined
          };
        });

        // Merge today's reservations into table statuses BEFORE setting state
        // This prevents the flash where tables briefly show 'available' before reservation merge
        const today = new Date().toISOString().split('T')[0];
        const todayRes = reservationsRef.current.filter(r => {
          try {
            let resTime: Date;
            if (typeof r.reservation_time === 'string') {
              resTime = new Date(r.reservation_time);
            } else if ((r.reservation_time as any)?.toDate) {
              resTime = (r.reservation_time as any).toDate();
            } else return false;
            return resTime.toISOString().split('T')[0] === today && r.status === 'confirmed';
          } catch { return false; }
        });

        const mergedTables = todayRes.length > 0
          ? localTables.map(table => {
              const res = todayRes.find(r => r.table_number && r.table_number === table.number);
              if (res && table.status !== 'occupied') {
                // Auto-fix: if Firestore says 'available' but there's a reservation, update Firestore
                if (table.status === 'available') {
                  firebaseTableService.updateTableByNumber(
                    table.number, 'reserved',
                    { customer_name: res.customer_name, start_time: res.reservation_time as string },
                    table.area
                  ).catch(() => {});
                }
                return { ...table, status: 'reserved' as const, customer_name: res.customer_name || table.customer_name };
              }
              return table;
            })
          : localTables;

        setTables(mergedTables);
        updateTableCounts(mergedTables);
      });
      
      return { 
        unsubscribeReservations, 
        unsubscribeTables
      };
    };
    
    // Set up listeners after a short delay to avoid race conditions
    let cleanupFunctions: { unsubscribeReservations: () => void; unsubscribeTables: () => void } | null = null;
    
    const listenerTimeout = setTimeout(() => {
      cleanupFunctions = setupListeners();
    }, 1000);
    
    return () => {
      clearTimeout(listenerTimeout);
      if (cleanupFunctions) {
        cleanupFunctions.unsubscribeReservations();
        cleanupFunctions.unsubscribeTables();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time listener for orders to update table info automatically
  useEffect(() => {
    console.log('🔄 Setting up real-time order listener for table updates...');
    
    let unsubscribe: (() => void) | null = null;
    
    const setupOrderListener = async () => {
      try {
        const { collection, query, where, onSnapshot } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        
        // Listen to all dine-in orders (active orders only)
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('order_type', '==', 'dine_in'),
          where('status', 'in', ['pending', 'confirmed', 'preparing', 'ready', 'assigned'])
        );
        
        unsubscribe = onSnapshot(q, async (snapshot) => {
          console.log('📦 [REAL-TIME] Orders updated, refreshing table info...', snapshot.size, 'active orders');
          
          // CRITICAL: Check if table operation is in progress before fetching (use ref for current value)
          if (!isTableOperationInProgressRef.current) {
            // Update table info from orders
            await fetchTableCustomerInfo();
          } else {
            console.log('⏸️ [REAL-TIME] Skipping fetchTableCustomerInfo - table operation in progress');
          }
          
          // Also refresh tables to get latest status (silent background refresh)
          await fetchTables(true);
        }, (error) => {
          console.error('❌ Error in order listener:', error);
        });
        
        console.log('✅ Real-time order listener set up');
      } catch (error) {
        console.error('❌ Failed to set up order listener:', error);
      }
    };
    
    // Set up listener after a delay to avoid race conditions
    const listenerTimeout = setTimeout(() => {
      setupOrderListener();
    }, 1500);
    
    return () => {
      clearTimeout(listenerTimeout);
      if (unsubscribe) {
        unsubscribe();
        console.log('🛑 Real-time order listener removed');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- QR Order Notification: sound + real-time listener ---
  const playQrOrderAlert = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const now = audioCtx.currentTime;

      // Restaurant-style chime: warm bell tones with reverb feel
      const playNote = (time: number, freq: number, duration: number, vol: number, type: OscillatorType = 'sine') => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, now + time);
        gain.gain.exponentialRampToValueAtTime(0.001, now + time + duration);
        osc.start(now + time);
        osc.stop(now + time + duration);
      };

      // Warm bell chord 1
      playNote(0, 523.25, 0.6, 0.25, 'sine');     // C5
      playNote(0, 659.25, 0.6, 0.2, 'sine');      // E5
      playNote(0, 783.99, 0.6, 0.15, 'triangle'); // G5

      // Warm bell chord 2 (higher, brighter)
      playNote(0.35, 659.25, 0.6, 0.25, 'sine');  // E5
      playNote(0.35, 783.99, 0.6, 0.2, 'sine');   // G5
      playNote(0.35, 1046.5, 0.6, 0.15, 'triangle'); // C6

      // Final sparkle
      playNote(0.7, 1318.5, 0.8, 0.15, 'sine');   // E6
      playNote(0.7, 1568.0, 0.8, 0.1, 'triangle'); // G6

    } catch (e) {
      console.warn('Audio alert failed:', e);
    }
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupQrOrderListener = async () => {
      try {
        const { collection, query, onSnapshot, limit } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        console.log('%c[QR-ALERT] Setting up QR notification listener...', 'color: #9c27b0; font-weight: bold; font-size: 14px;');

        const { orderBy: fsOrderBy, deleteDoc: fsDeleteDoc, doc: fsDoc } = await import('../../services/firestoreCompat');

        // Listen to recent qr_notifications only, ordered by time
        const q = query(
          collection(db, 'qr_notifications'),
          fsOrderBy('created_at', 'desc'),
          limit(10)
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          const currentIds = new Set<string>();
          snapshot.forEach((docSnap) => currentIds.add(docSnap.id));

          console.log(`[QR-ALERT] Snapshot received: ${currentIds.size} notifications, initialLoad=${qrIsInitialLoad.current}, seenBefore=${qrSeenOrderIds.current.size}`);

          if (qrIsInitialLoad.current) {
            qrSeenOrderIds.current = currentIds;
            qrIsInitialLoad.current = false;
            console.log(`%c[QR-ALERT] ✅ Listener active! Tracking ${currentIds.size} existing notifications`, 'color: #4caf50; font-weight: bold;');

            // Clean up old notifications (older than 1 hour)
            snapshot.forEach((docSnap) => {
              const data = docSnap.data();
              const createdAt = data.created_at?.toDate?.() || (data.created_at?.seconds ? new Date(data.created_at.seconds * 1000) : null);
              if (createdAt && Date.now() - createdAt.getTime() > 60 * 60 * 1000) {
                fsDeleteDoc(fsDoc(db, 'qr_notifications', docSnap.id)).catch(() => {});
              }
            });
            return;
          }

          let newOrderFound = false;
          currentIds.forEach((id) => {
            if (!qrSeenOrderIds.current.has(id)) {
              newOrderFound = true;
              const docSnap = snapshot.docs.find((d) => d.id === id);
              const data = docSnap?.data();
              const tableNumber = data?.table_number || '?';
              const orderNumber = data?.order_number || id.slice(-6);
              const total = data?.total != null ? Number(data.total).toFixed(2) : '0.00';
              const merged = data?.merged ? ' (hinzugefügt)' : '';

              console.log(`%c[QR-ALERT] 🔔 NEW QR ORDER DETECTED! Table ${tableNumber} — #${orderNumber} (€${total})${merged}`, 'color: #ff5722; font-weight: bold; font-size: 16px;');
              playQrOrderAlert();
              setQrNotification({
                open: true,
                message: `Tisch ${tableNumber} — #${orderNumber} (€${total})${merged}`,
              });

              // Delete processed notification after 5 seconds
              setTimeout(() => {
                fsDeleteDoc(fsDoc(db, 'qr_notifications', id)).catch(() => {});
              }, 5000);
            }
          });

          if (!newOrderFound) {
            console.log(`[QR-ALERT] No new notifications in this snapshot`);
          }

          qrSeenOrderIds.current = currentIds;
        }, (error) => {
          console.error('%c[QR-ALERT] ❌ Listener error:', 'color: red; font-weight: bold;', error);
        });
      } catch (error) {
        console.error('%c[QR-ALERT] ❌ Failed to set up listener:', 'color: red; font-weight: bold;', error);
      }
    };

    setupQrOrderListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log('[QR-ORDER] Listener removed');
      }
    };
  }, [playQrOrderAlert]);

  // Real-time listener directly on restaurant_tables collection
  // Needed so split_payment_complete changes immediately update the UI
  useEffect(() => {
    const unsubscribe = firebaseTableService.onTablesChange((updatedTables) => {
      setTables(prevTables =>
        prevTables.map(prev => {
          const live = updatedTables.find(t => t.number === prev.number);
          if (!live) return prev;
          return {
            ...prev,
            split_payment_complete: (live as any).split_payment_complete || false,
            split_people: (live as any).split_people,
            split_total: (live as any).split_total,
            split_session_id: (live as any).split_session_id,
          };
        })
      );
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh table info when navigating to restaurant page (user returns from checkout)
  useEffect(() => {
      if (location.pathname === '/restaurant') {
        console.log('🔄 Navigated to restaurant page, refreshing table info...');
        fetchTables(true); // Silent background refresh
        setTimeout(() => {
          // CRITICAL: Check if table operation is in progress before fetching (use ref for current value)
          if (!isTableOperationInProgressRef.current) {
            fetchTableCustomerInfo();
          } else {
            console.log('⏸️ [NAVIGATION] Skipping fetchTableCustomerInfo - table operation in progress');
          }
        }, 500);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-open CloseTabDialog when navigated from dashboard with autoCloseTab state
  useEffect(() => {
    const autoCloseTableNumber = (location.state as any)?.autoCloseTab;
    if (!autoCloseTableNumber || tables.length === 0) return;
    // Find the table by number
    const targetTable = tables.find(t => t.number === autoCloseTableNumber || t.number === parseInt(String(autoCloseTableNumber), 10));
    if (targetTable) {
      console.log('🧾 [AUTO CLOSE TAB] Auto-opening CloseTabDialog for table', autoCloseTableNumber);
      setCloseTabDialog({ open: true, table: targetTable });
      // Clear the navigation state so it doesn't re-trigger on re-render
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, tables]);

  // Refresh table info when page becomes visible (user returns from checkout)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/restaurant') {
        console.log('👁️ Page became visible, refreshing table info...');
        fetchTables(true); // Silent background refresh
        setTimeout(() => {
          // CRITICAL: Check if table operation is in progress before fetching (use ref for current value)
          if (!isTableOperationInProgressRef.current) {
            fetchTableCustomerInfo();
          } else {
            console.log('⏸️ [VISIBILITY] Skipping fetchTableCustomerInfo - table operation in progress');
          }
        }, 500);
      }
    };
    
    const handleFocus = () => {
      if (location.pathname === '/restaurant') {
        console.log('🎯 Window focused, refreshing table info...');
        fetchTables(true); // Silent background refresh
        setTimeout(() => {
          // CRITICAL: Check if table operation is in progress before fetching (use ref for current value)
          if (!isTableOperationInProgressRef.current) {
            fetchTableCustomerInfo();
          } else {
            console.log('⏸️ [FOCUS] Skipping fetchTableCustomerInfo - table operation in progress');
          }
        }, 500);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Update table statuses based on reservations
  const updateTablesWithReservations = useCallback((todayReservations: CloudReservation[]) => {
    // Removed excessive log - only update silently
    
    setTables(prevTables => {
      const updatedTables = prevTables.map(table => {
        // Check if this table has a reservation for TODAY ONLY (skip waitlist reservations with table_number=0)
        const tableReservation = todayReservations.find(res =>
          res.table_number && res.table_number === table.number
        );

        // If table is available AND has no reservation, keep it available
        if (table.status === 'available' && !table.customer_name && !table.start_time && !tableReservation) {
          return table;
        }
        
        // If table is manually occupied (has active orders), preserve that status
        if (table.status === 'occupied' && table.customer_name && table.start_time) {
          // Check if this is a real active order (not stale data)
          // If start_time is very old (> 8 hours), it might be stale
          if (table.start_time) {
            const startTime = new Date(table.start_time);
            const now = new Date();
            const hoursDiff = (now.getTime() - startTime.getTime()) / (1000 * 60 * 60);
            
            // If order is older than 8 hours, it's likely stale - mark as available and clean up in Firebase
            if (hoursDiff > 8) {
              console.log(`⚠️ Table ${table.number} has stale order data (${hoursDiff.toFixed(1)} hours old), marking as available`);
              
              // Auto-cleanup stale table data in Firebase (fire and forget)
              firebaseTableService.updateTableByNumber(
                table.number, 
                'available', 
                {
                customer_name: undefined,
                total_amount: undefined,
                item_count: undefined,
                start_time: undefined,
                end_time: undefined
                },
                table.area // CRITICAL: Pass area to ensure correct table is updated
              ).catch(err => {
                console.warn(`⚠️ Could not auto-cleanup stale data for table ${table.number}:`, err);
              });
              
              return {
                ...table,
                status: 'available' as const,
                customer_name: undefined,
                total_amount: undefined,
                item_count: 0,
                start_time: undefined
              };
            }
          }
          
          return table; // Keep the current occupied status
        }
        
        if (tableReservation) {
          // Table has a reservation for TODAY - mark as reserved
          // Auto-fix: if Firestore says 'available', update it to 'reserved'
          if (table.status === 'available') {
            firebaseTableService.updateTableByNumber(
              table.number, 'reserved',
              { customer_name: tableReservation.customer_name, start_time: tableReservation.reservation_time as string },
              table.area
            ).catch(() => {});
          }
          return {
            ...table,
            status: 'reserved' as const,
            customer_name: tableReservation.customer_name || '',
            total_amount: 0,
            item_count: 0,
            start_time: tableReservation.reservation_time
          };
        } else {
          // Table does NOT have a reservation for today
          if (table.status === 'available') {
            return table;
          }

          // If table is 'reserved' in Firestore but no reservation exists, free it
          if (table.status === 'reserved') {
            console.log(`⚠️ Table ${table.number} is reserved but has no matching reservation — freeing`);
            firebaseTableService.updateTableByNumber(
              table.number, 'available',
              { customer_name: undefined, start_time: undefined },
              table.area
            ).catch(() => {});
            return {
              ...table,
              status: 'available' as const,
              customer_name: undefined,
              start_time: undefined,
            };
          }

          // Keep occupied tables as-is — only Close Tab should free them
          // Don't auto-free based on missing customer_name or item_count

          return table;
        }
      });
      // Update table counts after reservation merge
      updateTableCounts(updatedTables);
      return updatedTables;
    });
  }, [updateTableCounts]);

  // Improved reservation notification system with Firebase tracking
  const checkReservationNotifications = useCallback(async () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const tomorrowReservations: CloudReservation[] = [];
    const todayReservations: CloudReservation[] = [];
    const soonReservations: CloudReservation[] = []; // Reservations in next 2 hours
    
    // Filter confirmed reservations only
    const confirmedReservations = reservations.filter(r => r.status === 'confirmed');
    
    confirmedReservations.forEach((reservation) => {
      let reservationTime: Date;
      
      // Handle Firestore Timestamp or ISO string
      if (typeof reservation.reservation_time === 'string') {
        reservationTime = new Date(reservation.reservation_time);
      } else if (reservation.reservation_time && typeof reservation.reservation_time === 'object') {
        const timeObj = reservation.reservation_time as any;
        if ('toDate' in timeObj && typeof timeObj.toDate === 'function') {
          reservationTime = timeObj.toDate();
        } else if ('seconds' in timeObj && typeof timeObj.seconds === 'number') {
          reservationTime = new Date(timeObj.seconds * 1000);
        } else {
          return; // Skip invalid dates
        }
      } else {
        return; // Skip invalid dates
      }
      
      const reservationDate = new Date(reservationTime);
      reservationDate.setHours(0, 0, 0, 0);
      
      const timeDiff = reservationTime.getTime() - now.getTime();
      const hoursUntilReservation = timeDiff / (1000 * 60 * 60);
      const daysUntilReservation = reservationDate.getTime() - today.getTime();
      const daysDiff = daysUntilReservation / (1000 * 60 * 60 * 24);
      
      // Check if reservation is tomorrow (1 day before)
      if (daysDiff >= 0.9 && daysDiff <= 1.1) {
        const notificationKey = `reservation_notified_${reservation.id}_tomorrow`;
        const alreadyNotified = localStorage.getItem(notificationKey);
        if (!alreadyNotified) {
          tomorrowReservations.push(reservation);
        }
      }
      
      // Check if reservation is today
      if (daysDiff >= -0.1 && daysDiff <= 0.1) {
        const notificationKey = `reservation_notified_${reservation.id}_today`;
        const alreadyNotified = localStorage.getItem(notificationKey);
        if (!alreadyNotified) {
          todayReservations.push(reservation);
        }
      }
      
      // Check if reservation is within 2 hours (urgent)
      if (hoursUntilReservation >= 0 && hoursUntilReservation <= 2) {
        const notificationKey = `reservation_notified_${reservation.id}_soon`;
        const alreadyNotified = localStorage.getItem(notificationKey);
        if (!alreadyNotified) {
          soonReservations.push(reservation);
        }
      }
    });
    
    // Show notifications in priority order: soon > today > tomorrow
    if (soonReservations.length > 0) {
      setUpcomingReservations(soonReservations);
      setNotificationType('soon');
      setReservationNotificationDialog(true);
      soonReservations.forEach(r => {
        localStorage.setItem(`reservation_notified_${r.id}_soon`, 'true');
      });
      console.log(`⏰ [NOTIFICATION] Urgent: ${soonReservations.length} reservation(s) in next 2 hours`);
    } else if (todayReservations.length > 0) {
      setUpcomingReservations(todayReservations);
      setNotificationType('today');
      setReservationNotificationDialog(true);
      todayReservations.forEach(r => {
        localStorage.setItem(`reservation_notified_${r.id}_today`, 'true');
      });
      console.log(`📅 [NOTIFICATION] Today: ${todayReservations.length} reservation(s) for today`);
    } else if (tomorrowReservations.length > 0) {
      setUpcomingReservations(tomorrowReservations);
      setNotificationType('tomorrow');
      setReservationNotificationDialog(true);
      tomorrowReservations.forEach(r => {
        localStorage.setItem(`reservation_notified_${r.id}_tomorrow`, 'true');
      });
      console.log(`📅 [NOTIFICATION] Tomorrow: ${tomorrowReservations.length} reservation(s) for tomorrow`);
    }
  }, [reservations]);

  // Check for reservations on specific dates and show pop-up alerts
  const checkReservationsForToday = useCallback(() => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    // Helper function to parse reservation time (same logic as in reservation dialog)
    const parseReservationTime = (reservation: any): Date => {
      if (typeof reservation.reservation_time === 'string') {
        return new Date(reservation.reservation_time);
      } else if (reservation.reservation_time && typeof reservation.reservation_time === 'object') {
        const timeObj = reservation.reservation_time as any;
        if ('toDate' in timeObj && typeof timeObj.toDate === 'function') {
          return timeObj.toDate();
        } else if ('seconds' in timeObj && typeof timeObj.seconds === 'number') {
          return new Date(timeObj.seconds * 1000);
        }
      }
      return new Date();
    };

    // Clear ALL reservation notification localStorage flags so the popup always shows
    // when visiting the Restaurant page
    reservations.forEach(r => {
      localStorage.removeItem(`reservation_notified_${r.id}_today`);
      localStorage.removeItem(`reservation_notified_${r.id}_soon`);
      localStorage.removeItem(`reservation_notified_${r.id}_tomorrow`);
    });

    const todayReservations = reservations.filter(reservation => {
      try {
        const reservationTime = parseReservationTime(reservation);
        const reservationDate = reservationTime.toISOString().split('T')[0];
      return reservationDate === today;
      } catch (error) {
        console.warn('[RESERVATION] Error parsing reservation time:', error, reservation);
        return false;
      }
    });

    // Only log if there are reservations for today
    if (todayReservations.length > 0) {
      console.log('[RESTAURANT] Found', todayReservations.length, 'reservations for today');
      console.log('[RESTAURANT] Today reservations:', todayReservations.map(r => ({
        id: r.id,
        customer_name: r.customer_name,
        table_number: r.table_number,
        reservation_time: parseReservationTime(r).toISOString()
      })));
    }

    if (todayReservations.length > 0) {
      // Always show reservation notification on Restaurant page load
      // Include all today's reservations - even past ones - so staff is aware
      setUpcomingReservations(todayReservations);
      setNotificationType('today');
      setReservationNotificationDialog(true);
    }

    // ALWAYS update table statuses - clear reservations that are NOT for today
    updateTablesWithReservations(todayReservations);
  }, [reservations, updateTablesWithReservations]);


  // Always check reservations on component mount/page visit
  useEffect(() => {
    if (reservations.length > 0) {
      checkReservationsForToday();
    }
  }, [checkReservationsForToday]); // Re-runs when reservations change (via useCallback dep)

  // Check reservations when reservations change (FIXED: Removed infinite loop)
  useEffect(() => {
    if (reservations.length > 0) {
    checkReservationsForToday();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations.length]); // Only depend on reservations.length to prevent infinite loop

  // Also check when tables are loaded (FIXED: Removed infinite loop)
  useEffect(() => {
    if (tables.length > 0) {
      // Only check reservations if we have reservations loaded
      if (reservations.length > 0) {
      checkReservationsForToday();
      }
      // CRITICAL: Add delay before fetching customer info to prevent incorrectly linking orders to newly created tables
      // This prevents new tables from being auto-occupied by stale orders
      const timeoutId = setTimeout(() => {
        // CRITICAL: Check if table operation is in progress before fetching (use ref for current value)
        if (!isTableOperationInProgressRef.current) {
          console.log('🔄 [EFFECT] Fetching table customer info after tables change...');
          fetchTableCustomerInfo();
        } else {
          console.log('⏸️ [EFFECT] Skipping fetchTableCustomerInfo - table operation in progress');
        }
      }, 1500); // 1.5 second delay to allow Firebase to process table changes
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables.length]); // Only depend on tables.length to prevent infinite loop

  // Set up interval to check reservation notifications every minute (FIXED: Removed from deps)
  useEffect(() => {
    const interval = setInterval(() => {
      if (reservations.length > 0) {
      checkReservationNotifications();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Manual trigger - call immediately when component mounts (removed duplicate)
  // This is now handled by the reservations.length effect above

  const handleSeatingAreaChange = (_event: React.MouseEvent<HTMLElement>, newArea: 'indoor' | 'outdoor' | null) => {
    if (newArea !== null) {
      console.log('🔄 Switching seating area from', seatingArea, 'to', newArea);
      setSeatingArea(newArea);
      // Force refresh tables to ensure correct area assignments (silent background refresh)
      fetchTables(true);
      
      // Fetch customer info for all tables when switching tabs
      // Use setTimeout to ensure tables are refreshed first
      setTimeout(() => {
        console.log(`🔄 [AREA SWITCH] Fetching customer info for all tables`);
        fetchTableCustomerInfo();
      }, 500);
    }
  };

  // Fetch orders for a specific table - FIXED: Handle both string and number table_number with robust error handling
  const handleViewTableOrder = async (table: RestaurantTable) => {
    console.log(`🔍 [TABLE ORDERS] ===== Starting to fetch orders for table ${table.number} =====`);
    console.log(`🔍 [TABLE ORDERS] Table object:`, { number: table.number, type: typeof table.number, id: table.id, area: table.area });

    // display_number must be the REAL physical table number — receipts, kitchen
    // tickets, and order docs all print this. Using a sequential UI index here
    // caused "Tisch 100" to print as "Tisch 5" on the second round.
    const displayNumber = table.number;

    // Create table object with display number
    const tableWithDisplayNumber = {
      ...table,
      display_number: displayNumber,
      area: table.area || seatingArea
    };
    
    setLoadingTableOrders(true);
    setTableOrderDialog(true);
    setSelectedTableOrder(tableWithDisplayNumber);
    setTableOrders([]); // Clear previous orders
    
    try {
      const { collection, query, where, getDocs } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      
      const ordersRef = collection(db, 'orders');
      const tableNumberStr = String(table.number);
      const tableNumberNum = typeof table.number === 'number' ? table.number : parseInt(tableNumberStr);
      
      // CRITICAL: Get table area to filter orders correctly
      const tableArea = table.area || seatingArea;
      console.log(`🔍 [TABLE ORDERS] Search values: string="${tableNumberStr}", number=${tableNumberNum}, area="${tableArea}"`);
      console.log(`🔍 [TABLE ORDERS] Searching for orders with table_number == "${tableNumberStr}" OR ${tableNumberNum} AND table_area == "${tableArea}"`);
      
      let orders: any[] = [];
      let querySnapshot: any = null;
      let needsClientSideFilter = false; // Track if we fetched all orders
      
      // FIXED: Try WITHOUT orderBy FIRST (index is building, will fail)
      // CRITICAL: Always filter by table_area to prevent matching orders from wrong area
      // Strategy 1: Try string match with area filter (no index needed)
        try {
          const q = query(
            ordersRef, 
            where('table_number', '==', tableNumberStr),
            where('table_area', '==', tableArea), // CRITICAL: Filter by area
            where('order_type', '==', 'dine_in') // Only dine-in orders
          );
          querySnapshot = await getDocs(q);
        console.log(`🔍 Query 1 (string + area, no orderBy): Found ${querySnapshot.size} orders`);
        
        // If query succeeds but returns 0, try number query as fallback
        if (querySnapshot.size === 0) {
          console.log(`⚠️ Query 1 found 0 orders with string "${tableNumberStr}" and area "${tableArea}", trying number ${tableNumberNum}...`);
          try {
            const q2 = query(
              ordersRef, 
              where('table_number', '==', tableNumberNum),
              where('table_area', '==', tableArea), // CRITICAL: Filter by area
              where('order_type', '==', 'dine_in') // Only dine-in orders
            );
            const querySnapshot2 = await getDocs(q2);
            if (querySnapshot2.size > 0) {
              querySnapshot = querySnapshot2;
              console.log(`✅ Query 1b (number + area): Found ${querySnapshot.size} orders`);
            }
          } catch (numError: any) {
            console.log(`⚠️ Query 1b failed: ${numError.message}`);
          }
        }
      } catch (error1: any) {
        console.log('⚠️ Query 1 (string + area) failed, trying number:', error1.message);
            
        // Strategy 2: Try number match with area filter
            try {
              const q = query(
                ordersRef, 
                where('table_number', '==', tableNumberNum),
                where('table_area', '==', tableArea), // CRITICAL: Filter by area
                where('order_type', '==', 'dine_in') // Only dine-in orders
              );
              querySnapshot = await getDocs(q);
          console.log(`🔍 Query 2 (number + area, no orderBy): Found ${querySnapshot.size} orders`);
        } catch (error2: any) {
          console.log('⚠️ Query 2 (number + area) failed, trying fallback without area filter:', error2.message);
              
          // Strategy 3: Try without area filter (for old orders that don't have table_area)
          // BUT filter client-side by area to prevent cross-area matching
          try {
            const q3 = query(
              ordersRef,
              where('table_number', '==', tableNumberNum),
              where('order_type', '==', 'dine_in')
              // NOTE: No table_area filter - this is for old orders that don't have it
            );
            querySnapshot = await getDocs(q3);
            needsClientSideFilter = true; // Mark that we need to filter client-side by area
            console.log(`🔍 Query 3 (number, no area filter): Fetched ${querySnapshot.size} orders, will filter by area client-side`);
          } catch (error3: any) {
            console.log('⚠️ Query 3 failed, trying client-side filter for all orders:', error3.message);
            
            // Strategy 4: Fetch all orders and filter client-side (last resort)
            try {
              const allOrdersQuery = query(ordersRef);
              querySnapshot = await getDocs(allOrdersQuery);
              needsClientSideFilter = true; // Mark that we need to filter client-side
              console.log(`🔍 Query 4 (all orders): Fetched ${querySnapshot.size} total orders, filtering client-side by table number and area`);
            } catch (error4: any) {
              throw new Error(`All query strategies failed. Last error: ${error4.message}`);
            }
          }
        }
      }
      
      // If still no orders found, try client-side filter as absolute last resort
      if (querySnapshot && querySnapshot.size === 0 && !needsClientSideFilter) {
        console.log(`⚠️ No orders found with direct query, trying client-side filter...`);
        try {
          const allOrdersQuery = query(ordersRef);
          const allOrdersSnapshot = await getDocs(allOrdersQuery);
          needsClientSideFilter = true;
          querySnapshot = allOrdersSnapshot;
          console.log(`🔍 Client-side filter: Fetched ${querySnapshot.size} total orders, filtering for table ${table.number}`);
        } catch (error: any) {
          console.warn('⚠️ Could not fetch all orders for client-side filter:', error);
        }
      }
      
      // Process results
      if (querySnapshot) {
        querySnapshot.forEach((doc: any) => {
        const data = doc.data();
          
          // Client-side filter only if we fetched all orders or orders without area filter
          if (needsClientSideFilter) {
            const orderTableNumber = data.table_number;
            // Handle null/undefined - skip orders without table_number
            if (!orderTableNumber) {
              return; // Skip orders without table_number
            }
            
            // Check if table number matches
            const tableNumberMatches = 
              orderTableNumber === tableNumberStr || 
              orderTableNumber === tableNumberNum ||
              String(orderTableNumber) === tableNumberStr ||
              Number(orderTableNumber) === tableNumberNum ||
              (orderTableNumber && String(orderTableNumber).trim() === tableNumberStr);
            
            if (!tableNumberMatches) {
              return; // Skip this order - table number doesn't match
            }
            
            // CRITICAL: Also filter by area to prevent cross-area matching
            // For old orders without table_area, only include if they're very recent (within last hour)
            const orderArea = data.table_area || data.area;
            if (orderArea) {
              // Order has area - must match table area
              if (orderArea !== tableArea) {
                console.log(`⏭️ [TABLE ORDERS] Skipping order ${doc.id} - area mismatch (order: ${orderArea}, table: ${tableArea})`);
                return; // Skip this order - area doesn't match
              }
            } else {
              // Order doesn't have area - only include if very recent (within last hour)
              // This prevents old orders from wrong area being matched
              const orderDate = data.created_at?.toDate?.() || 
                               (data.created_at?.seconds ? new Date(data.created_at.seconds * 1000) : 
                                (typeof data.created_at === 'string' ? new Date(data.created_at) : null));
              const isVeryRecent = orderDate && (Date.now() - orderDate.getTime()) < (60 * 60 * 1000); // 1 hour
              
              if (!isVeryRecent) {
                console.log(`⏭️ [TABLE ORDERS] Skipping old order ${doc.id} without table_area (not recent enough)`);
                return; // Skip old orders without area
              }
              
              console.log(`⚠️ [TABLE ORDERS] Including order ${doc.id} without table_area (very recent, within last hour)`);
            }
          }
          
          // Also skip if order doesn't have table_number (shouldn't happen with proper query, but safety check)
          if (!data.table_number && !needsClientSideFilter) {
            return; // Skip orders without table_number
          }
          
          // CRITICAL: Filter out old orders - only show today's orders (within last 12 hours)
          const orderCreatedAt = data.created_at?.toDate?.() ||
            (data.created_at?.seconds ? new Date(data.created_at.seconds * 1000) :
             (typeof data.created_at === 'string' ? new Date(data.created_at) : null));
          const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000);
          if (orderCreatedAt && orderCreatedAt < twelveHoursAgo) {
            return; // Skip old orders
          }

          // CRITICAL: Filter out completed and cancelled orders - only show active orders
          // Active orders are: pending, confirmed, preparing, ready
          // Exclude: completed, delivered, cancelled
          const orderStatus = data.status || 'pending';
          const inactiveStatuses = ['completed', 'delivered', 'cancelled'];
          if (inactiveStatuses.includes(orderStatus.toLowerCase())) {
            return; // Skip completed/cancelled orders
          }
          
          // Convert created_at from Firestore Timestamp to ISO string if needed
          let created_at: string;
          if (data.created_at) {
            if (typeof data.created_at === 'object' && 'toDate' in data.created_at) {
              // Firestore Timestamp object
              created_at = data.created_at.toDate().toISOString();
            } else if (typeof data.created_at === 'object' && 'seconds' in data.created_at) {
              // Firestore Timestamp with seconds property
              created_at = new Date(data.created_at.seconds * 1000).toISOString();
            } else if (typeof data.created_at === 'string') {
              // Already an ISO string
              created_at = data.created_at;
            } else {
              // Fallback to current time
              created_at = new Date().toISOString();
            }
          } else {
            created_at = new Date().toISOString();
          }
          
          // Add order to results
        orders.push({
          id: doc.id,
          order_number: data.order_number || doc.id,
          customer_name: data.customer_name || undefined,
          customer_first_name: data.customer_first_name || '',
          customer_last_name: data.customer_last_name || '',
          customer_phone: data.customer_phone || '',
          customer_address: data.customer_address || '',
          customer_postcode: data.customer_postcode || '',
          customer_city: data.customer_city || '',
          items: data.items || [],
          total: parseFloat(data.total || 0),
          subtotal: parseFloat(data.subtotal || 0),
          tax: parseFloat(data.tax || 0),
          status: data.status || 'pending',
          payment_method: data.payment_method || 'cash',
          payment_status: data.payment_status || 'completed',
          order_type: data.order_type || 'dine_in',
          source: data.source || 'pos',
          created_at: created_at,
            notes: data.notes || '',
            table_number: data.table_number || null,
            // User tracking information
            created_by: data.created_by || '',
            created_by_email: data.created_by_email || '',
            created_by_name: data.created_by_name || ''
        });
      });
        
        // Always sort by created_at (newest first) to ensure latest order is first
        // This is important for showing only the latest order when multiple exist
        if (orders.length > 0) {
          orders.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA; // Descending order (newest first)
          });
        }
      }
      
      // FIXED: For restaurant tables, show only the LATEST/MOST RECENT active order
      // Multiple active orders for the same table don't make sense in restaurant context
      // A table should have ONE current order being worked on
      if (orders.length > 1) {
        console.log(`⚠️ [TABLE ORDERS] Found ${orders.length} active orders for table ${table.number}. Showing only the latest order.`);
        console.log(`📋 [TABLE ORDERS] All orders:`, orders.map(o => ({
          order_number: o.order_number,
          created_at: o.created_at,
          status: o.status,
          total: o.total
        })));
        
        // Keep only the most recent order (already sorted by created_at descending)
        const latestOrder = orders[0];
        orders = [latestOrder];
        
        console.log(`✅ [TABLE ORDERS] Filtered to latest order: ${latestOrder.order_number} (created: ${latestOrder.created_at})`);
      }
      
      setTableOrders(orders);
      console.log(`✅ [TABLE ORDERS] Successfully fetched ${orders.length} order(s) for table ${table.number}`);
      
      if (orders.length > 0) {
        console.log(`📋 [TABLE ORDERS] Order details:`, orders.map(o => ({
          id: o.id,
          order_number: o.order_number,
          customer_name: o.customer_name,
          total: o.total,
          items_count: o.items?.length || 0,
          table_number: o.table_number
        })));
        
        // Update table info with latest order data (optional - don't fail if this fails)
        // CRITICAL: Filter out orders with "Test Customer" - NEVER update tables with test data
        const validOrders = orders.filter((order: any) => {
          const customerName = order.customer_name || '';
          const customerNameLower = customerName.toLowerCase().trim();
          const isTestCustomer = customerNameLower === 'test customer' || 
                                customerNameLower.includes('test customer') ||
                                customerNameLower.startsWith('test customer');
          if (isTestCustomer) {
            console.log(`🚫 [FILTER] Skipping order ${order.order_number || order.id} with "Test Customer" - will not update table`);
            return false;
          }
          return true;
        });
        
        const latestOrder = validOrders[0];
        if (latestOrder) {
          try {
            // Convert created_at to ISO string if it's a Firestore Timestamp
            let startTime: string;
            if (latestOrder.created_at) {
              if (typeof latestOrder.created_at === 'object' && 'toDate' in latestOrder.created_at) {
                // Firestore Timestamp object
                startTime = latestOrder.created_at.toDate().toISOString();
              } else if (typeof latestOrder.created_at === 'object' && 'seconds' in latestOrder.created_at) {
                // Firestore Timestamp with seconds property
                startTime = new Date(latestOrder.created_at.seconds * 1000).toISOString();
              } else if (typeof latestOrder.created_at === 'string') {
                // Already an ISO string
                startTime = latestOrder.created_at;
              } else {
                // Fallback to current time
                startTime = new Date().toISOString();
              }
            } else {
              startTime = new Date().toISOString();
            }
            
            await firebaseTableService.updateTableByNumber(
              table.number, 
              'occupied', 
              {
              customer_name: latestOrder.customer_name || undefined, // Use actual customer name or undefined
              total_amount: latestOrder.total || 0,
              item_count: latestOrder.items?.length || 0,
              start_time: startTime
              },
              table.area // CRITICAL: Pass area to ensure correct table is updated
            );
          } catch (updateError) {
            console.warn(`⚠️ Could not update table ${table.number} from order:`, updateError);
          }
        } else {
          console.log(`ℹ️ No valid orders found for table ${table.number} (filtered out test customer orders)`);
        }
        
        // Refresh tables to show updated info (silent background refresh)
        await fetchTables(true);
      } else {
        console.log(`⚠️ [TABLE ORDERS] No orders found for table ${table.number}`);
        console.log(`🔍 [TABLE ORDERS] Searched for: string="${tableNumberStr}", number=${tableNumberNum}`);
        setSnackbar({
          open: true,
          message: `No orders found for table ${table.number}.`,
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('❌ Error fetching table orders:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      setSnackbar({
        open: true,
        message: `Failed to load table orders: ${error.message || 'Unknown error'}. Please try again.`,
        severity: 'error'
      });
      setTableOrders([]);
    } finally {
      setLoadingTableOrders(false);
    }
  };

  // --- Reopen Tab (client request: undo a mistaken Close Tab within 15 min)
  const [reopenTable, setReopenTable] = useState<RestaurantTable | null>(null);
  const [reopenPinOpen, setReopenPinOpen] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);

  const handleReopenClick = (table: RestaurantTable) => {
    setReopenTable(table);
    setReopenPinOpen(true);
  };

  const performReopen = async (_managerEmail: string, reason: string) => {
    if (!reopenTable) return;
    setReopenBusy(true);
    const tableForMsg = reopenTable;
    let message = '';
    try {
      const { HAS_VALID_FIREBASE_CONFIG } = await import('../../services/firebaseConfig');
      if (!HAS_VALID_FIREBASE_CONFIG) {
        message = 'Reopen table requires Firebase Functions — feature not configured in this build.';
        alert(message);
        return;
      }
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const fn = httpsCallable(getFunctions(), 'reopenTab');
      const res: any = await fn({ table_id: reopenTable.id, reason });
      if (res.data?.success) {
        const orderNumber = res.data.order_number;
        const orderId = res.data.order_id;
        const total = res.data.total || 0;
        message = `✅ Tisch ${tableForMsg.number} wieder geöffnet. Bestellung #${orderNumber} wird fortgesetzt — €${total.toFixed(2)}`;

        // CRITICAL: seed the add-to-existing-order context immediately so the next
        // "+ Add Order" click doesn't race the Firestore listener and end up
        // creating a brand-new order doc. This is the whole point of "reopen":
        // new items must append to the original order number.
        try {
          const { doc: firestoreDoc, setDoc } = await import('../../services/firestoreCompat');
          const { db } = await import('../../services/firebaseConfig');
          const displayArea = tableForMsg.area || 'indoor';
          const contextData: any = {
            number: tableForMsg.number,
            display_number: tableForMsg.number,
            type: 'dine_in',
            area: displayArea,
            start_time: new Date().toISOString(),
            active_order_id: orderId,
            active_order_number: orderNumber,
            customer_name: tableForMsg.customer_name || '',
            _reopened: true,
          };
          if (tableForMsg.id) contextData.id = String(tableForMsg.id);
          localStorage.setItem('selectedTable', JSON.stringify(contextData));
          await setDoc(firestoreDoc(db, 'table_context', 'current'), {
            selectedTable: contextData,
            updated_at: new Date().toISOString(),
          }, { merge: true });
        } catch (ctxErr) {
          console.warn('⚠️ [REOPEN] Could not seed table_context:', ctxErr);
        }

        // Refresh local tableCustomerInfo so the card immediately shows the restored
        // order number. Firestore listeners will eventually do this, but the user
        // is clicking things right now — we don't want to wait for propagation.
        setTableCustomerInfo(prev => {
          const next = new Map(prev);
          const existing = next.get(tableForMsg.number) || {};
          next.set(tableForMsg.number, {
            ...existing,
            order_id: orderId,
            order_number: orderNumber,
            total,
          });
          return next;
        });
      } else {
        message = '⚠️ Reopen failed';
      }
    } catch (e: any) {
      message = `❌ ${e?.message || 'Reopen failed'}`;
    } finally {
      setReopenBusy(false);
      setReopenTable(null);
      setReopenPinOpen(false);
    }
    if (message) {
      // GlobalAlertProvider picks up alert() and renders a themed centered
      // dialog automatically — no duplicate dialog state needed here.
      setTimeout(() => alert(message), 100);
    }
  };

  const handleTableClick = async (table: RestaurantTable) => {
    console.log('🖱️ [RESTAURANT] ===== handleTableClick CALLED =====');
    console.log('🖱️ [RESTAURANT] Table:', { number: table.number, status: table.status, id: table.id });
    
    if (table.status === 'available') {
      console.log('✅ [RESTAURANT] Table is available, proceeding with update...');
      try {
        console.log(`🪑 [RESTAURANT] STEP 1: Marking table ${table.number} as occupied...`);
        
        // Update table status in Firebase - DO NOT set customer_name yet, wait for order creation
        console.log(`🪑 [RESTAURANT] STEP 2: Calling updateTableByNumber...`);
        await firebaseTableService.updateTableByNumber(
          table.number,
          'occupied',
          {
          customer_name: undefined, // Leave empty - will be set when order is created
          start_time: new Date().toISOString(),
          total_amount: 0, // Initialize to 0
          item_count: 0 // Initialize to 0
          },
          table.area // CRITICAL: Pass area to ensure correct table is updated
        );

        // Initialize tab on the table
        const foundTable = await firebaseTableService.findTable(table.number, table.area);
        if (foundTable && foundTable.id) {
          await firebaseTableService.openTab(foundTable.id);
          console.log(`✅ [RESTAURANT] Tab opened on table ${table.number}`);
        }
        
        // Update local state and recalculate counts from actual data
        setTables(prevTables => {
          const updatedTables = prevTables.map(t => 
            t.number === table.number 
              ? { ...t, status: 'occupied' as const, customer_name: undefined, start_time: new Date().toISOString(), total_amount: 0, item_count: 0 }
              : t
          );
          // Recalculate counts from updated tables to ensure accuracy
          updateTableCounts(updatedTables);
          return updatedTables;
        });

        console.log(`✅ [RESTAURANT] STEP 3: Table ${table.number} marked as occupied in Firebase`);

        // Save table context to Firebase for Dashboard to read
        console.log('🪑 [RESTAURANT] STEP 4: Saving table context to Firebase...');
        try {
          const { doc, setDoc } = await import('../../services/firestoreCompat');
          const { db } = await import('../../services/firebaseConfig');

          // display_number must be the REAL physical table number — receipts,
          // kitchen tickets, and tab labels all print this. Using a sequential
          // UI index made "Tisch 100" reprint as "Tisch 5" on the next round.
          const displayNumber = table.number;
          
          // Save table context to Firebase for Dashboard to read
          // Use setDoc with merge to create if doesn't exist
          const tableContextRef = doc(db, 'table_context', 'current');
          
          console.log('🪑 [RESTAURANT] STEP 5: Setting table context document...');
          // FIXED: Build object without undefined fields (Firebase doesn't allow undefined)
          // Include both database number (for operations) and display number (for UI)
          const tableContextData: any = {
              number: table.number, // Database number (for operations)
              display_number: displayNumber, // Sequential display number (for UI)
              type: 'dine_in',
            area: table.area || 'indoor',
              start_time: new Date().toISOString()
          };
          // Include id if it exists (Firestore IDs are strings)
          if (table.id) {
            tableContextData.id = String(table.id);
          }

          // ALWAYS save to localStorage as backup (Dashboard reads this as fallback)
          localStorage.setItem('selectedTable', JSON.stringify(tableContextData));

          await setDoc(tableContextRef, {
            selectedTable: tableContextData,
            updated_at: new Date().toISOString()
          }, { merge: true });

          console.log('✅ [RESTAURANT] STEP 6: Table context saved to Firebase + localStorage', {
            database_number: table.number,
            display_number: displayNumber,
            area: table.area
          });
        } catch (error) {
          console.error('❌ [RESTAURANT] Error saving table context to Firebase:', error);
          // localStorage was already set above as backup
          console.log('✅ [RESTAURANT] Table context available in localStorage fallback');
        }
        
        // Navigate to dashboard
        console.log('🪑 [RESTAURANT] STEP 7: Navigating to dashboard...');
        try {
          // Use React Router navigation for better control
          navigate('/dashboard', { replace: false });
          console.log('✅ [RESTAURANT] STEP 8: Navigation initiated via React Router');
        } catch (navError) {
          console.warn('⚠️ [RESTAURANT] React Router navigation failed, using window.location:', navError);
          // Fallback to window.location if React Router fails
        window.location.href = '/dashboard';
        }
        
      } catch (error: any) {
        console.error('❌ [RESTAURANT] ===== ERROR IN handleTableClick =====');
        console.error('❌ [RESTAURANT] Error updating table status in Firebase:', error);
        console.error('❌ [RESTAURANT] Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack,
          tableNumber: table.number,
          tableId: table.id
        });
        
        // If table doesn't exist in Firebase, try to create it
        if (error.message && error.message.includes('not found')) {
          console.log('🔄 [RESTAURANT] Table not found in Firebase, attempting to create it...');
          try {
            await firebaseTableService.createTable({
              number: table.number,
              capacity: table.capacity || 4,
              area: table.area || 'indoor',
              status: 'occupied'
            });
            console.log('✅ [RESTAURANT] Table created in Firebase, retrying update...');
            
            // Retry the update
            await firebaseTableService.updateTableByNumber(
              table.number, 
              'occupied', 
              {
              customer_name: undefined,
              start_time: new Date().toISOString(),
              total_amount: 0,
              item_count: 0
              },
              table.area // CRITICAL: Pass area to ensure correct table is updated
            );
            
            // Continue with navigation
            window.location.href = '/dashboard';
            return;
          } catch (createError: any) {
            console.error('❌ [RESTAURANT] Failed to create table:', createError);
            setSnackbar({
              open: true,
              message: `Failed to create/update table ${table.number}. Please try again.`,
              severity: 'error'
            });
            return;
          }
        }
        
        setSnackbar({
          open: true,
          message: `Failed to update table status: ${error.message || 'Unknown error'}. Please try again.`,
          severity: 'error'
        });
      }
    } else if (table.status === 'occupied') {
      // Permission check — only table owner or admin can add orders
      const permission = canModifyTable(table);
      if (!permission.allowed) {
        setCenteredAlert({ open: true, title: 'Access Restricted', icon: '🔒', message: permission.reason || 'Permission denied' });
        return;
      }
      // Table is occupied — add another round to the running tab
      console.log(`🪑 [RESTAURANT] Table ${table.number} is occupied, adding another round...`);
      try {
        // display_number must equal the REAL table number — see same fix in the
        // empty-table click branch above. Sequential indices were flipping the
        // printed table number between rounds.
        const displayNumber = table.number;

        // Save table context to Firebase for Dashboard
        const { doc: firestoreDoc, setDoc } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        const tableContextRef = firestoreDoc(db, 'table_context', 'current');
        const tableContextData: any = {
          number: table.number,
          display_number: displayNumber,
          type: 'dine_in',
          area: table.area || 'indoor',
          start_time: table.start_time || new Date().toISOString()
        };
        if (table.id) tableContextData.id = String(table.id);
        // CRITICAL: Pass existing customer name so Dashboard/Checkout can pre-fill it
        if (table.customer_name) tableContextData.customer_name = table.customer_name;
        // Pass active order ID and number so Dashboard can add items to existing order
        const customerInfo = tableCustomerInfo.get(table.number);
        if (customerInfo?.order_id) tableContextData.active_order_id = customerInfo.order_id;
        if (customerInfo?.order_number) tableContextData.active_order_number = customerInfo.order_number;

        // ALWAYS save to localStorage as backup
        localStorage.setItem('selectedTable', JSON.stringify(tableContextData));

        await setDoc(tableContextRef, {
          selectedTable: tableContextData,
          updated_at: new Date().toISOString()
        }, { merge: true });

        console.log(`✅ [RESTAURANT] Navigating to dashboard to add round for table ${table.number}`);
        navigate('/dashboard', { replace: false });
      } catch (error: any) {
        console.error('❌ [RESTAURANT] Error setting up add-order for occupied table:', error);
        // Try navigating anyway — localStorage has the context
        const localCtx = localStorage.getItem('selectedTable');
        if (localCtx) {
          console.log('✅ [RESTAURANT] Navigating with localStorage fallback');
          navigate('/dashboard', { replace: false });
        } else {
          setSnackbar({
            open: true,
            message: `Error: ${(error as any).message || 'Unknown error'}`,
            severity: 'error'
          });
        }
      }
    } else if (table.status === 'reserved') {
      // Show reservation details for reserved tables
      console.log('🔍 Looking for reservation for table:', table.number);
      console.log('📋 Available reservations:', reservations);
      const reservation = reservations.find(r => r.table_number === table.number);
      console.log('🎯 Found reservation:', reservation);
      
      if (reservation) {
        setSelectedReservation(reservation);
        setReservationDetailsDialog(true);
        console.log('✅ Opening reservation details dialog');
      } else {
        console.log('❌ No reservation found for table', table.number);
        setSnackbar({
          open: true,
          message: 'No reservation found for this table',
          severity: 'warning'
        });
      }
    }
  };

  const handleReserveTable = (table: RestaurantTable | null) => {
    setSelectedTableForReservation(table);

    // Set default reservation time to 1 hour from now
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const defaultTime = now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM

    setReservationData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      reservation_time: defaultTime,
      party_size: 2,
      special_requests: ''
    });
    setReservationDialogOpen(true);
  };

  const handleCreateReservation = async () => {
    // Validate required fields
    if (!reservationData.customer_name.trim()) {
      setSnackbar({
        open: true,
        message: 'Customer name is required',
        severity: 'error'
      });
      return;
    }

    if (!reservationData.reservation_time) {
      setSnackbar({
        open: true,
        message: 'Reservation time is required',
        severity: 'error'
      });
      return;
    }

    // Validate reservation time is in the future
    const reservationDateTime = new Date(reservationData.reservation_time);
    const now = new Date();
    if (reservationDateTime <= now) {
      setSnackbar({
        open: true,
        message: 'Reservation time must be in the future',
        severity: 'error'
      });
      return;
    }

    try {
      const tableNum = selectedTableForReservation?.number || 0;
      console.log('🎯 Creating reservation', tableNum ? `for table: ${tableNum}` : '(no table - waitlist)');
      console.log('📋 Reservation data:', reservationData);

      // Create reservation in Firebase (no authentication required)
      const reservationData_cloud = {
        table_number: tableNum,
        customer_name: reservationData.customer_name,
        customer_phone: reservationData.customer_phone,
        customer_email: reservationData.customer_email,
        reservation_time: reservationData.reservation_time,
        party_size: reservationData.party_size,
        special_requests: reservationData.special_requests,
        status: 'confirmed' as const
      };

      try {
        const reservationId = await ReservationCloudService.createReservation(reservationData_cloud);
        console.log('✅ Reservation saved to cloud with ID:', reservationId);

        // If a table was selected, update its status
        if (selectedTableForReservation) {
          // CRITICAL: Update table status in Firestore to 'reserved' so real-time listener stays consistent
          try {
            await firebaseTableService.updateTableByNumber(
              selectedTableForReservation.number,
              'reserved',
              {
                customer_name: reservationData.customer_name,
                start_time: reservationData.reservation_time,
              },
              selectedTableForReservation.area
            );
            console.log('✅ Table status updated to reserved in Firestore');
          } catch (err) {
            console.warn('⚠️ Could not update table status in Firestore:', err);
          }

          // Update table status to reserved locally and recalculate counts
          setTables(prevTables => {
            const updatedTables = prevTables.map(t =>
              t.number === selectedTableForReservation.number
                ? { ...t, status: 'reserved' as const, customer_name: reservationData.customer_name }
                : t
            );
            updateTableCounts(updatedTables);
            return updatedTables;
          });
        }

        // Refresh reservations from cloud
        await fetchReservations();

        // Show success message and close dialog
        const successMsg = tableNum
          ? `Reservation created successfully for Table ${tableNum}`
          : `Waitlist reservation created for ${reservationData.customer_name}`;
        setSnackbar({
          open: true,
          message: successMsg,
          severity: 'success'
        });
        setReservationDialogOpen(false);
      } catch (error) {
        console.error('❌ Failed to save reservation to cloud:', error);
        
        // Show error message
        setSnackbar({
          open: true,
          message: 'Failed to create reservation. Please try again.',
          severity: 'error'
        });
      }

      // Update table status counts immediately
      setTableStatusCounts(prevCounts => {
        const newCounts = { ...prevCounts };
        newCounts.available = Math.max(0, newCounts.available - 1);
        newCounts.reserved = newCounts.reserved + 1;
        return newCounts;
      });
        
      setSnackbar({
        open: true,
        message: `Reservation created${selectedTableForReservation ? ` for Table ${selectedTableForReservation.number}` : ''}`,
        severity: 'success'
      });
      setReservationDialogOpen(false);
    } catch (error) {
      console.error('Error creating reservation:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection and try again.',
        severity: 'error'
      });
    }
  };

  // handleAddTable - DISABLED: Table management is now admin-only
  // Removed unused function to fix TypeScript error

  const handleCreateTable = async () => {
    try {
      console.log('🪑 [FIREBASE TABLE] Creating new table...');
      console.log('🪑 [FIREBASE TABLE] New table data:', newTableData);
      
      // Validate input
      if (!newTableData.number || !newTableData.capacity || !newTableData.area) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        return;
      }
      
      // Parse the table number from user input
      const requestedTableNumber = parseInt(newTableData.number);
      
      // Validate table number is a positive integer
      if (isNaN(requestedTableNumber) || requestedTableNumber <= 0) {
        setSnackbar({
          open: true,
          message: 'Table number must be a positive number',
          severity: 'error'
        });
        return;
      }
      
      // Validate capacity is a positive integer
      const requestedCapacity = parseInt(String(newTableData.capacity));
      if (isNaN(requestedCapacity) || requestedCapacity <= 0) {
        setSnackbar({
          open: true,
          message: 'Capacity must be a positive number',
          severity: 'error'
        });
        return;
      }
      
      // CRITICAL: Ensure area is valid before creating table
      const tableArea = (newTableData.area === 'indoor' || newTableData.area === 'outdoor') 
        ? newTableData.area 
        : seatingArea; // Fallback to current seating area
      
      console.log(`🪑 [FIREBASE TABLE] Creating table with area: ${tableArea}`);
      
      // CRITICAL: Set flag to prevent fetchTableCustomerInfo from running
      // This protects existing customer data from being overwritten
      setIsTableOperationInProgress(true);
      
      try {
        // First, clean up any truly invalid/orphaned tables (only those with invalid numbers)
        await cleanupInvalidTables();
        
        // IMPORTANT: Fetch fresh tables from Firebase to check for duplicates
        // This ensures we have the most up-to-date list after deletions and cleanup
        const cleanedTables = await firebaseTableService.getTables();
      
      // Debug: Log ALL tables to see what's in Firebase
      console.log(`📋 [DEBUG] All tables in Firebase (${cleanedTables.length} total):`, cleanedTables.map(t => ({
        id: t.id,
        number: t.number,
        area: t.area || 'MISSING',
        status: t.status
      })));
      
      // Filter tables by the same area - table numbers can be duplicated across areas
      // IMPORTANT: Only check tables in the SAME area
      const existingTablesInArea = cleanedTables.filter(t => {
        // Normalize area - if missing, skip it (don't count as duplicate)
        const tableAreaNormalized = (t.area === 'indoor' || t.area === 'outdoor') ? t.area : null;
        const matchesArea = tableAreaNormalized === tableArea;
        const hasValidNumber = t.number && !isNaN(t.number) && t.number > 0;
        
        // Only include if it has valid area AND matches the target area AND has valid number
        return tableAreaNormalized && matchesArea && hasValidNumber;
      });
      
      const existingNumbersInArea = existingTablesInArea.map(t => t.number);
      
      console.log(`📋 [AREA CHECK] Tables in ${tableArea} area:`, existingTablesInArea.map(t => `Table ${t.number}`));
      console.log(`📋 [AREA CHECK] Existing table numbers in ${tableArea} area:`, existingNumbersInArea);
      console.log(`📋 [AREA CHECK] User requested: Table ${requestedTableNumber} in ${tableArea} area`);
      
      // Check if the requested table number already exists IN THE SAME AREA
      if (existingNumbersInArea.includes(requestedTableNumber)) {
        // Find the conflicting table for better error message
        const conflictingTable = existingTablesInArea.find(t => t.number === requestedTableNumber);
        console.error(`❌ [CONFLICT] Table ${requestedTableNumber} already exists:`, conflictingTable);
        
        // Offer to delete the conflicting table
        const shouldDelete = window.confirm(
          `Table ${requestedTableNumber} already exists in ${tableArea} area.\n\n` +
          `Would you like to delete the existing table and create a new one?`
        );
        
        if (shouldDelete && conflictingTable?.id) {
          try {
            await firebaseTableService.deleteTable(conflictingTable.id);
            console.log(`✅ Deleted conflicting table ${requestedTableNumber}`);
            setSnackbar({
              open: true,
              message: `Deleted existing Table ${requestedTableNumber}. Creating new table...`,
              severity: 'info'
            });
            // Continue with creation below
          } catch (deleteError: any) {
            console.error('❌ Failed to delete conflicting table:', deleteError);
            setSnackbar({
              open: true,
              message: `Failed to delete existing table: ${deleteError.message}`,
              severity: 'error'
            });
            return;
          }
        } else {
          setSnackbar({
            open: true,
            message: `Table ${requestedTableNumber} already exists in ${tableArea} area. Please choose a different number.`,
            severity: 'error'
          });
          return;
        }
      }
      
      // Use the user's requested number
      const tableNumber = requestedTableNumber;
      
      console.log(`📋 [CREATE] Creating Table ${tableNumber} in ${tableArea} area with capacity ${requestedCapacity}`);
      
      // Create table in Firebase
      const newTable = await firebaseTableService.createTable({
        number: tableNumber,
        capacity: requestedCapacity,
        area: tableArea, // CRITICAL: Use the validated area
        status: 'available'
      });
      
      console.log('✅ [CREATE] Table created in Firebase:', {
        id: newTable.id,
        number: tableNumber,
        area: tableArea,
        capacity: requestedCapacity
      });
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Table ${tableNumber} (${tableArea}) created successfully with capacity ${requestedCapacity}!`,
        severity: 'success'
      });
      
      setAddTableDialog(false);
      setNewTableData({ number: '', capacity: 4, area: seatingArea }); // Reset form with current area
      
      // Reset auto-initialization flag since user is manually creating tables
      skipAutoInitializationRef.current = false;
      
      // IMPORTANT: Wait a moment for Firebase to process, then refresh
      // CRITICAL: Do NOT call fetchTableCustomerInfo here - it might incorrectly link orders to new tables
      setTimeout(async () => {
        console.log('🔄 [CREATE] Refreshing tables after creation...');
      await fetchTables(true); // Silent background refresh
        console.log('✅ [CREATE] Tables refreshed. New table should be available.');
        // Explicitly ensure the new table is available (not occupied)
        // This prevents fetchTableCustomerInfo from incorrectly updating it
        try {
          // Get the area from the table being created (should match seatingArea)
          const tableAreaForCreation = tableArea; // Use the area from the creation context
          await firebaseTableService.updateTableByNumber(
            tableNumber, 
            'available', 
            {
              customer_name: undefined,
              total_amount: undefined,
              item_count: undefined,
              start_time: undefined
            },
            tableAreaForCreation // CRITICAL: Pass area to ensure correct table is updated
          );
          console.log(`✅ [CREATE] Ensured Table ${tableNumber} is set to 'available' status`);
        } catch (error) {
          console.warn(`⚠️ [CREATE] Could not ensure table status:`, error);
        }
        
        // Fetch customer info for all tables after table creation
        setTimeout(() => {
          setIsTableOperationInProgress(false);
          console.log(`✅ [CREATE] Table operation complete - fetchTableCustomerInfo can now run safely`);
          fetchTableCustomerInfo();
        }, 2000); // 2 seconds delay
      }, 500);
      
      // Ensure we're on restaurant page (in case navigated elsewhere)
      if (window.location.pathname !== '/restaurant') {
        navigate('/restaurant');
      }
      
      } finally {
        // CRITICAL: Always reset the flag, even if there's an error
        // Use a timeout to ensure it's reset after operations complete
        setTimeout(() => {
          setIsTableOperationInProgress(false);
          console.log('✅ [CREATE] Table operation flag reset');
        }, 3000);
      }
    } catch (error: any) {
      console.error('❌ Error creating table in Firebase:', error);
      setSnackbar({
        open: true,
        message: `Failed to create table: ${error.message || 'Please try again.'}`,
        severity: 'error'
      });
      // Reset flag on error
      setTimeout(() => {
        setIsTableOperationInProgress(false);
      }, 1000);
    }
  };

  // Function to clean up orphaned/invalid tables (only truly invalid ones)
  const cleanupInvalidTables = async () => {
    try {
      console.log('🧹 [CLEANUP] Starting table cleanup...');
      const allTables = await firebaseTableService.getTables();
      
      // Find ONLY tables with truly invalid data (not just missing area - area can be fixed)
      const invalidTables = allTables.filter(t => {
        // Only delete if number is completely invalid (not just area)
        const hasInvalidNumber = !t.number || isNaN(t.number) || t.number <= 0;
        // Only delete if area is completely missing AND number is also invalid
        const hasCompletelyInvalidArea = !t.area && hasInvalidNumber;
        return hasInvalidNumber || hasCompletelyInvalidArea;
      });
      
      console.log(`🧹 [CLEANUP] Found ${invalidTables.length} truly invalid tables (missing/invalid number):`, invalidTables);
      
      // Delete only truly invalid tables
      for (const invalidTable of invalidTables) {
        if (invalidTable.id) {
          console.log(`🗑️ [CLEANUP] Deleting invalid table:`, invalidTable);
          await firebaseTableService.deleteTable(invalidTable.id);
        }
      }
      
      if (invalidTables.length > 0) {
        console.log(`✅ [CLEANUP] Cleaned up ${invalidTables.length} invalid table(s)`);
      }
      
      return invalidTables;
    } catch (error: any) {
      console.error('❌ [CLEANUP] Error cleaning up tables:', error);
      return [];
    }
  };

  // handleDeleteTable - DISABLED: Table management is now admin-only
  // Removed unused function to fix TypeScript error

  // handleDeleteAllTables - DISABLED: Table management is now admin-only
  // Removed unused function to fix TypeScript error

  const handleCancelReservation = async (reservation: any) => {
    if (!reservation) {
      console.error('❌ No reservation provided to cancel');
      setSnackbar({
        open: true,
        message: 'No reservation selected to cancel.',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('❌ Cancelling reservation:', reservation);
      
      // Delete reservation from Firebase
      if (reservation.id) {
        try {
          await ReservationCloudService.deleteReservation(reservation.id);
          console.log('✅ Reservation deleted from Firebase');
        } catch (error) {
          console.error('❌ Failed to delete reservation from Firebase:', error);
          setSnackbar({
            open: true,
            message: 'Failed to delete reservation from database. Please try again.',
            severity: 'error'
          });
          return; // Don't continue if deletion fails
        }
      } else {
        console.warn('⚠️ Reservation has no ID, cannot delete from Firebase');
        setSnackbar({
          open: true,
          message: 'Reservation ID missing. Cannot cancel reservation.',
          severity: 'error'
        });
        return;
      }

      // Update table status to available
      const table = tables.find(t => t.number === reservation.table_number);
      if (table) {
        // Update table status in Firebase directly (more reliable than API)
        try {
          await firebaseTableService.updateTableStatus(String(table.id), 'available', {
            customer_name: null,
            start_time: null
          });
          console.log('✅ Table status updated to available in Firebase');
        } catch (firebaseError) {
          console.warn('⚠️ Failed to update table in Firebase, trying API fallback:', firebaseError);
          
          // Fallback to API if Firebase update fails
          let token = localStorage.getItem('access_token') || 
                      localStorage.getItem('accessToken') || 
                      localStorage.getItem('token') ||
                      localStorage.getItem('auth_token');
          
          if (!token) {
            token = sessionStorage.getItem('access_token') || 
                    sessionStorage.getItem('accessToken') || 
                    sessionStorage.getItem('token') ||
                    sessionStorage.getItem('auth_token');
          }
          
          if (!token) {
            const sessionData = localStorage.getItem('session') || 
                               localStorage.getItem('saheeh_pos_session') ||
                               sessionStorage.getItem('session');
            if (sessionData) {
              try {
                const session = JSON.parse(sessionData);
                token = session.access || session.access_token || session.token || session.tokens?.access;
              } catch (e) {
                console.log('Could not parse session data for cancel reservation:', e);
              }
            }
          }

          if (token) {
            try {
              const response = await fetch(`https://__SAHEEH_LEGACY_BACKEND_UNSET__/api/v1/restaurant/tables/${table.id}/status/`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  status: 'available'
                })
              });

              if (response.ok) {
                console.log('✅ Table status updated to available via API');
              } else {
                console.log('⚠️ API failed, using local update only');
              }
            } catch (error) {
              console.log('⚠️ API error, using local update only');
            }
          }
        }

        // Update table status locally and recalculate counts
        setTables(prevTables => {
          const updatedTables = prevTables.map(t => 
            t.number === table.number 
              ? { ...t, status: 'available' as const, customer_name: undefined, start_time: undefined }
              : t
          );
          // Recalculate counts from updated tables to ensure accuracy
          updateTableCounts(updatedTables);
          return updatedTables;
        });
      } else {
        console.warn('⚠️ Table not found for reservation:', reservation.table_number);
      }

      // Refresh reservations from cloud
      await fetchReservations();
        
      setSnackbar({
        open: true,
        message: 'Reservation cancelled successfully',
        severity: 'success'
      });
        
      // Close reservation details dialog
      setReservationDetailsDialog(false);
      setSelectedReservation(null);

    } catch (error) {
      console.error('❌ Error cancelling reservation:', error);
      setSnackbar({
        open: true,
        message: 'Failed to cancel reservation. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleConfirmArrival = async (reservation: any) => {
    try {
      console.log('✅ Confirming arrival for reservation:', reservation);
      
      // Update table status to occupied locally
      setTables(prevTables => {
        const updatedTables = prevTables.map(table => 
          table.number === reservation.table_number 
            ? { 
                ...table, 
                status: 'occupied' as const,
                customer_name: reservation.customer_name,
                start_time: new Date().toISOString()
              }
            : table
        );
        
        // Data is now stored in Firebase only - no localStorage needed
        // Recalculate counts from updated tables to ensure accuracy
        updateTableCounts(updatedTables);
        return updatedTables;
      });

      // Remove reservation from cloud
      if (reservation.id) {
        try {
          await ReservationCloudService.deleteReservation(reservation.id);
          console.log('✅ Reservation deleted from cloud');
          
          // Refresh reservations from cloud
          await fetchReservations();
        } catch (error) {
          console.error('❌ Failed to delete reservation from cloud:', error);
          setSnackbar({
            open: true,
            message: 'Failed to delete reservation. Please try again.',
            severity: 'error'
          });
          return;
        }
      }

      console.log('✅ Guest arrival confirmed locally');
        
        setSnackbar({
          open: true,
          message: `Guest ${reservation.customer_name} has arrived at Table ${reservation.table_number}`,
          severity: 'success'
        });
        
        setReservationDetailsDialog(false);
        await fetchTables(true); // Silent background refresh
        await fetchReservations(); // Refresh reservations
    } catch (error) {
      console.error('Error confirming arrival:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection and try again.',
        severity: 'error'
      });
    }
  };

  const handleStartOrder = async (reservation: any) => {
    try {
      console.log('🍽️ Starting order for reservation:', reservation);
      
      // First confirm arrival (update table status)
      await handleConfirmArrival(reservation);
      
      // Set table context for dashboard in Firebase
      const tableContext = {
      id: reservation.table_number,
      number: reservation.table_number,
      type: 'dine_in',
      customer_name: reservation.customer_name,
        party_size: reservation.party_size,
        area: tables.find(t => t.number === reservation.table_number)?.area || 'indoor'
      };
      
      try {
        const { doc, updateDoc } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        
        // Save table context to Firebase for Dashboard to read
        const tableContextRef = doc(db, 'table_context', 'current');
        await updateDoc(tableContextRef, {
          selectedTable: tableContext,
          updated_at: new Date().toISOString()
        });
        
        console.log('✅ Table context saved to Firebase for Dashboard:', tableContext);
      } catch (error) {
        console.error('❌ Error saving table context to Firebase:', error);
        // Fallback to localStorage if Firebase fails
        localStorage.setItem('selectedTable', JSON.stringify(tableContext));
        console.log('📋 Table context set for dashboard (fallback):', tableContext);
      }
      
      // Close reservation details dialog
      setReservationDetailsDialog(false);
      setSelectedReservation(null);
      
      // Navigate to dashboard
    window.location.href = '/dashboard';
      
    } catch (error) {
      console.error('❌ Error starting order:', error);
      setSnackbar({
        open: true,
        message: 'Failed to start order. Please try again.',
        severity: 'error'
      });
    }
  };

  // Admin-only: force-clear a table's entire history
  const handleAdminClearTable = async (table: RestaurantTable) => {
    if (!window.confirm(`Clear ALL history for Table ${table.number}? This will complete all orders and reset the table.`)) return;
    try {
      const { collection: col, query: q, where: w, getDocs: gd, doc: fsDoc, updateDoc, deleteDoc, deleteField, serverTimestamp, getDoc } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const now = new Date().toISOString();

      // 1. Complete all active orders for this table
      const ordersRef = col(db, 'orders');
      const completedIds = new Set<string>();
      for (const tryNum of [table.number, String(table.number), Number(table.number)]) {
        for (const snap of await Promise.all([
          gd(q(ordersRef, w('table_number', '==', tryNum))),
        ])) {
          for (const d of snap.docs) {
            if (completedIds.has(d.id)) continue;
            const s = (d.data().status || '').toLowerCase();
            if (s !== 'completed' && s !== 'cancelled') {
              await updateDoc(fsDoc(db, 'orders', d.id), { status: 'completed', completed_at: serverTimestamp(), updated_at: serverTimestamp() });
              completedIds.add(d.id);
            }
          }
        }
      }

      // 2. Reset the restaurant_table document
      const tablesRef = col(db, 'restaurant_tables');
      const snap = await gd(q(tablesRef, w('number', '==', table.number)));
      for (const d of snap.docs) {
        await updateDoc(fsDoc(db, 'restaurant_tables', d.id), {
          status: 'available',
          customer_name: deleteField(), total_amount: deleteField(), item_count: deleteField(),
          start_time: deleteField(), end_time: deleteField(), order_id: deleteField(),
          active_order_id: deleteField(), active_order_number: deleteField(), occupied_by: deleteField(),
          tab_open: false, tab_orders: deleteField(), tab_total: deleteField(),
          tab_item_count: deleteField(), tab_rounds: deleteField(),
          split_payment_complete: deleteField(), split_people: deleteField(),
          split_total: deleteField(), split_session_id: deleteField(),
          updated_at: now,
        });
      }

      // 3. Clear table_context if it points to this table
      try {
        const ctxSnap = await getDoc(fsDoc(db, 'table_context', 'current'));
        if (ctxSnap.exists()) {
          const num = ctxSnap.data()?.selectedTable?.number;
          if (Number(num) === Number(table.number)) await deleteDoc(fsDoc(db, 'table_context', 'current'));
        }
      } catch { /* ignore */ }

      // 4. Update local state
      setTables(prev => prev.map(t => t.number === table.number
        ? { ...t, status: 'available' as const, customer_name: undefined, total_amount: undefined, item_count: 0, start_time: undefined }
        : t
      ));
      setSnackbar({ open: true, message: `Table ${table.number} cleared successfully`, severity: 'success' });
    } catch (err) {
      console.error('Admin clear table error:', err);
      setSnackbar({ open: true, message: 'Failed to clear table', severity: 'error' });
    }
  };

  // Admin-only: clear ALL tables at once (end-of-night / fresh start)
  const handleClearAllTables = async () => {
    const occupiedCount = tables.filter(t => t.status === 'occupied' || t.status === 'reserved').length;
    if (!window.confirm(
      `Clear ALL tables?\n\n` +
      `${occupiedCount} table(s) currently occupied/reserved will be reset to Available.\n` +
      `All active orders will be marked as completed.\n\n` +
      `This cannot be undone.`
    )) return;

    try {
      const { collection: col, query: q, where: w, getDocs: gd, doc: fsDoc, updateDoc, deleteDoc, deleteField, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const now = new Date().toISOString();

      // 1. Complete ALL active orders (any table)
      const ordersRef = col(db, 'orders');
      const activeOrdersSnap = await gd(q(ordersRef, w('status', 'in', ['pending', 'preparing', 'ready', 'active'])));
      const orderUpdates = activeOrdersSnap.docs.map(d =>
        updateDoc(fsDoc(db, 'orders', d.id), {
          status: 'completed',
          completed_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
      );
      await Promise.all(orderUpdates);
      console.log(`✅ Completed ${orderUpdates.length} active orders`);

      // 2. Reset ALL restaurant_tables documents
      const tablesSnap = await gd(col(db, 'restaurant_tables'));
      const tableUpdates = tablesSnap.docs.map(d =>
        updateDoc(fsDoc(db, 'restaurant_tables', d.id), {
          status: 'available',
          customer_name: deleteField(), total_amount: deleteField(), item_count: deleteField(),
          start_time: deleteField(), end_time: deleteField(), order_id: deleteField(),
          active_order_id: deleteField(), active_order_number: deleteField(), occupied_by: deleteField(),
          tab_open: false, tab_orders: deleteField(), tab_total: deleteField(),
          tab_item_count: deleteField(), tab_rounds: deleteField(),
          split_payment_complete: deleteField(), split_people: deleteField(),
          split_total: deleteField(), split_session_id: deleteField(),
          updated_at: now,
        })
      );
      await Promise.all(tableUpdates);
      console.log(`✅ Reset ${tableUpdates.length} tables to available`);

      // 3. Clear table_context doc
      try {
        await deleteDoc(fsDoc(db, 'table_context', 'current'));
      } catch { /* may not exist */ }

      // 4. Cancel all active split drafts
      try {
        const splitDraftsSnap = await gd(q(col(db, 'split_table_drafts'), w('status', 'in', ['draft', 'pending'])));
        await Promise.all(splitDraftsSnap.docs.map(d =>
          updateDoc(fsDoc(db, 'split_table_drafts', d.id), { status: 'cancelled', updated_at: serverTimestamp() })
        ));
        console.log(`✅ Cancelled ${splitDraftsSnap.docs.length} split drafts`);
      } catch { /* ignore */ }

      // 5. Update local state
      setTables(prev => prev.map(t => ({
        ...t,
        status: 'available' as const,
        customer_name: undefined,
        total_amount: undefined,
        item_count: 0,
        start_time: undefined,
        tab_open: false,
        tab_total: undefined,
        tab_item_count: undefined,
        split_payment_complete: undefined,
      })));

      setSnackbar({ open: true, message: `All ${tableUpdates.length} tables cleared successfully`, severity: 'success' });
    } catch (err) {
      console.error('Clear all tables error:', err);
      setSnackbar({ open: true, message: 'Failed to clear all tables', severity: 'error' });
    }
  };

  // Helper: Check if current user can modify a table (owner or admin)
  const canModifyTable = (table: RestaurantTable): { allowed: boolean; reason?: string; currentUser?: string } => {
    const HARDCODED_ADMINS = ['admin@saheehpos.com', 'admin@saheehpos.com'];
    const occupiedBy = (table as any).occupied_by || '';

    // Primary: read email directly from Firebase Auth (most reliable, no async required)
    const firebaseEmail = getAuth().currentUser?.email?.toLowerCase() || '';
    if (HARDCODED_ADMINS.includes(firebaseEmail)) return { allowed: true, currentUser: firebaseEmail };

    // Secondary: check role from localStorage profile
    let storedEmail = '';
    let storedRole = '';
    try {
      const raw = localStorage.getItem('saheeh_pos_user');
      if (raw) {
        const profile = JSON.parse(raw);
        storedEmail = (profile.email || '').toLowerCase();
        storedRole = profile.role || '';
      }
    } catch { /* ignore */ }

    const currentEmail = firebaseEmail || storedEmail;

    // Hardcoded admin override (catches stale localStorage role)
    if (HARDCODED_ADMINS.includes(currentEmail)) return { allowed: true, currentUser: currentEmail };

    // Role-based admin check
    const isAdmin = storedRole === 'admin' || storedRole === 'manager'
      || currentEmail.includes('admin') || currentEmail.includes('manager');
    if (isAdmin) return { allowed: true, currentUser: currentEmail };

    // No occupied_by set — allow (legacy tables or brand-new table)
    if (!occupiedBy) return { allowed: true, currentUser: currentEmail };

    // QR order tables — any POS staff can manage (close, add items, split, charge)
    if (occupiedBy.toLowerCase().includes('qr')) return { allowed: true, currentUser: currentEmail };

    // Check if current user is the one who occupied the table
    if (currentEmail && currentEmail === occupiedBy.toLowerCase()) return { allowed: true, currentUser: currentEmail };

    return {
      allowed: false,
      reason: `This table is managed by ${occupiedBy}. Only that user or an admin can modify it.`,
      currentUser: currentEmail
    };
  };

  // Open Split Table dialog for an occupied table
  const handleSplitTableClick = async (table: RestaurantTable) => {
    const permission = canModifyTable(table);
    if (!permission.allowed) {
      setCenteredAlert({ open: true, title: 'Access Restricted', icon: '🔒', message: permission.reason || 'Permission denied' });
      return;
    }

    try {
      // Fetch active orders for this table to get items and total
      const { collection, query, where, getDocs, getDoc, doc, updateDoc: ud, deleteField: df, serverTimestamp: st, limit: fsLimit } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      // Fetch fresh table data from Firestore (source of truth for active_order_id)
      let freshTableData: any = null;
      try {
        const tablesSnap = await getDocs(query(
          collection(db, 'restaurant_tables'),
          where('number', '==', (table as any).number || parseInt(String(table.id), 10))
        ));
        if (!tablesSnap.empty) {
          freshTableData = tablesSnap.docs[0].data();
          if (freshTableData.split_payment_complete) {
            if (freshTableData.status === 'available' || !freshTableData.tab_open) {
              console.log('🧹 [SPLIT] Clearing stale split_payment_complete flag for table', (table as any).number);
              await ud(tablesSnap.docs[0].ref, {
                split_payment_complete: df(),
                split_session_id: df(),
                split_people: df(),
                split_total: df(),
                updated_at: new Date().toISOString(),
                updatedAt: st(),
              });
            } else {
              setCenteredAlert({ open: true, title: 'Split Already Paid', icon: '💳', message: 'Table already paid via split. Add a new order first to enable splitting.' });
              return;
            }
          }
        }
      } catch (_) { /* non-blocking */ }
      const ordersRef = collection(db, 'orders');
      const tableNum = (table as any).number || table.id;
      const tableNumStr = String(tableNum);
      const tableNumInt = parseInt(tableNumStr, 10);

      let allItems: any[] = [];
      let orderTotal = 0;
      // Use fresh Firestore data for active_order_id (NOT stale React state)
      let orderId = freshTableData?.active_order_id || table.active_order_id || '';

      // DIRECT FETCH: If we have active_order_id, trust it unconditionally and get items/total
      if (orderId) {
        try {
          const snap = await getDoc(doc(db, 'orders', orderId));
          if (snap.exists()) {
            const d = snap.data();
            orderTotal = parseFloat(d.total) || 0;
            (d.items || []).forEach((item: any) => allItems.push(item));
            console.log(`✅ [SPLIT] Direct fetch by active_order_id: ${orderId}, total: €${orderTotal}, items: ${allItems.length}`);
          }
        } catch (_) { /* non-blocking */ }
      }

      // Session start time — only include orders created after this table session began
      let sessionStart: Date | null = null;
      const rawStart = (table as any).start_time;
      if (rawStart) {
        if (rawStart.toDate) sessionStart = rawStart.toDate();
        else if (rawStart.seconds) sessionStart = new Date(rawStart.seconds * 1000);
        else if (typeof rawStart === 'string') sessionStart = new Date(rawStart);
      }
      // If no session start, use 12 hours ago as safety cutoff
      if (!sessionStart) sessionStart = new Date(Date.now() - 12 * 60 * 60 * 1000);

      const seenOrderIds = new Set<string>();
      const isActiveOrder = (d: any, skipTimeCheck = false) => {
        // Positive allow-list — matches the real-time listener and prevents
        // ghost items with non-standard terminal statuses (e.g., 'paid',
        // 'archived', empty string) from leaking into split / receipt flows.
        // See memory/pos-order-status-filter-rule.md.
        const ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
        const s = (d.status || '').toLowerCase();
        if (!ACTIVE_STATUSES.includes(s)) return false;
        if (d.is_split_payment) return false;
        // For direct lookups (by ID/order_number), skip time check — they're explicitly linked to this table
        if (!skipTimeCheck) {
          let orderDate: Date | null = null;
          if (d.created_at?.toDate) orderDate = d.created_at.toDate();
          else if (d.created_at?.seconds) orderDate = new Date(d.created_at.seconds * 1000);
          else if (typeof d.created_at === 'string') orderDate = new Date(d.created_at);
          if (orderDate && sessionStart && orderDate < sessionStart) return false;
        }
        return true;
      };
      const collectFromDoc = (docId: string, d: any, skipTimeCheck = false) => {
        if (seenOrderIds.has(docId)) return;
        seenOrderIds.add(docId);
        if (!isActiveOrder(d, skipTimeCheck)) return;
        if (!orderId) orderId = docId;
        orderTotal += parseFloat(d.total) || 0;
        (d.items || []).forEach((item: any) => allItems.push(item));
      };

      // Strategy: use active_order_id first (single source of truth), then fallback to query
      // NEVER use tab_orders — it accumulates stale IDs across table sessions

      // Step 1: direct fetch by active_order_id (skip time check — explicitly linked)
      if ((table as any).active_order_id) {
        try {
          const snap = await getDoc(doc(db, 'orders', (table as any).active_order_id));
          if (snap.exists()) collectFromDoc(snap.id, snap.data(), true);
        } catch (_) { /* non-blocking */ }
      }

      // Step 2: query by active_order_number (skip time check — explicitly linked)
      if (allItems.length === 0 && (table as any).active_order_number) {
        for (const numForm of [String((table as any).active_order_number), parseInt(String((table as any).active_order_number), 10)]) {
          if (allItems.length > 0) break;
          try {
            const snap = await getDocs(query(ordersRef, where('order_number', '==', numForm), fsLimit(5)));
            snap.forEach(d => collectFromDoc(d.id, d.data(), true));
          } catch (_) { /* non-blocking */ }
        }
      }

      // Step 3: query by table_number (only active, non-split orders)
      // Server-side status filter narrows the result set AND defends against
      // ghost orders even if isActiveOrder() is bypassed somewhere downstream.
      const ACTIVE_STATUSES_ALLOWLIST = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
      if (allItems.length === 0) {
        for (const tryNum of [tableNumInt, tableNumStr, tableNum]) {
          if (allItems.length > 0) break;
          try {
            const snap = await getDocs(query(
              ordersRef,
              where('table_number', '==', tryNum),
              where('order_type', '==', 'dine_in'),
              where('status', 'in', ACTIVE_STATUSES_ALLOWLIST),
              fsLimit(20),
            ));
            snap.forEach(d => collectFromDoc(d.id, d.data()));
          } catch (_) { /* non-blocking */ }
        }
      }

      // Step 4: last resort — table_number without order_type filter (but
      // KEEP the status filter; last-resort must not mean "any order ever").
      if (allItems.length === 0) {
        for (const tryNum of [tableNumInt, tableNumStr, tableNum]) {
          if (allItems.length > 0) break;
          try {
            const snap = await getDocs(query(
              ordersRef,
              where('table_number', '==', tryNum),
              where('status', 'in', ACTIVE_STATUSES_ALLOWLIST),
              fsLimit(20),
            ));
            snap.forEach(d => collectFromDoc(d.id, d.data()));
          } catch (_) { /* non-blocking */ }
        }
      }

      console.log(`🔍 [SPLIT] Table ${tableNumStr}: found ${allItems.length} items from ${seenOrderIds.size} orders (orderId: ${orderId}, sessionStart: ${sessionStart?.toISOString()})`);

      // Fallback: use tab_orders totals if no items found
      if (!orderTotal) orderTotal = (table as any).tab_total || 0;

      const { getCurrentUser } = require('../../services/currentUserHelper');
      const user = getCurrentUser();

      setNewSplitDialog({
        open: true,
        tableId: typeof tableNum === 'number' ? tableNum : parseInt(tableNumStr, 10) || 0,
        tableNumber: tableNumStr,
        orderId,
        orderTotal,
        orderItems: allItems,
        staffMember: user.email || user.name || 'POS System',
      });
    } catch (err) {
      console.error('[SPLIT] Failed to load table orders:', err);
      setSnackbar({ open: true, message: 'Failed to load table orders', severity: 'error' });
    }
  };

  // Print Pre-Bill (Zwischenrechnung) — shows all unpaid items, marked as OPEN
  const handlePrintPreBill = async (table: RestaurantTable) => {
    try {
      setSnackbar({ open: true, message: t('restaurant.prebill_generating'), severity: 'info' });

      const { collection, query, where, getDocs, doc: fsDoc, getDoc: fsGetDoc, limit: fsLimit } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const { firestorePrintService } = await import('../../services/firestorePrintService');

      const tableNum = table.number;
      const ordersRef = collection(db, 'orders');

      // Fetch active (unpaid) orders for this table
      const allItems: any[] = [];
      let totalAmount = 0;
      let orderNumber = '';
      const completedIds = new Set<string>();

      // Helper to process an order document
      //
      // Positive allow-list — matches the real-time listener and CloseTabDialog
      // to guarantee no order with a non-standard terminal status ('delivered',
      // 'paid', '', etc.) can leak into a Zwischenrechnung. This function is
      // read from tab_orders (known-stale IDs, see line 3852 comment) + the
      // table_number fallback query, so the filter must be strict.
      const PRE_BILL_ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
      const processOrder = (docId: string, data: any) => {
        if (completedIds.has(docId)) return;
        const status = (data.status || '').toLowerCase();
        if (!PRE_BILL_ACTIVE_STATUSES.includes(status)) return;
        if (data.is_split_payment) return;
        completedIds.add(docId);
        if (!orderNumber) orderNumber = data.order_number || '';
        const items = data.items || [];
        items.forEach((item: any) => {
          allItems.push({
            name: item.menu_item_name || item.name,
            quantity: item.quantity || 1,
            price: item.price || item.unit_price || 0,
            total: (item.price || item.unit_price || 0) * (item.quantity || 1),
            category_name: item.category_name || 'Sonstiges',
          });
        });
        totalAmount += data.total || 0;
      };

      // PRIORITY 0: Fetch live table doc from Firestore to get active_order_id + tab_orders
      // (the table prop may not have these fields)
      let activeOrderId = (table as any).active_order_id || '';
      let tabOrders: string[] = (table as any).tab_orders || [];
      const tableNumStr = String(tableNum);
      const tableNumInt = parseInt(tableNumStr, 10);
      try {
        // Try number type first (Firestore stores as integer)
        let tablesSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNumInt)));
        if (tablesSnap.empty) {
          // Fallback: try string type
          tablesSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNumStr)));
        }
        tablesSnap.forEach(td => {
          const tData = td.data();
          if (tData.active_order_id) activeOrderId = tData.active_order_id;
          if (tData.tab_orders) tabOrders = tData.tab_orders;
        });
        console.log(`🧾 [PRE-BILL] Table ${tableNum} Firestore: active_order_id=${activeOrderId}, tab_orders=${tabOrders.length}`);
      } catch (e) { console.warn('🧾 [PRE-BILL] Table doc fetch failed:', e); }

      // PRIORITY 1: Direct fetch by active_order_id (fastest, most reliable)
      if (activeOrderId) {
        try {
          const orderSnap = await fsGetDoc(fsDoc(db, 'orders', activeOrderId));
          if (orderSnap.exists()) {
            console.log(`🧾 [PRE-BILL] Found order via active_order_id: ${activeOrderId}`);
            processOrder(orderSnap.id, orderSnap.data());
          }
        } catch (e) { console.warn('🧾 [PRE-BILL] active_order_id fetch failed:', e); }
      }

      // PRIORITY 2: Check tab_orders array
      if (allItems.length === 0 && tabOrders.length > 0) {
        for (const oid of tabOrders) {
          try {
            const snap = await fsGetDoc(fsDoc(db, 'orders', oid));
            if (snap.exists()) processOrder(snap.id, snap.data());
          } catch { /* skip */ }
        }
      }

      // PRIORITY 3: Query by table_number (fallback, tries all type/format combos)
      // Server-side status filter ensures even if processOrder's filter is
      // bypassed, stale orders cannot reach the Zwischenrechnung.
      if (allItems.length === 0) {
        for (const tryNum of [tableNum, String(tableNum), Number(tableNum)]) {
          for (const orderTypeFilter of [
            [where('order_type', '==', 'dine_in')],
            [where('order_type', '==', 'Dine-In')],
            [],
          ]) {
            try {
              const snap = await getDocs(query(
                ordersRef,
                where('table_number', '==', tryNum),
                ...orderTypeFilter,
                where('status', 'in', PRE_BILL_ACTIVE_STATUSES),
                fsLimit(20),
              ));
              snap.forEach(d => processOrder(d.id, d.data()));
            } catch { /* try next */ }
          }
        }
      }

      console.log(`🧾 [PRE-BILL] Table ${tableNum}: found ${completedIds.size} orders, ${allItems.length} items, total €${totalAmount.toFixed(2)}`);

      if (allItems.length === 0) {
        setSnackbar({ open: true, message: t('restaurant.prebill_no_items'), severity: 'warning' });
        return;
      }

      // Calculate tax (19% included in total for dine-in)
      const taxRate = 19 / 119;
      const tax = Math.round(totalAmount * taxRate * 100) / 100;
      const subtotal = Math.round((totalAmount - tax) * 100) / 100;

      // Build pre-bill receipt data — marked as OPEN/ZWISCHENRECHNUNG
      const preBillData = {
        orderNumber: orderNumber || `T${tableNum}`,
        customerName: (table as any).customer_name || `Table ${tableNum}`,
        items: allItems,
        subtotal,
        tax,
        total: totalAmount,
        order_type: 'dine_in',
        payment_method: '',
        special_instructions: `*** ${t('restaurant.print_check').toUpperCase()} — OPEN ***\nTable ${tableNum} | ${allItems.length} items | NOT a final receipt`,
        is_prebill: true,
      };

      const preBillId = `PREBILL-T${tableNum}-${Date.now()}`;
      const preBillNumber = `ZWR-T${tableNum}`;

      await firestorePrintService.printCashierReceipt(preBillId, preBillNumber, preBillData);

      console.log(`✅ [PRE-BILL] Zwischenrechnung printed for Table ${tableNum}: €${totalAmount.toFixed(2)}, ${allItems.length} items`);
      setSnackbar({ open: true, message: `${t('restaurant.prebill_printed')}: €${totalAmount.toFixed(2)} (${allItems.length} items)`, severity: 'success' });

    } catch (error) {
      console.error('❌ [PRE-BILL] Failed to print pre-bill:', error);
      setSnackbar({ open: true, message: t('restaurant.prebill_error'), severity: 'error' });
    }
  };

  // Open Close Tab dialog instead of directly freeing
  const handleFreeTable = (table: RestaurantTable) => {
    const permission = canModifyTable(table);
    if (!permission.allowed) {
      setCenteredAlert({ open: true, title: 'Access Restricted', icon: '🔒', message: permission.reason || 'Permission denied' });
      return;
    }
    setCloseTabDialog({ open: true, table });
  };

  // Called when Close Tab dialog confirms payment
  const handleCloseTabConfirm = async (paymentMethod: string, orders: any[], totalAmount: number) => {
    const table = closeTabDialog.table;
    if (!table) return;

    setIsTableOperationInProgress(true);
    setCloseTabDialog({ open: false, table: null });

    try {
      const rawCustomerName = table.customer_name || orders[0]?.customer_name || '';
      const isWalkIn = !rawCustomerName || rawCustomerName === 'Laufkunde' || rawCustomerName === 'Walk-in Customer';
      const customerName = isWalkIn
        ? (table.number ? `Dine-In Tisch ${table.number}` : 'Dine-In')
        : rawCustomerName;
      const latestOrderNumber = table.active_order_number || orders[0]?.order_number || '';
      const latestOrderId = orders[0]?.id || '';

      const isSplitRelease = paymentMethod === 'split';

      // Guard: if there are no items and total is zero, skip payment record + printing entirely
      // This handles the "Free Table" case where a table was opened but nothing was ordered
      const allItemsCheck = orders.flatMap((o: any) => o.items || []);
      const isEmptyTable = allItemsCheck.length === 0 && totalAmount <= 0;

      // 1. Create payment record (skip for split — payment already created per person during split)
      if (!isSplitRelease && !isEmptyTable) {
        try {
          const firebasePaymentService = (await import('../../services/firebasePaymentService')).default;
          const paymentService = firebasePaymentService.getInstance();
          const { getCurrentUser } = await import('../../services/currentUserHelper');
          const user = getCurrentUser();
          await paymentService.createPayment({
            order_id: latestOrderId,
            order_number: latestOrderNumber,
            customer_name: customerName,
            payment_method: paymentMethod,
            amount: totalAmount,
            status: 'completed',
            table_number: table.number,
            processed_by: user.email || 'POS System',
          });
          console.log(`✅ Payment record created: €${totalAmount.toFixed(2)} via ${paymentMethod}`);
        } catch (payErr) {
          console.error('❌ Failed to create payment:', payErr);
        }
      } else if (isSplitRelease) {
        console.log('ℹ️ Split-paid table — skipping duplicate payment record');
      } else {
        console.log('ℹ️ Empty table (no items, zero total) — skipping payment record');
      }

      // 2. Print final receipt (skip for split — receipt already printed per person during split)
      // Also skip if table is empty — no items means nothing to print
      if (!isSplitRelease && totalAmount > 0) {
        try {
          const { firestorePrintService } = await import('../../services/firestorePrintService');
          // Collect items from orders — handle both POS and QR order structures
          let allItems = orders.flatMap((o: any) => o.items || []);

          // Fallback: if items array is empty but we have a total, create a summary item
          // This covers QR orders where items might be nested differently
          if (allItems.length === 0 && totalAmount > 0) {
            console.log('⚠️ [CLOSE TAB] No items found in orders, using summary for receipt');
            allItems = [{ name: `Tisch ${table.number} Bestellung`, quantity: 1, price: totalAmount, total: totalAmount }];
          }

          // Aggregate discount across all orders on this table
          const totalDiscount = orders.reduce(
            (sum: number, o: any) => sum + (parseFloat(o.discount_amount) || 0),
            0,
          );
          const totalBeforeDiscount = orders.reduce(
            (sum: number, o: any) => sum + (parseFloat(o.total_before_discount) || parseFloat(o.total) || 0),
            0,
          );
          const firstDiscountReason = orders.find((o: any) => o.discount_reason)?.discount_reason || '';
          const firstDiscountType = orders.find((o: any) => o.discount_type)?.discount_type || '';
          const firstDiscountValue = orders.find((o: any) => o.discount_value)?.discount_value || 0;

          const receiptData: any = {
            orderNumber: latestOrderNumber,
            customerName: customerName,
            customerAddress: '',
            customerPhone: '',
            orderDate: new Date().toLocaleDateString('en-US'),
            orderTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            items: allItems.map((item: any) => ({
              name: item.name || item.menu_item_name || 'Item',
              quantity: item.quantity || 1,
              price: parseFloat(item.unit_price || item.price || 0),
              total: parseFloat(item.total || item.price || item.unit_price || 0),
              addons: item.addons || [],
            })),
            subtotal: totalAmount / 1.19,
            tax: totalAmount - (totalAmount / 1.19),
            total: totalAmount,
            paymentMethod: paymentMethod,
            tableNumber: table.number,
            order_type: 'dine_in' as const,
            paymentStatus: 'completed',
            isClosingTab: true,
            // Render "Rabatt" line on BELEG when a discount was applied to any order on this tab.
            ...(totalDiscount > 0 ? {
              discount_amount: totalDiscount,
              discount_reason: firstDiscountReason,
              discount_type: firstDiscountType,
              discount_value: firstDiscountValue,
              total_before_discount: totalBeforeDiscount > 0 ? totalBeforeDiscount : totalAmount + totalDiscount,
            } : {}),
          };
          console.log(`🧾 [CLOSE TAB] Printing receipt: ${allItems.length} items, €${totalAmount.toFixed(2)}, order #${latestOrderNumber}`);
          await firestorePrintService.printCashierReceipt(latestOrderId, latestOrderNumber, receiptData);
          console.log('✅ Final receipt sent to printer');
        } catch (printErr) {
          console.error('❌ Failed to print receipt:', printErr);
        }
      } else if (isSplitRelease) {
        console.log('ℹ️ Split-paid table — skipping duplicate receipt print');
      } else {
        console.log('ℹ️ Empty table (no items, zero total) — skipping receipt print');
      }

      // 3. Mark all active orders as completed
      try {
        const { collection: col, query: q, where: w, getDocs: gd, doc: firestoreDoc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        const ordersRef = col(db, 'orders');
        const tableNum = table.number;

        // Find ALL active orders for this table — try both string and number, with and without order_type filter
        const completedIds = new Set<string>();
        for (const tryNum of [tableNum, String(tableNum), Number(tableNum)]) {
          for (const extraFilter of [
            [w('order_type', '==', 'dine_in')],
            [w('order_type', '==', 'Dine-In')],
            [], // no order_type filter — catch anything linked to this table
          ]) {
            try {
              const snap = await gd(q(ordersRef, w('table_number', '==', tryNum), ...extraFilter));
              for (const docSnap of snap.docs) {
                if (completedIds.has(docSnap.id)) continue;
                const data = docSnap.data();
                const status = (data.status || '').toLowerCase();
                if (status !== 'completed' && status !== 'cancelled') {
                  await updateDoc(firestoreDoc(db, 'orders', docSnap.id), {
                    status: 'completed',
                    completed_at: serverTimestamp(),
                    updated_at: serverTimestamp(),
                  });
                  completedIds.add(docSnap.id);
                  console.log(`✅ Order ${data.order_number || docSnap.id} marked as completed`);
                }
              }
            } catch { /* ignore individual query errors */ }
          }
        }
      } catch (orderError) {
        console.error('❌ Error marking orders as completed:', orderError);
      }

      // Update table status to available in Firebase — use deleteField() to fully remove all fields
      try {
        const { doc, updateDoc, query, where, getDocs, deleteField, collection: firestoreCollection } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        const tablesRef = firestoreCollection(db, 'restaurant_tables');
        const q = query(tablesRef, where('number', '==', table.number), where('area', '==', table.area));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const tableDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'restaurant_tables', tableDoc.id), {
            status: 'available',
            customer_name: deleteField(),
            customer_phone: deleteField(),
            customer_address: deleteField(),
            total_amount: deleteField(),
            item_count: deleteField(),
            start_time: deleteField(),
            end_time: deleteField(),
            order_id: deleteField(),
            active_order_id: deleteField(),
            active_order_number: deleteField(),
            occupied_by: deleteField(),
            tab_open: false,
            tab_orders: deleteField(),
            tab_total: deleteField(),
            tab_item_count: deleteField(),
            tab_rounds: deleteField(),
            // Clear all split payment fields
            split_payment_complete: deleteField(),
            split_people: deleteField(),
            split_total: deleteField(),
            split_session_id: deleteField(),
            updated_at: new Date().toISOString()
          });
          console.log(`✅ Table ${table.number} freed in Firestore (all fields deleted)`);

          // Cancel any pending split drafts for this table
          try {
            const SplitTableDraftService = (await import('../../services/splitTableDraftService')).default;
            const draftSvc = SplitTableDraftService.getInstance();
            await draftSvc.cancelAllDraftsForTable(String(table.number));
            console.log(`✅ Cleared split drafts for table ${table.number}`);
          } catch (draftErr) {
            console.warn('Could not clear split drafts:', draftErr);
          }

          // Also clear dashboard table context if it references this table
          try {
            const { getDoc } = await import('../../services/firestoreCompat');
            const ctxRef = doc(db, 'table_context', 'current');
            const ctxSnap = await getDoc(ctxRef);
            if (ctxSnap.exists()) {
              const ctxData = ctxSnap.data();
              const ctxTableNum = ctxData.selectedTable?.number || ctxData.selectedTable?.display_number;
              if (Number(ctxTableNum) === Number(table.number)) {
                const { deleteDoc: delDoc } = await import('../../services/firestoreCompat');
                await delDoc(ctxRef);
                console.log(`✅ Cleared dashboard table context for table ${table.number}`);
              }
            }
          } catch (ctxErr) {
            console.warn('Could not clear table context:', ctxErr);
          }
        } else {
          console.error(`❌ Could not find table ${table.number} (${table.area}) in Firestore`);
        }
      } catch (error) {
        console.error(`❌ Error freeing table ${table.number} in Firebase:`, error);
      }

      // Update table status to available locally and recalculate counts from actual data
      setTables(prevTables => {
        const updatedTables = prevTables.map(t => 
          t.number === table.number 
            ? { ...t, status: 'available' as const, customer_name: undefined, total_amount: undefined, item_count: 0, start_time: undefined }
            : t
        );
        // Recalculate counts from updated tables to ensure accuracy
        updateTableCounts(updatedTables);
        return updatedTables;
      });

      console.log('✅ Table freed successfully');
        
        setSnackbar({
          open: true,
          message: `Table ${table.number} has been freed and is now available for new guests.`,
          severity: 'success'
        });
        
        // CRITICAL: Wait before allowing fetchTableCustomerInfo to run
        // This ensures the table status is fully updated in Firebase
        setTimeout(() => {
          setIsTableOperationInProgress(false);
          console.log('✅ [FREE] Table operation complete - fetchTableCustomerInfo can now run safely');
        }, 2000);
    } catch (error) {
      console.error('Error freeing table:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection and try again.',
        severity: 'error'
      });
      // Reset flag on error
      setTimeout(() => {
        setIsTableOperationInProgress(false);
      }, 1000);
    }
  };


  return (
    <Box sx={{
      // Dashboard uses `width: 100%` inside SharedLayout (no extra flex container).
      // Restaurant historically used `display: flex` + a fallback sidebar. When
      // mounted inside SharedLayout (the normal case), that flex wrapper fought
      // with SharedLayout's margin-left and left a dead gap on the left column
      // after resize / dialog teardown. Only switch to flex when we're rendering
      // our own fallback sidebar (standalone mode).
      display: hasSharedSidebar ? 'block' : 'flex',
      width: '100%',
      minHeight: '100vh',
      backgroundColor: appTheme.theme.background_color
    }}>
      {/* Sidebar - Only show if NOT in SharedLayout */}
      {!hasSharedSidebar && (
      <Box sx={{
        width: 280,
        backgroundColor: appTheme.theme.background_color || '#1a1d2e',
        borderRight: '1px solid #3a3d52',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        height: '100vh'
      }}>
        {/* Logo Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #3a3d52' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 180,
              height: 180,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <img
                src="/saheeh_logo_w.svg"
                alt="Saheeh"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#e0e0e0' }}>
                POS
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                Berlin Mitte
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, p: 2, paddingBottom: '80px' }}>
          {[
            { label: t('nav.dashboard'), icon: <Restaurant />, path: '/dashboard', active: false },
            { label: t('nav.restaurant'), icon: <Restaurant />, path: '/restaurant', active: true },
            { label: t('nav.orders'), icon: <ShoppingCart />, path: '/orders', active: false },
            { label: t('nav.payments'), icon: <Euro />, path: '/payments', active: false },
            { label: t('nav.reports'), icon: <TrendingUp />, path: '/reports', active: false },
          ].map((item) => (
            <Button
              key={item.label}
              fullWidth
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                mb: 1,
                borderRadius: '8px',
                backgroundColor: item.active ? (appTheme.theme.navigation_sidebar_active_color || '#FF8E53') : (appTheme.theme.navigation_sidebar_inactive_color || '#2d3048'),
                color: item.active ? '#ffffff' : (appTheme.theme.navigation_sidebar_text_color || '#9ca3af'),
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: item.active ? 600 : 500,
                minWidth: '100%',
                border: item.active ? 'none' : '1px solid #3a3d52',
                boxShadow: item.active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: item.active ? (appTheme.theme.navigation_sidebar_active_color || '#FF8E53') : (appTheme.theme.navigation_sidebar_hover_color || '#3a3d52'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Logout */}
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid #3a3d52',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#252838',
        }}>
          <Button
            fullWidth
            startIcon={<ExitToApp />}
            onClick={() => {
              localStorage.removeItem('saheeh_pos_session');
              navigate('/login');
            }}
            sx={{
              justifyContent: 'flex-start',
              p: 2,
              borderRadius: '8px',
              backgroundColor: '#2d3048',
              color: '#9ca3af',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              border: '1px solid #3a3d52',
              '&:hover': {
                backgroundColor: '#2d3048',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              },
            }}
          >
            {t('nav.logout')}
          </Button>
        </Box>
      </Box>
      )}

      {/* Main Content */}
      <Box sx={{
        // `flex: 1` only applies when parent is `display: flex` (fallback sidebar
        // mode). Inside SharedLayout we use block layout, so fall back to 100%.
        flex: hasSharedSidebar ? undefined : 1,
        width: hasSharedSidebar ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'auto', sm: 'hidden' },
        height: { xs: 'auto', sm: '100vh' },
        minHeight: { xs: 'calc(100vh - 56px)', sm: '100vh' },
        WebkitOverflowScrolling: 'touch',
        backgroundColor: '#1e2139'
      }}>
        {/* Compact Header Bar — sticky on mobile */}
        <Box sx={{
          px: { xs: 1.5, md: 3 },
          py: { xs: 1, md: 2 },
          backgroundColor: '#252838',
          borderBottom: '1px solid #3a3d52',
          position: { xs: 'sticky', sm: 'relative' },
          top: { xs: 0 },
          zIndex: 10,
        }}>
          {/* Title + inline status counts */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.75, md: 1 } }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
              {t('restaurant.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, backgroundColor: '#38a169', borderRadius: '50%' }} />
                <Typography sx={{ fontWeight: 600, color: '#68d391', fontSize: { xs: '0.72rem', md: '0.85rem' } }}>
                  {tableStatusCounts.available}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, backgroundColor: '#ed8936', borderRadius: '50%' }} />
                <Typography sx={{ fontWeight: 600, color: '#ed8936', fontSize: { xs: '0.72rem', md: '0.85rem' } }}>
                  {tableStatusCounts.occupied}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, backgroundColor: '#3182ce', borderRadius: '50%' }} />
                <Typography sx={{ fontWeight: 600, color: '#63b3ed', fontSize: { xs: '0.72rem', md: '0.85rem' } }}>
                  {tableStatusCounts.reserved}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Subtitle - desktop only */}
          <Typography variant="body2" sx={{ color: '#9ca3af', display: { xs: 'none', md: 'block' }, mb: 1 }}>
            {t('restaurant.subtitle')}
          </Typography>

          {/* Toolbar: Area Toggle + Actions - single row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, overflowX: 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { height: 0 } }}>
          <ToggleButtonGroup
            value={seatingArea}
            exclusive
            onChange={handleSeatingAreaChange}
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                px: { xs: 1.5, md: 2.5 }, py: { xs: 0.5, md: 0.75 },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: '1px solid #3a3d52',
                color: '#9ca3af',
                '&.Mui-selected': {
                  backgroundColor: appTheme.theme.primary_color || '#1976d2',
                  color: '#ffffff',
                  borderColor: appTheme.theme.primary_color || '#1976d2',
                  '&:hover': {
                    backgroundColor: appTheme.theme.primary_color || '#1565c0',
                  }
                },
                '&:hover': {
                  backgroundColor: '#2d3048',
                }
              }
            }}
          >
            <ToggleButton value="indoor">
              <LocalDining sx={{ mr: 0.75, fontSize: 18 }} />
              {t('restaurant.indoor')} ({areaStatusCounts.indoor.available + areaStatusCounts.indoor.occupied + areaStatusCounts.indoor.reserved})
            </ToggleButton>
            <ToggleButton value="outdoor">
              <OutdoorGrill sx={{ mr: 0.75, fontSize: 18 }} />
              {t('restaurant.outdoor')} ({areaStatusCounts.outdoor.available + areaStatusCounts.outdoor.occupied + areaStatusCounts.outdoor.reserved})
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EventNote sx={{ fontSize: 16 }} />}
              onClick={() => setReservationsListDialog(true)}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                borderColor: '#3a3d52', color: '#9ca3af', borderRadius: '8px',
                '&:hover': { borderColor: '#4a4d62', backgroundColor: '#2d3048' }
              }}
            >
              {t('restaurant.reservations_label')} ({reservations.length})
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleReserveTable(null)}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                backgroundColor: '#558B2F', borderRadius: '8px',
                '&:hover': { backgroundColor: '#689F38' },
                minWidth: 'auto', px: 1.5,
              }}
            >
              + {t('restaurant.new_reservation') || 'New Reservation'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DraftIcon sx={{ fontSize: 16 }} />}
              onClick={() => setDraftSplitOrdersDialog(true)}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                borderColor: '#3a3d52', color: '#9ca3af', borderRadius: '8px',
                display: { xs: 'none', md: 'inline-flex' },
                '&:hover': { borderColor: '#4a4d62', backgroundColor: '#2d3048' }
              }}
            >
              {t('restaurant.drafts_label')}
            </Button>
            {isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteForever sx={{ fontSize: 16 }} />}
                onClick={handleClearAllTables}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                  borderColor: 'rgba(229, 62, 62, 0.3)', color: '#c53030', borderRadius: '8px',
                  display: { xs: 'none', md: 'inline-flex' },
                  '&:hover': { borderColor: 'rgba(229, 62, 62, 0.5)', backgroundColor: 'rgba(229, 62, 62, 0.08)' }
                }}
              >
                Clear All Tables
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 16 }} />}
              onClick={() => {
                setIsLoading(true);
                skipAutoInitializationRef.current = false;
                fetchTables();
                fetchReservations();
              }}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                borderColor: '#3a3d52', color: '#9ca3af', borderRadius: '8px', minWidth: 'auto',
                display: { xs: 'none', md: 'inline-flex' },
                '&:hover': { borderColor: '#4a4d62', backgroundColor: '#2d3048' }
              }}
            >
              {t('restaurant.refresh')}
            </Button>
          </Box>
          </Box>
        </Box>

        {/* Content — Table Grid */}
        <Box sx={{
          flex: 1,
          p: { xs: 1.5, md: 3 },
          pb: { xs: 4, md: 3 },
          overflow: 'auto',
          maxHeight: { xs: 'none', sm: 'calc(100vh - 140px)' },
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#3a3d52', borderRadius: '3px', '&:hover': { background: '#4a4d62' } },
        }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: '#a0aec0' }}>
                {t('restaurant.loading_tables')}
              </Typography>
            </Box>
          ) : (
          <Grid container spacing={{ xs: 1, lg: 2.5 }}>
            {filteredTables.map((table) => {
              const isOccupied = table.status === 'occupied';
              const isReserved = table.status === 'reserved';
              const isAvailable = table.status === 'available';
              const statusColor = isOccupied ? '#ed8936' : isReserved ? '#3182ce' : '#38a169';

              return (
                <Grid item xs={6} sm={4} md={3} lg={3} key={`${table.area}-${table.number}`}>
                  <Card sx={{
                    borderRadius: { xs: '8px', lg: '12px' },
                    border: 'none',
                    boxShadow: isOccupied
                      ? '0 0 0 2px #ed893640, 0 4px 12px rgba(0,0,0,0.08)'
                      : '0 1px 4px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)'
                    },
                  }}>
                    {/* Color status bar at top */}
                    <Box sx={{ height: { xs: 3, lg: 4 }, backgroundColor: statusColor }} />

                    <Box sx={{ p: { xs: 1, sm: 1.25, lg: 2.5 } }}>
                      {/* Table header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.5, lg: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, lg: 1 } }}>
                          <Box sx={{
                            width: { xs: 28, lg: 40 }, height: { xs: 28, lg: 40 }, borderRadius: { xs: '6px', lg: '10px' },
                            backgroundColor: `${statusColor}15`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <Typography sx={{ fontWeight: 800, color: statusColor, fontSize: { xs: '0.85rem', lg: '1.1rem' } }}>
                              {table.number}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#e0e0e0', lineHeight: 1.2, fontSize: { xs: '0.75rem', lg: '1rem' } }}>
                              {t('restaurant.table')} {table.number}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#a0aec0', fontSize: { xs: '0.6rem', lg: '0.75rem' } }}>
                              {table.capacity} {t('restaurant.seats')}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={t(`restaurant.${table.status}`)}
                            size="small"
                            sx={{
                              fontWeight: 600, fontSize: { xs: '0.58rem', lg: '0.7rem' }, height: { xs: 18, lg: 24 },
                              backgroundColor: `${statusColor}15`,
                              color: statusColor,
                              border: 'none'
                            }}
                          />
                          {isOccupied && tableCustomerInfo.get(table.number)?.has_qr_order && (
                            <Chip
                              label="QR"
                              size="small"
                              sx={{
                                fontWeight: 700, fontSize: { xs: '0.55rem', lg: '0.65rem' }, height: { xs: 18, lg: 22 },
                                backgroundColor: 'rgba(156, 39, 176, 0.12)',
                                color: '#9c27b0',
                                border: '1px solid rgba(156, 39, 176, 0.3)',
                                minWidth: 30,
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isAdmin && (
                            <Tooltip title="Admin: Clear table history" placement="top">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleAdminClearTable(table); }}
                                sx={{
                                  p: 0.3, color: '#e53e3e',
                                  '&:hover': { backgroundColor: 'rgba(229, 62, 62, 0.08)' },
                                }}
                              >
                                <DeleteForever sx={{ fontSize: { xs: 14, lg: 18 } }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Occupied table — customer info + tab details */}
                      {isOccupied && (() => {
                        const customerInfo = tableCustomerInfo.get(table.number);
                        const displayOrderNumber = table.active_order_number || customerInfo?.order_number || '';
                        return (
                          <>
                            {/* Customer section */}
                            <Box sx={{
                              p: { xs: 0.75, lg: 1.5 }, backgroundColor: 'rgba(156, 39, 176, 0.06)', borderRadius: { xs: '6px', lg: '8px' }, mb: { xs: 0.75, lg: 1.5 },
                              border: '1px solid rgba(156, 39, 176, 0.25)'
                            }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b46c1', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: { xs: '0.55rem', lg: '0.65rem' } }}>
                                {t('restaurant.customer_label')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d0d0', mt: 0.25, fontSize: { xs: '0.7rem', lg: '0.875rem' } }} noWrap>
                                {(customerInfo?.customer_first_name || customerInfo?.customer_last_name)
                                  ? `${customerInfo.customer_first_name || ''} ${customerInfo.customer_last_name || ''}`.trim()
                                  : (() => {
                                      const name = customerInfo?.customer_name || table.customer_name || '';
                                      const isDefault = !name || name === 'Laufkunde' || name === 'Walk-in Customer' || name === 'Bestellung' || name.startsWith('Dine-In');
                                      return isDefault ? `Dine-In Tisch ${table.number}` : name;
                                    })()}
                              </Typography>
                              {displayOrderNumber && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#6b46c1', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }}>
                                  #{displayOrderNumber}
                                </Typography>
                              )}
                              {customerInfo?.customer_phone && (
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                  {customerInfo.customer_phone}
                                </Typography>
                              )}
                            </Box>

                            {/* Split Paid banner */}
                            {(table as any).split_payment_complete && (
                              <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                px: 2, py: 1, mb: 1.5,
                                backgroundColor: 'rgba(156, 39, 176, 0.08)', borderRadius: '8px',
                                border: '1.5px solid #9f7aea',
                              }}>
                                <Chip
                                  label="✅ SPLIT PAID"
                                  size="small"
                                  sx={{
                                    fontWeight: 800, fontSize: '0.72rem', height: 24,
                                    backgroundColor: '#6b46c1', color: '#fff',
                                    letterSpacing: '0.05em',
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: '#b794f4', fontWeight: 600 }}>
                                  Click "Release Table" to free
                                </Typography>
                              </Box>
                            )}

                            {/* Tab summary — compact grid */}
                            <Box sx={{
                              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: { xs: 0.5, lg: 1 }, mb: { xs: 0.5, lg: 1.5 }
                            }}>
                              <Box sx={{ p: { xs: 0.5, lg: 1 }, backgroundColor: '#2d3048', borderRadius: { xs: '4px', lg: '8px' }, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#a0aec0', fontSize: { xs: '0.5rem', lg: '0.65rem' }, fontWeight: 600, textTransform: 'uppercase' }}>
                                  {t('restaurant.total')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 800, color: '#e0e0e0', fontSize: { xs: '0.8rem', lg: '1rem' } }}>
                                  €{(customerInfo?.total ? customerInfo.total : ((table as any).tab_total || (table as any).total_amount || 0)).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ p: { xs: 0.5, lg: 1 }, backgroundColor: '#2d3048', borderRadius: { xs: '4px', lg: '8px' }, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#a0aec0', fontSize: { xs: '0.5rem', lg: '0.65rem' }, fontWeight: 600, textTransform: 'uppercase' }}>
                                  {t('restaurant.items')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 800, color: '#e0e0e0', fontSize: { xs: '0.8rem', lg: '1rem' } }}>
                                  {customerInfo?.items_count != null ? customerInfo.items_count : ((table as any).tab_item_count ?? table.item_count ?? 0)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Time + Rounds row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.75, lg: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                                  {t('restaurant.time')}:
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                  {table.start_time || customerInfo?.created_at ? <CountdownTimer startTime={table.start_time || customerInfo?.created_at} /> : '00:00'}
                                </Typography>
                              </Box>
                              {((table as any).tab_rounds || 0) > 0 && (
                                <Chip
                                  label={`${(table as any).tab_rounds} ${t('restaurant.rounds')}`}
                                  size="small"
                                  sx={{
                                    fontWeight: 700, height: 22, fontSize: '0.7rem',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)', color: '#63b3ed', border: 'none'
                                  }}
                                />
                              )}
                            </Box>

                            {/* Action Buttons — stacked, compact on tablet */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.4, lg: 0.75 } }}>
                              <Button
                                fullWidth variant="contained"
                                onClick={() => handleTableClick(table)}
                                sx={{
                                  py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                  fontWeight: 700, fontSize: { xs: '0.62rem', lg: '0.85rem' },
                                  minHeight: { xs: 24, lg: 36 },
                                  backgroundColor: '#ed8936',
                                  boxShadow: '0 2px 4px rgba(237,137,54,0.3)',
                                  '&:hover': { backgroundColor: '#dd6b20' }
                                }}
                              >
                                {t('restaurant.add_order_reorder')}
                              </Button>
                              {/* Split Table — 2 buttons side by side */}
                              <Box sx={{ display: 'flex', gap: { xs: 0.4, lg: 0.75 } }}>
                                <Button
                                  fullWidth variant="contained"
                                  onClick={() => handleViewTableOrder(table)}
                                  sx={{
                                    py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                    fontWeight: 600, fontSize: { xs: '0.58rem', lg: '0.8rem' },
                                    minHeight: { xs: 22, lg: 36 },
                                    backgroundColor: '#4a5568',
                                    '&:hover': { backgroundColor: '#2d3748' }
                                  }}
                                >
                                  {t('restaurant.view_all_orders')}
                                </Button>
                                {(table as any).split_payment_complete ? (
                                  <Box sx={{
                                    py: 1, px: 1.5, borderRadius: '8px', textAlign: 'center',
                                    background: 'linear-gradient(135deg,#1b5e20,#2e7d32)',
                                    color: '#ffffff',
                                  }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2 }}>✅ {t('split.split_completed')}</Typography>
                                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>{t('split.split_completed_hint')}</Typography>
                                  </Box>
                                ) : (
                                  <Button
                                    fullWidth variant="contained"
                                    onClick={() => handleSplitTableClick(table)}
                                    sx={{
                                      py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                      fontWeight: 700, fontSize: { xs: '0.58rem', lg: '0.8rem' },
                                      minHeight: { xs: 22, lg: 36 },
                                      backgroundColor: '#2e7d32',
                                      '&:hover': { backgroundColor: '#1b5e20' }
                                    }}
                                  >
                                    {t('split.split_bill') || 'Split Table'}
                                  </Button>
                                )}
                              </Box>
                              {/* Pre-Bill (Zwischenrechnung) — only when NOT split-paid */}
                              {!(table as any).split_payment_complete && (
                                <Button
                                  fullWidth variant="outlined"
                                  onClick={() => handlePrintPreBill(table)}
                                  sx={{
                                    py: { xs: 0.3, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                    fontWeight: 600, fontSize: { xs: '0.58rem', lg: '0.8rem' },
                                    minHeight: { xs: 22, lg: 36 },
                                    borderColor: '#3182ce', color: '#63b3ed',
                                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)', borderColor: '#2b6cb0', color: '#63b3ed' }
                                  }}
                                >
                                  🧾 {t('restaurant.print_check')}
                                </Button>
                              )}
                              <Button
                                fullWidth variant={(table as any).split_payment_complete ? 'contained' : 'outlined'}
                                onClick={() => handleFreeTable(table)}
                                sx={{
                                  py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                  fontWeight: 700, fontSize: { xs: '0.62rem', lg: '0.85rem' },
                                  minHeight: { xs: 24, lg: 36 },
                                  ...((table as any).split_payment_complete
                                    ? {
                                        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                        color: '#fff',
                                        '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #145214 100%)' }
                                      }
                                    : {
                                        borderColor: '#38a169', color: '#68d391',
                                        '&:hover': { backgroundColor: '#38a169', color: '#fff', borderColor: '#38a169' }
                                      }
                                  )
                                }}
                              >
                                {(table as any).split_payment_complete
                                  ? '✅ Release Table'
                                  : t('restaurant.close_tab_charge')
                                }
                              </Button>
                            </Box>
                          </>
                        );
                      })()}

                      {/* Available table */}
                      {isAvailable && (
                        <>
                          <Box sx={{ mt: { xs: 0.5, lg: 1 }, display: 'flex', gap: { xs: 0.5, lg: 1 } }}>
                            <Button
                              fullWidth variant="contained"
                              onClick={() => handleTableClick(table)}
                              sx={{
                                py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                fontWeight: 600, fontSize: { xs: '0.62rem', lg: '0.85rem' },
                                minHeight: { xs: 24, lg: 36 },
                                backgroundColor: appTheme.theme.primary_color || '#1976d2',
                                '&:hover': { backgroundColor: appTheme.theme.primary_color || '#1565c0' }
                              }}
                            >
                              {t('restaurant.new_order')}
                            </Button>
                            <Button
                              fullWidth variant="outlined"
                              onClick={() => handleReserveTable(table)}
                              sx={{
                                py: { xs: 0.4, lg: 1 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                                fontWeight: 600, fontSize: { xs: '0.62rem', lg: '0.85rem' },
                                minHeight: { xs: 24, lg: 36 },
                                borderColor: '#3a3d52', color: '#9ca3af',
                                '&:hover': { borderColor: '#3182ce', color: '#63b3ed', backgroundColor: 'rgba(33, 150, 243, 0.08)' }
                              }}
                            >
                              {t('restaurant.reserve_table')}
                            </Button>
                          </Box>
                          {/* Reopen tab — undo a mistaken "Close Tab" within 15 min.
                              Cloud function enforces the time window server-side so we
                              always show the button on available tables; it errors
                              clearly if no eligible order exists. Requires manager PIN. */}
                          <Button
                            fullWidth variant="text"
                            onClick={() => handleReopenClick(table)}
                            sx={{
                              mt: { xs: 0.4, lg: 0.8 },
                              py: { xs: 0.3, lg: 0.6 }, borderRadius: { xs: '5px', lg: '8px' }, textTransform: 'none',
                              fontWeight: 500, fontSize: { xs: '0.58rem', lg: '0.75rem' },
                              minHeight: { xs: 20, lg: 28 },
                              color: '#f59e0b',
                              '&:hover': { backgroundColor: 'rgba(245,158,11,0.08)', color: '#fbbf24' }
                            }}
                          >
                            ⟲ {t('restaurant.reopen_last_tab') || 'Reopen last closed tab'}
                          </Button>
                        </>
                      )}

                      {/* Reserved table */}
                      {isReserved && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{
                            p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)', borderRadius: '8px', mb: 1.5,
                            border: '1px solid #bee3f8'
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#63b3ed', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                              {t('restaurant.reserved_table')}
                            </Typography>
                            {(() => {
                              const reservation = reservations.find(r => r.table_number === table.number);
                              return reservation && reservation.customer_name ? (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d0d0', mt: 0.25 }}>
                                  {reservation.customer_name}
                                </Typography>
                              ) : null;
                            })()}
                          </Box>
                          <Button
                            fullWidth variant="contained"
                            onClick={() => handleTableClick(table)}
                            sx={{
                              py: 1, borderRadius: '8px', textTransform: 'none',
                              fontWeight: 600, fontSize: '0.85rem',
                              backgroundColor: '#3182ce',
                              '&:hover': { backgroundColor: '#2b6cb0' }
                            }}
                          >
                            {t('restaurant.view_reservation')}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          )}
        </Box>
      </Box>

      {/* Reservation Dialog - Same Style as Customer Information Modal */}
      {reservationDialogOpen && (
        <>
          <style>{`
            select.reservation-time-select {
              background-color: white !important;
              color: black !important;
              border: 1.5px solid black !important;
              border-radius: 4px !important;
              height: 56px !important;
              padding: 0 40px 0 16px !important;
              font-weight: 500 !important;
              font-size: 16px !important;
              appearance: none !important;
              -webkit-appearance: none !important;
              -moz-appearance: none !important;
              cursor: pointer !important;
            }
            select.reservation-time-select:focus {
              outline: none !important;
              border-color: black !important;
              background-color: white !important;
            }
            select.reservation-time-select option {
              background-color: white !important;
              color: black !important;
              padding: 8px !important;
            }
            select.reservation-time-select option:checked {
              background-color: #e3f2fd !important;
              color: black !important;
            }
          `}</style>
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setReservationDialogOpen(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedTableForReservation
                  ? `${t('restaurant.reserve_table')} - ${t('restaurant.table')} ${selectedTableForReservation.number}`
                  : t('restaurant.new_reservation') || 'New Reservation'}
              </Typography>
              {!selectedTableForReservation && (
                <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
                  {t('restaurant.waitlist_hint') || 'No table selected — you can assign a table later when one becomes available.'}
                </Typography>
              )}
            </Box>
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Table Selection (optional) */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.table')} ({t('common.optional') || 'Optional'})
                  </Typography>
                  {/* Legend */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#38a169' }} />
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Available</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ed8936' }} />
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Occupied</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3182ce' }} />
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Reserved</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {/* No Table option */}
                    <Box
                      onClick={() => setSelectedTableForReservation(null)}
                      sx={{
                        width: 56, height: 56,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        border: !selectedTableForReservation ? '2px solid #558B2F' : '1.5px solid #3a3d52',
                        borderRadius: 2,
                        cursor: 'pointer',
                        backgroundColor: !selectedTableForReservation ? 'rgba(76, 175, 80, 0.08)' : '#252838',
                        '&:hover': { borderColor: '#558B2F', backgroundColor: 'rgba(76, 175, 80, 0.08)' },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9ca3af' }}>None</Typography>
                    </Box>
                    {/* ALL tables with status dots */}
                    {[...tables]
                      .sort((a, b) => a.number - b.number)
                      .map(table => {
                        const isOccupied = table.status === 'occupied';
                        const isReserved = table.status === 'reserved';
                        const isSelected = selectedTableForReservation?.number === table.number;
                        const statusColor = isOccupied ? '#ed8936' : isReserved ? '#3182ce' : '#38a169';
                        return (
                      <Box
                        key={table.number}
                            onClick={() => {
                              if (isOccupied || isReserved) {
                                setReservationTableWarning({ open: true, table, type: isOccupied ? 'occupied' : 'reserved' });
                              } else {
                                setSelectedTableForReservation(table);
                              }
                            }}
                        sx={{
                          width: 56, height: 56,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              border: isSelected ? '2px solid #558B2F' : '1.5px solid #3a3d52',
                          borderRadius: 2,
                          cursor: 'pointer',
                              backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.08)' : '#252838',
                              '&:hover': { borderColor: isOccupied ? '#ed8936' : isReserved ? '#3182ce' : '#558B2F', backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.08)' : '#1e2139' },
                          transition: 'all 0.15s',
                          position: 'relative',
                        }}
                      >
                            {/* Status dot */}
                            <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor }} />
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{table.number}</Typography>
                            <Typography sx={{ fontSize: '0.55rem', color: '#999' }}>{table.area}</Typography>
                      </Box>
                        );
                    })}
                  </Box>
                </Box>
                {/* Customer Name */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.customer_name')} *
                  </Typography>
                  <TextField
                    value={reservationData.customer_name}
                    onChange={(e) => setReservationData({...reservationData, customer_name: e.target.value})}
                    fullWidth
                    required
                    placeholder={t('restaurant.customer_name_placeholder')}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838'
                      }
                    }}
                  />
                </Box>

                {/* Phone Number */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.customer_phone')} (Optional)
                  </Typography>
                  <TextField
                    value={reservationData.customer_phone}
                    onChange={(e) => setReservationData({...reservationData, customer_phone: e.target.value})}
                    fullWidth
                    placeholder={t('restaurant.phone_placeholder')}
                    type="tel"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838'
                      }
                    }}
                  />
                </Box>

                {/* Email */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.customer_email')} (Optional)
                  </Typography>
                  <TextField
                    type="email"
                    value={reservationData.customer_email}
                    onChange={(e) => setReservationData({...reservationData, customer_email: e.target.value})}
                    fullWidth
                    placeholder={t('restaurant.email_placeholder')}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838'
                      }
                    }}
                  />
                </Box>

                {/* Reservation Time */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('restaurant.reservation_time')} *
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                      type="date"
                      value={reservationData.reservation_time ? reservationData.reservation_time.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = reservationData.reservation_time ? reservationData.reservation_time.split('T')[1] : '00:00';
                        setReservationData({...reservationData, reservation_time: `${date}T${time}`});
                      }}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                        flex: '1 1 50%',
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#252838',
                          '& fieldset': {
                            borderColor: '#000000',
                            borderWidth: '1.5px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#000000',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#000000',
                            borderWidth: '2px',
                          },
                        },
                    }}
                  />
                    <Box sx={{ 
                      flex: '1 1 50%',
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      <Box
                        component="select"
                        className="reservation-time-select"
                        required
                        value={reservationData.reservation_time ? reservationData.reservation_time.split('T')[1]?.split(':')[0] || '00' : '00'}
                        onChange={(e) => {
                          const hour = e.target.value;
                          const date = reservationData.reservation_time ? reservationData.reservation_time.split('T')[0] : new Date().toISOString().split('T')[0];
                          const minute = reservationData.reservation_time ? reservationData.reservation_time.split('T')[1]?.split(':')[1] || '00' : '00';
                          setReservationData({...reservationData, reservation_time: `${date}T${hour}:${minute}`});
                        }}
                        sx={{
                          flex: 1,
                          textAlign: 'left',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '12px',
                        }}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>
                            {i.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </Box>
                      <Typography 
                        sx={{ 
                          fontSize: '1.25rem', 
                          fontWeight: 500, 
                          color: '#e0e0e0',
                          lineHeight: 1,
                          mx: 0.5,
                          flexShrink: 0
                        }}
                      >
                        :
                      </Typography>
                      <Box
                        component="select"
                        className="reservation-time-select"
                        required
                        value={reservationData.reservation_time ? reservationData.reservation_time.split('T')[1]?.split(':')[1] || '00' : '00'}
                        onChange={(e) => {
                          const minute = e.target.value;
                          const date = reservationData.reservation_time ? reservationData.reservation_time.split('T')[0] : new Date().toISOString().split('T')[0];
                          const hour = reservationData.reservation_time ? reservationData.reservation_time.split('T')[1]?.split(':')[0] || '00' : '00';
                          setReservationData({...reservationData, reservation_time: `${date}T${hour}:${minute}`});
                        }}
                        sx={{
                          flex: 1,
                          textAlign: 'left',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '12px',
                        }}
                      >
                        {Array.from({ length: 60 }, (_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>
                            {i.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block', fontSize: '0.75rem' }}>
                    24-hour format (00:00 - 23:59)
                  </Typography>
                </Box>

                {/* Party Size */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.party_size')} (Optional)
                  </Typography>
                  <TextField
                    type="number"
                    value={reservationData.party_size}
                    onChange={(e) => setReservationData({...reservationData, party_size: parseInt(e.target.value) || 2})}
                    fullWidth
                    placeholder={t('restaurant.party_size_placeholder')}
                    inputProps={{ min: 1, max: 20 }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838'
                      }
                    }}
                  />
                </Box>

                {/* Special Requests */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('restaurant.special_requests')} (Optional)
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    value={reservationData.special_requests}
                    onChange={(e) => setReservationData({...reservationData, special_requests: e.target.value})}
                    fullWidth
                    placeholder={t('restaurant.special_requests_placeholder')}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'flex-end', 
              p: 2, 
              bgcolor: '#2d3048', 
              borderBottomLeftRadius: 12, 
              borderBottomRightRadius: 12 
            }}>
              <Button 
                onClick={() => setReservationDialogOpen(false)}
                variant="text"
                sx={{ color: '#9ca3af' }}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleCreateReservation}
                variant="contained"
                disabled={!reservationData.customer_name || !reservationData.reservation_time}
                sx={{ 
                  backgroundColor: '#28a745',
                  '&:hover': { backgroundColor: '#218838' },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#9ca3af'
                  }
                }}
              >
                {t('restaurant.create_reservation')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Table Occupied/Reserved Warning Popup */}
        {reservationTableWarning.open && reservationTableWarning.table && (
          <Box
            onClick={() => setReservationTableWarning({ open: false, table: null, type: 'occupied' })}
            sx={{
              position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1500, backgroundColor: 'rgba(0,0,0,0.4)',
            }}
          >
            <Box onClick={(e) => e.stopPropagation()} sx={{
              backgroundColor: '#252838', borderRadius: 3, maxWidth: 400, width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden',
            }}>
              <Box sx={{
                p: 2,
                background: reservationTableWarning.type === 'occupied'
                  ? 'linear-gradient(135deg, #ed8936 0%, #f6ad55 100%)'
                  : 'linear-gradient(135deg, #3182ce 0%, #63b3ed 100%)',
                color: '#ffffff',
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('restaurant.table')} {reservationTableWarning.table.number} — {reservationTableWarning.type === 'occupied'
                    ? (t('restaurant.occupied') || 'Occupied')
                    : (t('restaurant.reserved') || 'Reserved')}
                </Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Typography variant="body2" sx={{ color: '#555', mb: 0.5 }}>
                  {reservationTableWarning.table.customer_name && (
                    <strong>{reservationTableWarning.table.customer_name}<br/></strong>
                  )}
                  {reservationTableWarning.type === 'occupied'
                    ? (t('checkout.table_occupied_message') || 'This table has an active order. Please choose a different table.')
                    : (t('checkout.table_reserved_message') || 'This table is already reserved. Please choose a different table.')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid #3a3d52' }}>
                <Button
                  variant="contained"
                  onClick={() => setReservationTableWarning({ open: false, table: null, type: 'occupied' })}
                  sx={{
                    backgroundColor: reservationTableWarning.type === 'occupied' ? '#ed8936' : '#3182ce',
                    '&:hover': { backgroundColor: reservationTableWarning.type === 'occupied' ? '#dd6b20' : '#2b6cb0' },
                    textTransform: 'none', fontWeight: 600,
                  }}
                >
                  {t('checkout.choose_another_table') || 'Choose Another Table'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        </>
      )}

      {/* Reservation Details Dialog - Same Style as Customer Information Modal */}
      {reservationDetailsDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setReservationDetailsDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('restaurant.reservation_details')} - {t('restaurant.table')} {selectedReservation?.table_number}
              </Typography>
            </Box>
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedReservation && (
                <Box>
                  {/* Customer Name */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Customer Name
                </Typography>
                    <Box sx={{ 
                      p: 1.5, 
                      border: '1px solid #3a3d52', 
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedReservation.customer_name || 'Not provided'}
                </Typography>
                    </Box>
                  </Box>

                  {/* Party Size */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('restaurant.party_size')}
                </Typography>
                    <Box sx={{ 
                      p: 1.5, 
                      border: '1px solid #3a3d52', 
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedReservation.party_size || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Reservation Time */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('restaurant.reservation_time')}
                    </Typography>
                    <Box sx={{ 
                      p: 1.5, 
                      border: '1px solid #3a3d52', 
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedReservation.reservation_time ? (() => {
                          let reservationTime: Date;
                          if (typeof selectedReservation.reservation_time === 'string') {
                            reservationTime = new Date(selectedReservation.reservation_time);
                          } else if (selectedReservation.reservation_time && typeof selectedReservation.reservation_time === 'object') {
                            const timeObj = selectedReservation.reservation_time as any;
                            if ('toDate' in timeObj && typeof timeObj.toDate === 'function') {
                              reservationTime = timeObj.toDate();
                            } else if ('seconds' in timeObj && typeof timeObj.seconds === 'number') {
                              reservationTime = new Date(timeObj.seconds * 1000);
                            } else {
                              reservationTime = new Date(selectedReservation.reservation_time);
                            }
                          } else {
                            reservationTime = new Date(selectedReservation.reservation_time);
                          }
                          const year = reservationTime.getFullYear();
                          const month = String(reservationTime.getMonth() + 1).padStart(2, '0');
                          const day = String(reservationTime.getDate()).padStart(2, '0');
                          const hours = String(reservationTime.getHours()).padStart(2, '0');
                          const minutes = String(reservationTime.getMinutes()).padStart(2, '0');
                          const seconds = String(reservationTime.getSeconds()).padStart(2, '0');
                          return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
                        })() : 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Phone Number */}
                {selectedReservation.customer_phone && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('restaurant.customer_phone')}
                  </Typography>
                      <Box sx={{ 
                        p: 1.5, 
                        border: '1px solid #3a3d52', 
                        borderRadius: 1,
                        backgroundColor: '#252838',
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedReservation.customer_phone}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Email */}
                {selectedReservation.customer_email && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('restaurant.customer_email')}
                  </Typography>
                      <Box sx={{ 
                        p: 1.5, 
                        border: '1px solid #3a3d52', 
                        borderRadius: 1,
                        backgroundColor: '#252838',
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedReservation.customer_email}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Special Requests */}
                {selectedReservation.special_requests && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('restaurant.special_requests')}
                  </Typography>
                      <Box sx={{ 
                        p: 1.5, 
                        border: '1px solid #3a3d52', 
                        borderRadius: 1,
                        backgroundColor: '#252838',
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedReservation.special_requests}
                        </Typography>
              </Box>
            </Box>
          )}
                </Box>
              )}
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'flex-end', 
              p: 2, 
              bgcolor: '#2d3048', 
              borderBottomLeftRadius: 12, 
              borderBottomRightRadius: 12 
            }}>
              <Button 
                onClick={() => setReservationDetailsDialog(false)}
                variant="text"
                sx={{ color: '#9ca3af' }}
              >
            {t('common.close')}
          </Button>
          <Button 
            onClick={() => handleCancelReservation(selectedReservation)}
            variant="outlined"
            sx={{ 
              borderColor: '#dc3545',
              color: '#dc3545',
              '&:hover': { 
                backgroundColor: '#dc3545',
                color: '#ffffff',
                borderColor: '#dc3545'
              }
            }}
          >
            {t('restaurant.cancel_reservation')}
          </Button>
          <Button 
            onClick={() => handleConfirmArrival(selectedReservation)}
            variant="contained"
                sx={{ 
                  backgroundColor: '#28a745',
                  '&:hover': { backgroundColor: '#218838' }
                }}
          >
            {t('restaurant.confirm_arrival')}
          </Button>
          <Button 
            onClick={() => handleStartOrder(selectedReservation)}
            variant="contained"
                sx={{ 
                  backgroundColor: '#1976D2',
                  '&:hover': { backgroundColor: '#1565C0' }
                }}
          >
            {t('restaurant.start_order')}
          </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Add Table Dialog removed - table management is now admin-only */}
      {false && addTableDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setAddTableDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{ 
              bgcolor: '#2d3048', 
              p: 2, 
              borderTopLeftRadius: 12, 
              borderTopRightRadius: 12,
              borderBottom: '1px solid #3a3d52'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Add New Table
              </Typography>
            </Box>
            
            {/* Content */}
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                  label="Table Number *"
              value={newTableData.number}
              onChange={(e) => setNewTableData({ ...newTableData, number: e.target.value })}
              type="number"
              fullWidth
              required
            />
            <TextField
                  label="Capacity *"
              value={newTableData.capacity}
              onChange={(e) => setNewTableData({ ...newTableData, capacity: parseInt(e.target.value) || 4 })}
              type="number"
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>{t('restaurant.area')}</InputLabel>
              <Select
                value={newTableData.area}
                onChange={(e) => setNewTableData({ ...newTableData, area: e.target.value as 'indoor' | 'outdoor' })}
                label={t('restaurant.area')}
              >
                <MenuItem value="indoor">{t('restaurant.indoor')}</MenuItem>
                <MenuItem value="outdoor">{t('restaurant.outdoor')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
            </Box>
            
            {/* Footer */}
            <Box sx={{ 
              bgcolor: '#2d3048', 
              p: 2, 
              borderBottomLeftRadius: 12, 
              borderBottomRightRadius: 12,
              borderTop: '1px solid #3a3d52',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}>
              <Button 
                onClick={() => setAddTableDialog(false)}
                variant="outlined"
                sx={{
                  borderColor: '#3a3d52',
                  color: '#d0d0d0',
                  '&:hover': {
                    borderColor: '#b0b0b0',
                    backgroundColor: '#2d3048'
                  }
                }}
              >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateTable}
            variant="contained"
                sx={{ 
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
          >
            Create Table
          </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Reservations List Dialog - Same Style as Customer Information Modal */}
      {reservationsListDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setReservationsListDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('restaurant.current_future_reservations')} ({reservations.length})
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setReservationsListDialog(false);
                  handleReserveTable(null);
                }}
                sx={{
                  backgroundColor: '#558B2F',
                  '&:hover': { backgroundColor: '#689F38' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                + {t('restaurant.new_reservation') || 'New Reservation'}
              </Button>
            </Box>
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
              {reservations.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  border: '1px solid #3a3d52',
                  borderRadius: 1,
                  backgroundColor: '#252838'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    No reservations found
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Helper function to parse reservation time */}
                  {(() => {
                    const parseReservationTime = (reservation: any): Date => {
                      if (typeof reservation.reservation_time === 'string') {
                        return new Date(reservation.reservation_time);
                      } else if (reservation.reservation_time && typeof reservation.reservation_time === 'object') {
                        const timeObj = reservation.reservation_time as any;
                        if ('toDate' in timeObj && typeof timeObj.toDate === 'function') {
                          return timeObj.toDate();
                        } else if ('seconds' in timeObj && typeof timeObj.seconds === 'number') {
                          return new Date(timeObj.seconds * 1000);
                        }
                      }
                      return new Date();
                    };

                    // Helper function to format time in 24-hour format
                    const formatTime24Hour = (date: Date): string => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
                    };

                    const today = new Date().toISOString().split('T')[0];
                    const todayReservations: any[] = [];
                    const futureReservations: any[] = [];
                    const pastReservations: any[] = [];

                    // Categorize all reservations
                    reservations.forEach(reservation => {
                      const reservationTime = parseReservationTime(reservation);
                      const reservationDate = reservationTime.toISOString().split('T')[0];
                      
                      if (reservationDate === today) {
                        todayReservations.push(reservation);
                      } else if (reservationDate > today) {
                        futureReservations.push(reservation);
                                } else {
                        pastReservations.push(reservation);
                      }
                    });

                    // Render reservation card component
                    const renderReservationCard = (reservation: any, isToday: boolean) => {
                      const reservationTime = parseReservationTime(reservation);
                              
                              return (
                                <Box key={reservation.id} sx={{ 
                                  p: 3, 
                                  border: '1px solid #3a3d52', 
                                  borderRadius: 2, 
                                  backgroundColor: '#252838'
                                }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#d0d0d0' }}>
                                    {reservation.customer_name}
                                  </Typography>
                                  
                                  {/* Table Number */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                      Table
                                    </Typography>
                                    <Box sx={{
                                      p: 1.5,
                                      border: '1px solid #3a3d52',
                                      borderRadius: 1,
                                      backgroundColor: reservation.table_number ? '#252838' : 'rgba(255, 193, 7, 0.12)',
                                      minHeight: '48px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: reservation.table_number ? 'inherit' : '#f57c00' }}>
                                        {reservation.table_number || t('restaurant.no_table_waitlist')}
                                      </Typography>
                                      {!reservation.table_number && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedReservation(reservation);
                                            setReservationsListDialog(false);
                                            // Open assign table dialog
                                            setAssignTableDialog(true);
                                          }}
                                          sx={{
                                            textTransform: 'none',
                                            fontSize: '0.7rem',
                                            borderColor: '#558B2F',
                                            color: '#558B2F',
                                            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)', borderColor: '#558B2F' },
                                            ml: 1,
                                            minWidth: 'auto',
                                            py: 0.25,
                                          }}
                                        >
                                          {t('restaurant.assign_table')}
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>

                                  {/* Party Size */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                      {t('restaurant.party_size')}
                                    </Typography>
                                    <Box sx={{ 
                                      p: 1.5, 
                                      border: '1px solid #3a3d52', 
                                      borderRadius: 1,
                                      backgroundColor: '#252838',
                                      minHeight: '48px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {reservation.party_size}
                                      </Typography>
                                    </Box>
                                  </Box>

                          {/* Reservation Time - 24-hour format */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                      {t('restaurant.time')}
                                    </Typography>
                                    <Box sx={{ 
                                      p: 1.5, 
                                      border: '1px solid #3a3d52', 
                                      borderRadius: 1,
                                      backgroundColor: '#252838',
                                      minHeight: '48px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatTime24Hour(reservationTime)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Phone */}
                                  {reservation.customer_phone && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        {t('restaurant.phone')}
                                      </Typography>
                                      <Box sx={{ 
                                        p: 1.5, 
                                        border: '1px solid #3a3d52', 
                                        borderRadius: 1,
                                        backgroundColor: '#252838',
                                        minHeight: '48px',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {reservation.customer_phone}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* View Details Button */}
                                  <Box sx={{ mt: 3 }}>
                                    <Button
                                      fullWidth
                              variant={isToday ? "contained" : "outlined"}
                                      onClick={() => {
                                        setSelectedReservation(reservation);
                                        setReservationDetailsDialog(true);
                                        setReservationsListDialog(false);
                                      }}
                              sx={isToday ? { 
                                        backgroundColor: '#28a745',
                                        '&:hover': { backgroundColor: '#218838' }
                              } : {
                                borderColor: '#1976D2',
                                color: '#1976D2',
                                '&:hover': { 
                                  backgroundColor: '#1976D2',
                                  color: '#ffffff',
                                  borderColor: '#1976D2'
                                }
                                      }}
                                    >
                                      {t('restaurant.view_details')}
                                    </Button>
                                  </Box>
                                </Box>
                              );
                    };

                      return (
                      <>
                        {/* Today's Reservations */}
                        {todayReservations.length > 0 && (
                          <Box sx={{ mb: 4 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', color: '#d0d0d0' }}>
                              📅 {t('restaurant.today_reservations')} ({todayReservations.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {todayReservations.map(reservation => renderReservationCard(reservation, true))}
                                    </Box>
                                  </Box>
                        )}
                        
                        {/* Future Reservations */}
                        {futureReservations.length > 0 && (
                          <Box sx={{ mb: pastReservations.length > 0 ? 4 : 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', color: '#d0d0d0' }}>
                              📅 {t('restaurant.future_reservations')} ({futureReservations.length})
                                    </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {futureReservations.map(reservation => renderReservationCard(reservation, false))}
                                    </Box>
                                  </Box>
                        )}

                        {/* Past Reservations - Show if any exist */}
                        {pastReservations.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', color: '#9ca3af' }}>
                              📅 {t('restaurant.past_reservations')} ({pastReservations.length})
                                    </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {pastReservations.map(reservation => renderReservationCard(reservation, false))}
                                      </Box>
                                    </Box>
                                  )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'flex-end', 
              p: 2, 
              bgcolor: '#2d3048', 
              borderBottomLeftRadius: 12, 
              borderBottomRightRadius: 12 
            }}>
              <Button 
                onClick={() => setReservationsListDialog(false)}
                variant="text"
                sx={{ color: '#9ca3af' }}
              >
                {t('restaurant.close')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Assign Table Dialog for Waitlist Reservations */}
      {assignTableDialog && selectedReservation && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => { if (e.target === e.currentTarget) setAssignTableDialog(false); }}
          sx={{
            position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400, backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838', borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '1px solid #3a3d52',
              maxWidth: '500px', width: '90%',
            }}
          >
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('restaurant.assign_table')} — {selectedReservation.customer_name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                {t('restaurant.select_table_for')}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {tables
                  .filter(t => t.status === 'available')
                  .sort((a, b) => a.number - b.number)
                  .map(table => (
                  <Box
                    key={table.number}
                    onClick={async () => {
                      try {
                        // Update reservation with table number
                        await ReservationCloudService.updateReservation(selectedReservation.id, {
                          table_number: table.number,
                        });
                        // Update table status in Firestore
                        await firebaseTableService.updateTableByNumber(
                          table.number, 'reserved',
                          { customer_name: selectedReservation.customer_name, start_time: selectedReservation.reservation_time },
                          table.area
                        );
                        // Update local state
                        setTables(prevTables => {
                          const updated = prevTables.map(t =>
                            t.number === table.number
                              ? { ...t, status: 'reserved' as const, customer_name: selectedReservation.customer_name }
                              : t
                          );
                          updateTableCounts(updated);
                          return updated;
                        });
                        await fetchReservations();
                        setAssignTableDialog(false);
                        setSnackbar({ open: true, message: `Table ${table.number} assigned to ${selectedReservation.customer_name}`, severity: 'success' });
                      } catch (err) {
                        console.error('Failed to assign table:', err);
                        setSnackbar({ open: true, message: 'Failed to assign table', severity: 'error' });
                      }
                    }}
                    sx={{
                      width: 64, height: 64,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      border: '1px solid #3a3d52', borderRadius: 2,
                      cursor: 'pointer', backgroundColor: '#252838',
                      '&:hover': { borderColor: '#558B2F', backgroundColor: 'rgba(76, 175, 80, 0.08)' },
                      transition: 'all 0.15s',
                    }}
                  >
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{table.number}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#999' }}>{table.area}</Typography>
                  </Box>
                ))}
                {tables.filter(t => t.status === 'available').length === 0 && (
                  <Typography variant="body2" sx={{ color: '#999', py: 2, textAlign: 'center', width: '100%' }}>
                    {t('restaurant.no_available_tables')}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, bgcolor: '#2d3048', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Button onClick={() => { setAssignTableDialog(false); setReservationsListDialog(true); }} variant="text" sx={{ color: '#9ca3af' }}>
                {t('restaurant.cancel')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Table Order Details Dialog - Same Style as Customer Information Modal */}
      {tableOrderDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setTableOrderDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('restaurant.table')} {selectedTableOrder?.display_number || selectedTableOrder?.number} {selectedTableOrder?.area ? `(${selectedTableOrder.area === 'indoor' ? t('restaurant.indoor') : t('restaurant.outdoor')})` : ''}
              </Typography>
            </Box>
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
              {loadingTableOrders ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <Typography>{t('restaurant.loading_orders')}</Typography>
                </Box>
              ) : tableOrders.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  border: '1px solid #3a3d52',
                  borderRadius: 1,
                  backgroundColor: '#252838'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    No orders found for this table.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {tableOrders.map((order) => (
                    <Box key={order.id} sx={{ mb: 3, p: 3, border: '1px solid #3a3d52', borderRadius: 2, backgroundColor: '#252838' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#d0d0d0' }}>
                        Order #{order.order_number || order.id.substring(0, 8)}
                      </Typography>
                      
                      {/* Customer Information - Same Style as Dashboard */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                          Customer Information
                        </Typography>
                        
                        {/* Customer Name */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Name
                          </Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#252838',
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {(order.customer_first_name || order.customer_last_name)
                                ? `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim()
                                : order.customer_name || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Phone */}
                        {order.customer_phone && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Phone
                            </Typography>
                            <Box sx={{ 
                              p: 1.5, 
                              border: '1px solid #3a3d52', 
                              borderRadius: 1,
                              backgroundColor: '#252838',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.customer_phone}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Address */}
                        {(order.customer_address || order.customer_postcode || order.customer_city) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Address
                            </Typography>
                            <Box sx={{ 
                              p: 1.5, 
                              border: '1px solid #3a3d52', 
                              borderRadius: 1,
                              backgroundColor: '#252838',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.customer_address || ''} {order.customer_postcode || ''} {order.customer_city || ''}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Order Items - Same Style */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                          Order Items
                        </Typography>
                        {order.items && order.items.length > 0 ? (
                          <Box sx={{ 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#252838',
                            overflow: 'hidden'
                          }}>
                            {order.items.map((item: any, itemIndex: number) => (
                              <Box key={itemIndex} sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                p: 2,
                                borderBottom: itemIndex < order.items.length - 1 ? '1px solid #f0f0f0' : 'none',
                                backgroundColor: '#252838'
                              }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#d0d0d0' }}>
                                    {item.quantity}x {item.name || 'Item'}
                                  </Typography>
                                  {item.category_name && (
                                    <Typography variant="caption" color="text.secondary">
                                      Category: {item.category_name}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d0d0' }}>
                                  €{parseFloat(item.unit_price || item.price || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            p: 3, 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#252838',
                            textAlign: 'center',
                            color: 'text.secondary'
                          }}>
                            <Typography variant="body2">No items in this order.</Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Order Summary - Same Style */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                          Order Summary
                        </Typography>
                        <Box sx={{ 
                          border: '1px solid #3a3d52', 
                          borderRadius: 1,
                          backgroundColor: '#252838',
                          p: 2
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Subtotal:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              €{order.subtotal?.toFixed(2) || '0.00'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Tax:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              €{order.tax?.toFixed(2) || '0.00'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '2px solid #3a3d52' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#d0d0d0' }}>
                              Total:
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976D2' }}>
                              €{order.total?.toFixed(2) || '0.00'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Order Details - Same Style */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                          Order Details
                        </Typography>
                        
                        {/* Status */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Status
                          </Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#252838',
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.status || 'pending'} | Payment: {order.payment_method || 'cash'} ({order.payment_status || 'completed'})
                            </Typography>
                          </Box>
                        </Box>

                        {/* Created Date */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Created
                          </Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#252838',
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Created By - User Information */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Created By (Staff)
                          </Typography>
                          <Box sx={{ 
                            p: 1.5, 
                            border: '1px solid #3a3d52', 
                            borderRadius: 1,
                            backgroundColor: '#2d3048',
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976D2' }}>
                              {order.created_by_name || order.created_by_email || order.created_by || 'Unknown User'}
                              {order.created_by_email && order.created_by_email !== (order.created_by_name || order.created_by) && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: '#9ca3af' }}>
                                  ({order.created_by_email})
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          {(order.created_by || order.created_by_email) && (
                            <Typography variant="caption" sx={{ mt: 0.5, color: '#9ca3af', display: 'block' }}>
                              This information helps track who processed the order in case of any issues.
                            </Typography>
                          )}
                        </Box>

                        {/* Notes */}
                        {order.notes && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Notes
                            </Typography>
                            <Box sx={{ 
                              p: 1.5, 
                              border: '1px solid #3a3d52', 
                              borderRadius: 1,
                              backgroundColor: '#252838',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'flex-end', 
              p: 2, 
              bgcolor: '#2d3048', 
              borderBottomLeftRadius: 12, 
              borderBottomRightRadius: 12 
            }}>
              <Button 
                onClick={() => setTableOrderDialog(false)}
                variant="text"
                sx={{ color: '#9ca3af' }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Reservation Notification Dialog - Same Style as Full Order Details */}
      {reservationNotificationDialog && (() => {
        // Play notification sound on mount
        try {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const playTone = (freq: number, startTime: number, duration: number) => {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.connect(gain);
            gain.connect(audioCtx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0.15, audioCtx.currentTime + startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + startTime + duration);
            osc.start(audioCtx.currentTime + startTime);
            osc.stop(audioCtx.currentTime + startTime + duration);
          };
          // Pleasant chime: C5 - E5 - G5 arpeggio
          playTone(523.25, 0, 0.3);
          playTone(659.25, 0.15, 0.3);
          playTone(783.99, 0.3, 0.4);
          if (notificationType === 'soon') {
            // Extra urgent double chime
            playTone(783.99, 0.6, 0.2);
            playTone(1046.5, 0.75, 0.4);
          }
        } catch (e) { /* Audio not available */ }
        return null;
      })()}
      {reservationNotificationDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setReservationNotificationDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'auto',
            animation: 'fadeInOverlay 0.3s ease-out',
            '@keyframes fadeInOverlay': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
            '@keyframes slideUpBounce': {
              '0%': { opacity: 0, transform: 'translateY(40px) scale(0.95)' },
              '60%': { opacity: 1, transform: 'translateY(-8px) scale(1.01)' },
              '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
            },
            '@keyframes pulseGlow': {
              '0%, 100%': { boxShadow: notificationType === 'soon'
                ? '0 0 20px rgba(255, 87, 34, 0.3), 0 8px 40px rgba(0,0,0,0.2)'
                : '0 0 20px rgba(255, 152, 0, 0.2), 0 8px 40px rgba(0,0,0,0.2)' },
              '50%': { boxShadow: notificationType === 'soon'
                ? '0 0 40px rgba(255, 87, 34, 0.5), 0 8px 60px rgba(0,0,0,0.3)'
                : '0 0 30px rgba(255, 152, 0, 0.35), 0 8px 50px rgba(0,0,0,0.25)' },
            },
            '@keyframes bellSwing': {
              '0%, 100%': { transform: 'rotate(0deg)' },
              '10%': { transform: 'rotate(14deg)' },
              '20%': { transform: 'rotate(-12deg)' },
              '30%': { transform: 'rotate(10deg)' },
              '40%': { transform: 'rotate(-8deg)' },
              '50%': { transform: 'rotate(5deg)' },
              '60%': { transform: 'rotate(-3deg)' },
              '70%': { transform: 'rotate(1deg)' },
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 4,
              boxShadow: notificationType === 'soon'
                ? '0 0 30px rgba(255, 87, 34, 0.4), 0 16px 48px rgba(0,0,0,0.25)'
                : '0 0 20px rgba(255, 152, 0, 0.25), 0 16px 48px rgba(0,0,0,0.2)',
              maxWidth: '640px',
              width: '92%',
              maxHeight: '85vh',
              overflow: 'hidden',
              pointerEvents: 'auto',
              animation: 'slideUpBounce 0.4s ease-out, pulseGlow 2.5s ease-in-out infinite',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient and icon */}
            <Box sx={{
              background: notificationType === 'soon'
                ? 'linear-gradient(135deg, #ff5722, #e64a19)'
                : notificationType === 'today'
                  ? 'linear-gradient(135deg, #ff9800, #f57c00)'
                  : 'linear-gradient(135deg, #2196f3, #1976d2)',
              p: 3,
              pb: 2.5,
              color: '#ffffff',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Close button */}
              <Box
                onClick={() => setReservationNotificationDialog(false)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                ✕
              </Box>

              {/* Bell icon with animation */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1.5
              }}>
                <Box sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.6rem',
                  animation: 'bellSwing 1.5s ease-in-out',
                  transformOrigin: 'top center',
                }}>
                  {notificationType === 'soon' ? '🔔' : '📅'}
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: '"Poppins", sans-serif', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                    {notificationType === 'soon' && t('restaurant.upcoming_reservations')}
                    {notificationType === 'today' && t('restaurant.today_reservations')}
                    {notificationType === 'tomorrow' && t('restaurant.tomorrow_reservations')}
                  </Typography>
                </Box>
              </Box>

              {/* Count badge */}
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
              }}>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 800 }}>
                  {upcomingReservations.length}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, opacity: 0.95 }}>
                  {upcomingReservations.length === 1 ? t('restaurant.reservation') : t('restaurant.reservations')}
                  {notificationType === 'soon' && ` ${t('restaurant.in_next_2_hours')}`}
                  {notificationType === 'today' && ` ${t('restaurant.for_today')}`}
                  {notificationType === 'tomorrow' && ` ${t('restaurant.for_tomorrow')}`}
                </Typography>
              </Box>
            </Box>

            {/* Reservation Cards */}
            <Box sx={{ p: 2.5, maxHeight: '50vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {upcomingReservations.map((reservation, idx) => {
                let reservationTime: Date;
                if (typeof reservation.reservation_time === 'string') {
                  reservationTime = new Date(reservation.reservation_time);
                } else if (reservation.reservation_time && typeof reservation.reservation_time === 'object') {
                  const timeObj = reservation.reservation_time as any;
                  if ('toDate' in timeObj && typeof timeObj.toDate === 'function') {
                    reservationTime = timeObj.toDate();
                  } else if ('seconds' in timeObj && typeof timeObj.seconds === 'number') {
                    reservationTime = new Date(timeObj.seconds * 1000);
                  } else {
                    reservationTime = new Date();
                  }
                } else {
                  reservationTime = new Date();
                }

                const timeUntil = reservationTime.getTime() - new Date().getTime();
                const hoursUntil = Math.floor(timeUntil / (1000 * 60 * 60));
                const minutesUntil = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
                const hours24 = reservationTime.getHours().toString().padStart(2, '0');
                const minutes24 = reservationTime.getMinutes().toString().padStart(2, '0');
                const time24 = `${hours24}:${minutes24}`;

                const accentColor = notificationType === 'soon' ? '#ff5722' : notificationType === 'today' ? '#ff9800' : '#2196f3';

                return (
                  <Box key={reservation.id} sx={{
                    p: 2,
                    border: `1px solid ${accentColor}30`,
                    borderLeft: `4px solid ${accentColor}`,
                    borderRadius: 2,
                    backgroundColor: '#1e2139',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    animation: `slideUpBounce 0.4s ease-out ${idx * 0.08}s both`,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: `${accentColor}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 800,
                          color: accentColor,
                        }}>
                          {(reservation.customer_name || '?')[0].toUpperCase()}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#e0e0e0', lineHeight: 1.2 }}>
                            {reservation.customer_name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            Table {reservation.table_number} &bull; {reservation.party_size} {reservation.party_size === 1 ? 'guest' : 'guests'}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={notificationType === 'soon' ? `In ${hoursUntil}h ${minutesUntil}m` : time24}
                        sx={{
                          backgroundColor: accentColor,
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          height: 30,
                        }}
                        size="small"
                      />
                    </Box>

                    {/* Info row */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>Date:</Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#444' }}>
                          {reservationTime.toLocaleDateString('en-US')} at {time24}
                        </Typography>
                      </Box>
                      {reservation.customer_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>Phone:</Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#444' }}>
                            {reservation.customer_phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {reservation.special_requests && (
                      <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255, 193, 7, 0.12)', borderRadius: 1, border: '1px solid #fff3c4' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#8d6e00', fontStyle: 'italic' }}>
                          Note: {reservation.special_requests}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Footer with actions */}
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              p: 2.5,
              pt: 2,
              borderTop: '1px solid #eee',
              backgroundColor: '#1e2139',
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16
            }}>
              <Button
                onClick={() => setReservationNotificationDialog(false)}
                variant="outlined"
                sx={{
                  color: '#9ca3af',
                  borderColor: '#ddd',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { backgroundColor: '#2d3048', borderColor: '#ccc' },
                }}
              >
                {t('restaurant.dismiss')}
              </Button>
              <Button
                onClick={() => {
                  setReservationNotificationDialog(false);
                  setReservationsListDialog(true);
                }}
                variant="contained"
                sx={{
                  background: notificationType === 'soon'
                    ? 'linear-gradient(135deg, #ff5722, #e64a19)'
                    : notificationType === 'today'
                      ? 'linear-gradient(135deg, #ff9800, #f57c00)'
                      : 'linear-gradient(135deg, #2196f3, #1976d2)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
                }}
              >
                {t('restaurant.view_all_reservations')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Centered alert dialog — used for permission errors and split-blocked messages */}
      <Dialog
        open={Boolean(centeredAlert?.open)}
        onClose={() => setCenteredAlert(null)}
        PaperProps={{
          sx: {
            borderRadius: 3, overflow: 'hidden', maxWidth: 400, width: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            margin: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' },
          zIndex: 9999,
        }}
      >
        <Box sx={{
          background: centeredAlert?.icon === '✅' || centeredAlert?.title?.toLowerCase().includes('success')
            ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
            : centeredAlert?.icon === '💳'
              ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
              : 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
          px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5
        }}>
          <Typography sx={{ fontSize: 28 }}>{centeredAlert?.icon || '🔒'}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff !important', fontSize: '1rem' }}>
            {centeredAlert?.title || 'Notice'}
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#d0d0d0', fontWeight: 500, lineHeight: 1.6 }}>
            {centeredAlert?.message}
          </Typography>
        </Box>
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={() => setCenteredAlert(null)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              color: '#fff !important', borderRadius: 2, px: 4, fontWeight: 700, textTransform: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #145214 100%)' }
            }}
          >
            Got it
          </Button>
        </Box>
      </Dialog>

      {/* Centered Notification Dialog — replaces snackbar for all table operations */}
      <Dialog
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        TransitionProps={{
          onEntered: () => {
            // Auto-dismiss success/info after 2.8 seconds
            if (snackbar.severity === 'success' || snackbar.severity === 'info') {
              setTimeout(() => setSnackbar(s => ({ ...s, open: false })), 2800);
            }
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3, overflow: 'hidden', maxWidth: 380, width: '88vw',
            boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            margin: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' },
          zIndex: 9999,
        }}
      >
        {/* Header — color based on severity */}
        <Box sx={{
          background: snackbar.severity === 'success'
            ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
            : snackbar.severity === 'error'
              ? 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)'
              : snackbar.severity === 'warning'
                ? 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)'
                : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          px: 3, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography sx={{ fontSize: 26 }}>
            {snackbar.severity === 'success' ? '✅'
              : snackbar.severity === 'error' ? '❌'
              : snackbar.severity === 'warning' ? '⚠️'
              : 'ℹ️'}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff !important', fontSize: '0.95rem', letterSpacing: 0.3 }}>
            {snackbar.severity === 'success' ? 'Success'
              : snackbar.severity === 'error' ? 'Error'
              : snackbar.severity === 'warning' ? 'Warning'
              : 'Notice'}
          </Typography>
        </Box>
        {/* Body */}
        <Box sx={{ px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#e0e0e0', fontWeight: 500, lineHeight: 1.6, fontSize: '0.95rem' }}>
            {snackbar.message}
          </Typography>
        </Box>
        {/* Action */}
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={() => setSnackbar({ ...snackbar, open: false })}
            variant="contained"
            sx={{
              background: snackbar.severity === 'success'
                ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
                : snackbar.severity === 'error'
                  ? 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)'
                  : snackbar.severity === 'warning'
                    ? 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)'
                    : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              color: '#fff !important', borderRadius: 2, px: 5, fontWeight: 700,
              textTransform: 'none', fontSize: '0.9rem',
              '&:hover': { opacity: 0.9 }
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>

      {/* Draft Split Orders Dialog */}
      <DraftSplitOrdersDialog
        open={draftSplitOrdersDialog}
        onClose={() => setDraftSplitOrdersDialog(false)}
        onResume={(draft) => {
          setSelectedDraft(draft);
          setDraftSplitOrdersDialog(false);
          setSplitDialogOpen(true);
        }}
      />

      {/* Split Table Dialog - Resume from Draft */}
      {splitDialogOpen && selectedDraft && (
        <SplitTableDialog
          open={splitDialogOpen}
          onClose={() => {
            setSplitDialogOpen(false);
            setSelectedDraft(null);
          }}
          tableId={selectedDraft.table_id}
          tableNumber={selectedDraft.table_number}
          orderId={selectedDraft.order_id}
          orderTotal={selectedDraft.original_order_total}
          orderItems={[]} // Will be loaded from draft
          staffMember={selectedDraft.created_by || 'POS System'}
        />
      )}

      {/* Split Table Dialog - New split from table card */}
      {newSplitDialog?.open && (
        <SplitTableDialog
          open={newSplitDialog.open}
          onClose={() => setNewSplitDialog(null)}
          tableId={newSplitDialog.tableId}
          tableNumber={newSplitDialog.tableNumber}
          orderId={newSplitDialog.orderId}
          orderTotal={newSplitDialog.orderTotal}
          orderItems={newSplitDialog.orderItems}
          staffMember={newSplitDialog.staffMember}
        />
      )}

      {/* Close Tab & Charge Dialog */}
      <CloseTabDialog
        open={closeTabDialog.open}
        table={closeTabDialog.table}
        onClose={() => setCloseTabDialog({ open: false, table: null })}
        onConfirm={handleCloseTabConfirm}
      />

      {/* QR Order Notification — Centered Popup */}
      <Dialog
        open={qrNotification.open}
        onClose={() => setQrNotification({open: false, message: ''})}
        PaperProps={{
          sx: {
            borderRadius: 4,
            padding: '32px 40px',
            textAlign: 'center',
            minWidth: 360,
            maxWidth: 460,
            background: 'linear-gradient(135deg, rgba(0, 200, 150, 0.08) 0%, #252838 100%)',
            border: '1px solid #3a3d52',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            animation: 'qrPopIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes qrPopIn': {
              '0%': { transform: 'scale(0.5)', opacity: 0 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }
        }}
      >
        <Box sx={{ fontSize: 64, mb: 1, lineHeight: 1 }}>🔔</Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: '"Poppins"', color: '#00C896 !important', mb: 1 }}>
          Neue QR-Bestellung!
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, fontFamily: '"Poppins"', color: '#e0e0e0 !important', mb: 2 }}>
          {qrNotification.message}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setQrNotification({open: false, message: ''})}
          sx={{
            backgroundColor: '#00C896 !important',
            color: '#1a1d2e !important',
            fontWeight: 700,
            fontFamily: '"Poppins"',
            fontSize: '1rem',
            borderRadius: 3,
            px: 5, py: 1.2,
            '&:hover': { backgroundColor: '#7bc912 !important' },
          }}
        >
          OK
        </Button>
      </Dialog>

      {/* Manager PIN gate for Reopen Tab */}
      <ManagerPinDialog
        open={reopenPinOpen}
        actionLabel={reopenTable ? `Reopen table ${reopenTable.number} — undo close` : 'Reopen tab'}
        requireReason={true}
        onApproved={(email, reason) => { performReopen(email, reason); }}
        onClose={() => { if (!reopenBusy) { setReopenPinOpen(false); setReopenTable(null); } }}
      />

    </Box>
  );
};

export default ModernRestaurantPage;


